import { DESIGNER_STYLES } from './designer/styles.js';
import { GUIDE_STYLES } from './guide-styles.js';
import { COLLAGE_STEP_STYLES } from './collage/step-styles.js';
import { BOARD_STYLES } from './collage/board-styles.js';
import { CHOICE_BACKDROP_DARK, CHOICE_BACKDROP_LIGHT, CHOICE_TILE_DEFAULT_PX } from '@pz/scene';

/** The picked-song card in a phone-width column; used by a container query and its fallback. */
const SONG_CARD_NARROW = `
  .pz-song-card { grid-template-columns: 48px minmax(0, 1fr) auto; gap: 8px 10px; }
  .pz-song-card-cover { width: 48px; height: 48px; }
  .pz-song-card-meta { display: none; }
  .pz-song-note { font-size: 12px; line-height: 1.4; }
  .pz-song-text { padding: 10px 11px; gap: 8px; }
  .pz-song-line { gap: 4px; }
  /* "Copied from Spotify" is what the filled lines already show. */
  .pz-song-text-foot .pz-hint { display: none; }
  .pz-song-text-foot:has(> .pz-song-reset[hidden]) { display: none; }
`;

/**
 * Widget styles.
 *
 * Injected into a shadow root so the shop's theme cannot restyle the designer and
 * the designer cannot leak into the shop. WooCommerce themes routinely set global
 * rules on `input`, `button` and `img`, which is exactly what breaks embedded UI.
 *
 * The shopper guide keeps its rules in their own file (guide-styles.ts) and they
 * are appended at the end: they are a self-contained block, and last is where a
 * `.pz-row.is-guided` highlight has to be to win against the row's own rules.
 */
export const STYLES = `
.pz-surface-active .pz-edit, .pz-surface-active .pz-edit-above { display: none !important; }
/* A wizard step shows the tools of its own questions only (canvas-edit setFocus). */
.pz-edit.is-elsewhere { display: none !important; }

/* ── Tokens ──────────────────────────────────────────────────────────────── */
/* The shop-wide look (Studio → Settings → Storefront look) lands as custom
   properties on the host element; these are the defaults it overrides.
   Everything tinted is DERIVED from the accent, so a shop with a green accent
   gets green selections, rings and tints — never a purple leftover. Where
   color-mix() is missing the tints fall back to neutral greys, which suit any
   brand. */
:host { --pz-accent: #5145cd; --pz-btn-text: #ffffff; --pz-btn-radius: 12px;
        --pz-swatch-radius: 50%; --pz-swatch-size: 30px; --pz-font-size: 15px;
        /* Picture choices (choice-display.ts in @pz/scene): the shop's tile
           size, scaled per question by --pz-tile-scale, and the two backdrops
           a transparent picture is shown on. */
        --pz-tile-size: ${CHOICE_TILE_DEFAULT_PX}px; --pz-tile-light: ${CHOICE_BACKDROP_LIGHT}; --pz-tile-dark: ${CHOICE_BACKDROP_DARK};
        --pz-font: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        --pz-ink: #16161d; --pz-ink-2: #3b3b47; --pz-muted: #676774; --pz-subtle: #9696a3;
        --pz-line: #e5e5eb; --pz-line-2: #d2d2db; --pz-line-hover: #b5b5c3;
        --pz-surface: #ffffff; --pz-surface-2: #f6f6f8; --pz-surface-3: #ececf1;
        --pz-danger: var(--pz-required, #c62828); --pz-success: #1b7a4a; --pz-warn: #9a5b05;
        --pz-radius-sm: 8px; --pz-radius: 12px; --pz-radius-lg: 16px; --pz-control-h: 46px;
        --pz-shadow-sm: 0 1px 2px rgba(16,16,32,.06);
        --pz-shadow-md: 0 1px 2px rgba(16,16,32,.05), 0 10px 28px -10px rgba(16,16,32,.18);
        --pz-shadow-lg: 0 2px 6px rgba(10,10,30,.08), 0 32px 80px -16px rgba(10,10,30,.4);
        --pz-ease: cubic-bezier(.2,.7,.2,1);
        --pz-accent-soft: #f3f3f7; --pz-accent-line: #c6c6d3; --pz-accent-ring: rgba(16,16,32,.13);
        --pz-accent-ink: var(--pz-accent);
        /* Soft and line are OPAQUE (mixed with white) — for surfaces. Anything
           laid over a picture (a photo being dragged, a waveform, a face) must
           use the translucent wash/veil instead, or it hides what it marks. */
        --pz-accent-wash: rgba(81,69,205,.07); --pz-accent-veil: rgba(81,69,205,.28); }
@supports (color: color-mix(in srgb, red 50%, blue)) {
  :host { --pz-accent-soft: color-mix(in srgb, var(--pz-accent) 8%, #fff);
          --pz-accent-line: color-mix(in srgb, var(--pz-accent) 42%, #fff);
          --pz-accent-ring: color-mix(in srgb, var(--pz-accent) 22%, transparent);
          --pz-accent-ink: color-mix(in srgb, var(--pz-accent) 84%, #000);
          --pz-accent-wash: color-mix(in srgb, var(--pz-accent) 7%, transparent);
          --pz-accent-veil: color-mix(in srgb, var(--pz-accent) 28%, transparent); }
}
:host { all: initial; display: block; width: 100%; max-width: 100%; min-width: 0;
        font: var(--pz-font-size, 15px)/1.5 var(--pz-font);
        color: var(--pz-ink); -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
        -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
*, *::before, *::after { box-sizing: border-box; }
button { font: inherit; -webkit-tap-highlight-color: transparent; }
/* The shadow reset wipes UA styles, and author display rules beat the UA's
   [hidden] anyway — without this, an empty upload card shows its ✕ before any
   photo exists. */
[hidden] { display: none !important; }
/* Words for screen readers only. */
.pz-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
         clip: rect(0 0 0 0); white-space: nowrap; border: 0; }

/* ── Layout ──────────────────────────────────────────────────────────────── */
.pz-wrap { display: grid; grid-template-columns: minmax(0,1fr); gap: 20px;
           width: 100%; max-width: 100%; min-width: 0; }
.pz-wrap > * { min-width: 0; }
/* The column the questions live in: one rhythm for everything stacked in it. */
.pz-panel { display: grid; grid-template-columns: minmax(0,1fr); gap: 16px; align-content: start; min-width: 0; }
@media (min-width: 860px) { .pz-wrap { grid-template-columns: minmax(0,1fr) minmax(0,1fr); align-items: start; } }
/* On phones the preview rides along while the shopper fills the form below —
   otherwise "live" preview means scrolling up after every answer. */
@media (max-width: 859px) {
  .pz-wrap > .pz-preview { position: sticky; top: 8px; z-index: 2; }
  .pz-input { font-size: 16px; }
}

/* ── The inline form's heading and progress meter ────────────────────────── */
.pz-head { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap;
           padding-bottom: 12px; border-bottom: 1px solid var(--pz-line); }
.pz-head-title { display: inline-flex; align-items: center; gap: 8px; min-width: 0;
                 font-size: 16px; font-weight: 700; letter-spacing: -.01em; line-height: 1.3; color: var(--pz-ink); }
.pz-head-title .pz-ic { font-size: 18px; color: var(--pz-accent); }
.pz-meter { display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
            font-size: 12.5px; font-weight: 600; color: var(--pz-muted); font-variant-numeric: tabular-nums; }
.pz-meter-bar { position: relative; width: 56px; height: 6px; overflow: hidden; border-radius: 99px;
                background: var(--pz-surface-3); }
.pz-meter-fill { position: absolute; top: 0; bottom: 0; left: 0; width: 0; border-radius: inherit;
                 background: var(--pz-accent); transition: width .35s var(--pz-ease); }
.pz-meter-text { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.pz-meter.is-done { color: var(--pz-success); }
.pz-meter.is-done .pz-meter-bar { display: none; }
.pz-meter .pz-ic { font-size: 15px; }

/* ── Preview ─────────────────────────────────────────────────────────────── */
.pz-preview { position: relative; overflow: hidden; border-radius: var(--pz-radius-lg);
              background: var(--pz-surface-2); border: 1px solid var(--pz-line); box-shadow: var(--pz-shadow-sm); }
.pz-preview canvas { display: block; width: 100%; max-width: 100%; height: auto; }
/* On the page the picture itself opens full size. */
.pz-wrap > .pz-preview canvas { cursor: zoom-in; }
/* The caption under the artwork: what this is, and what can be done with it. */
.pz-preview-cap { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 46px;
                  padding: 6px 6px 6px 14px; background: var(--pz-surface); border-top: 1px solid var(--pz-line);
                  font-size: 12.5px; color: var(--pz-muted); }
.pz-preview-cap-text { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
/* A live dot: this picture redraws as they type. */
.pz-preview-cap-text::before { content: ''; width: 8px; height: 8px; border-radius: 99px; flex-shrink: 0;
                               background: var(--pz-success); box-shadow: 0 0 0 3px rgba(27,122,74,.15); }
.pz-preview-cap-actions { display: inline-flex; align-items: center; gap: 2px; flex-shrink: 0; }
.pz-preview-open, .pz-preview-toggle { display: inline-flex; align-items: center; gap: 6px; min-height: 34px;
  padding: 6px 10px; cursor: pointer; background: transparent; border: 0; border-radius: var(--pz-radius-sm);
  color: var(--pz-ink-2); font-family: inherit; font-size: 12.5px; font-weight: 600; line-height: 1; white-space: nowrap;
  transition: background-color .15s, color .15s; }
.pz-preview-open:hover, .pz-preview-toggle:hover { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-preview-open .pz-ic, .pz-preview-toggle .pz-ic { font-size: 15px; }
.pz-preview-toggle .pz-ic { transition: transform .2s var(--pz-ease); }
.pz-preview.is-collapsed .pz-preview-toggle .pz-ic { transform: rotate(180deg); }
/* "Puna veličina" at the top-right, only while a mouse is over the picture —
   the way every product gallery says "click to enlarge". Never on touch. */
.pz-preview-hint { position: absolute; top: 10px; right: 10px; z-index: 3;
  display: none; align-items: center; gap: 6px; padding: 7px 12px; cursor: pointer;
  background: rgba(255,255,255,.95); color: var(--pz-ink-2); border: 1px solid rgba(20,20,40,.08);
  border-radius: 99px; font-family: inherit; font-size: 12px; font-weight: 600; line-height: 1;
  box-shadow: var(--pz-shadow-md); opacity: 0; transition: opacity .15s; pointer-events: none; }
.pz-preview-hint svg { width: 14px; height: 14px; }
@media (hover: hover) {
  .pz-wrap > .pz-preview .pz-preview-hint { display: inline-flex; }
  .pz-wrap > .pz-preview:hover .pz-preview-hint { opacity: 1; pointer-events: auto; }
}
.pz-lightbox-inner .pz-preview-cap, .pz-lightbox-inner .pz-preview-hint,
.pz-modal-preview .pz-preview-cap-actions, .pz-modal-preview .pz-preview-hint,
.pz-preview.is-collapsed .pz-preview-hint { display: none !important; }
/* Several mockups: a strip of product-photo thumbnails under the picture. */
.pz-previews { display: flex; gap: 8px; padding: 10px; background: var(--pz-surface); border-top: 1px solid var(--pz-line);
               overflow-x: auto; scrollbar-width: thin; }
.pz-previews-item { width: 54px; height: 54px; flex: 0 0 auto; padding: 0; border: 2px solid var(--pz-line); border-radius: 10px;
  background: var(--pz-surface-2); cursor: pointer; overflow: hidden; display: grid; place-items: center;
  font-family: inherit; font-size: 13px; font-weight: 600; color: var(--pz-muted); transition: border-color .15s; }
.pz-previews-item:hover { border-color: var(--pz-line-hover); }
.pz-previews-item img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pz-previews-item.is-active { border-color: var(--pz-accent); box-shadow: 0 0 0 3px var(--pz-accent-ring); }
/* No mockup, several print sheets: named buttons ("List 1", "List 2") instead of photos. */
.pz-previews-item.is-sheet { width: auto; min-width: 54px; height: 44px; padding: 0 14px; white-space: nowrap; color: var(--pz-ink-2); }
.pz-previews-item.is-sheet.is-active { color: var(--pz-ink); }
.pz-preview.is-sticky { position: sticky; top: 12px; }
/* "Pregled dizajna" is the caption's own text — nothing is drawn over the artwork. */
.pz-badge { font-weight: 600; color: var(--pz-ink-2); min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Compact preview: the "see my design" card + full-screen lightbox ─────── */
.pz-wrap--single { grid-template-columns: minmax(0,1fr) !important; }
/* A button that looks like one: the live thumbnail with a magnifier badge, two
   lines of copy, and a tinted pill that is visibly pressable on a touch screen
   where there is no hover to discover. The shop's preview look (Studio →
   Storefront look) still lands through the --pz-preview-* variables. */
.pz-peek { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 76px; padding: 10px;
           cursor: pointer; text-align: start; font-family: inherit; line-height: 1.35;
           background: var(--pz-preview-bg, var(--pz-surface));
           color: var(--pz-preview-text, var(--pz-ink)); font-size: var(--pz-preview-font-size, inherit);
           border: var(--pz-preview-border-width, 1px) solid var(--pz-preview-border, var(--pz-line-2));
           border-radius: var(--pz-preview-radius, var(--pz-radius-lg));
           box-shadow: var(--pz-shadow-sm); -webkit-tap-highlight-color: transparent;
           transition: border-color .15s, box-shadow .15s, transform .12s; }
.pz-peek:hover { border-color: var(--pz-accent-line); box-shadow: var(--pz-shadow-md); }
.pz-peek:active { transform: scale(.995); }
.pz-peek:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
/* The thumbnail: a fixed square frame so the layout does not breathe with the
   design's aspect, the live canvas letterboxed inside it, the badge on top. */
.pz-peek-pic { position: relative; display: grid; place-items: center; width: 56px; height: 56px; flex-shrink: 0;
               border-radius: 10px; overflow: hidden; border: 1px solid var(--pz-line);
               background: repeating-conic-gradient(#efeff3 0 25%, #fafafb 0 50%) 50%/10px 10px; }
/* blitThumb sets the canvas's own width/height inline; the frame overrides them
   and letterboxes the picture instead, so a tall poster and a wide mug both fit. */
.pz-peek-pic .pz-thumb { position: absolute; inset: 0; width: 100% !important; height: 100% !important;
                         object-fit: contain; border: 0; border-radius: 0; background: none; }
.pz-peek-badge { position: absolute; right: 3px; bottom: 3px; z-index: 1; width: 20px; height: 20px;
                 border-radius: 99px; background: var(--pz-accent); color: var(--pz-btn-text, #fff);
                 font-size: 12px; box-shadow: 0 0 0 2px #fff, 0 1px 4px rgba(20,20,40,.25); }
.pz-thumb { display: block; border-radius: 8px; background: repeating-conic-gradient(#efeff3 0 25%, #fafafb 0 50%) 50%/10px 10px;
            border: 1px solid var(--pz-line); }
.pz-peek-copy { display: grid; gap: 2px; flex: 1; min-width: 0; }
.pz-peek-copy strong { font-size: 14.5px; font-weight: 650; }
.pz-peek-copy span { font-size: 12.5px; color: var(--pz-muted); }
.pz-peek-cta { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; min-height: 38px; padding: 0 14px;
               border-radius: var(--pz-btn-radius, 12px); background: var(--pz-accent-soft); color: var(--pz-accent-ink);
               font-size: 13px; font-weight: 650; line-height: 1; white-space: nowrap;
               transition: background-color .15s, color .15s; }
.pz-peek-cta .pz-ic { font-size: 15px; }
.pz-peek:hover .pz-peek-cta { background: var(--pz-accent); color: var(--pz-btn-text, #fff); }
/* Folded phones and the iPhone SE: the pill keeps its icon and drops the word —
   the badge and the raised card already say what it does. */
@media (max-width: 359px) {
  .pz-peek-cta { width: 40px; height: 40px; padding: 0; justify-content: center; font-size: 0; gap: 0; }
  .pz-peek-cta .pz-ic { font-size: 17px; }
}

/* Glyphs: inline SVG that takes the text's colour and size (see icons.ts). */
.pz-ic { display: inline-grid; place-items: center; width: 1em; height: 1em; flex-shrink: 0;
         vertical-align: -.15em; }
.pz-ic svg { width: 100%; height: 100%; display: block; }

/* The docked design bar: shown only when the page's own preview cannot be
   sticky and has scrolled away. A slim strip under the shop's header — the
   shape of a theme's sticky product bar — not a bubble in a corner. */
.pz-preview-fab { position: fixed; top: var(--pz-sticky-top, 0px); left: 0; right: 0; z-index: 2147483100;
                  display: flex; align-items: center; gap: 12px; width: min(100%, 720px); margin: 0 auto;
                  padding: 8px 12px; cursor: pointer; text-align: start;
                  background: rgba(255,255,255,.97); color: var(--pz-ink);
                  font-family: var(--pz-font); font-size: 13px; font-weight: 500; line-height: 1.25;
                  border: 1px solid var(--pz-line); border-top: 0; border-radius: 0 0 16px 16px;
                  box-shadow: 0 12px 32px -12px rgba(16,16,32,.3);
                  -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
.pz-preview-fab:hover { background: #fff; }
.pz-preview-fab-pic { display: block; width: 42px; height: 42px; border-radius: 9px; overflow: hidden;
                      background: var(--pz-surface-2); border: 1px solid var(--pz-line); flex-shrink: 0; }
.pz-preview-fab .pz-thumb { width: 42px !important; height: 42px !important; object-fit: cover;
                            border: 0; border-radius: 0; }
.pz-preview-fab-text { display: grid; gap: 2px; flex: 1; min-width: 0; }
.pz-preview-fab-text strong { font-size: 13.5px; font-weight: 650; min-width: 0; overflow-wrap: anywhere; }
.pz-preview-fab-text small { font-size: 11.5px; color: var(--pz-muted); min-width: 0; overflow-wrap: anywhere; }
.pz-preview-fab-ic { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; min-height: 36px; padding: 0 12px;
                     border-radius: var(--pz-btn-radius, 12px); background: var(--pz-accent-soft);
                     color: var(--pz-accent-ink); font-weight: 650; font-size: 13px; }
.pz-preview-fab-ic .pz-ic { font-size: 15px; }

/* ── The full-screen design view ─────────────────────────────────────────── */
/* Above the wizard, above chat bubbles. The plain background is for engines
   without <dialog>; a real dialog is transparent and dims through ::backdrop. */
.pz-lightbox { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483200; background: rgba(12,12,20,.78);
               display: grid; place-items: center; padding: 16px; }
.pz-lightbox.pz-layer { padding: 16px;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right))
           max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left)); }
/* The inner width is set from JS to the design's exact aspect, so the frame IS
   the design — no flanking background, no dependence on the canvas's own size. */
.pz-lightbox-inner { position: relative; display: flex; flex-direction: column; align-items: center; gap: 12px;
  max-width: calc(100% - 24px); max-height: calc(var(--pz-vvh, 100vh) - 32px); padding: 14px; box-sizing: content-box;
  border-radius: 20px; background: var(--pz-surface); box-shadow: var(--pz-shadow-lg); overflow: auto;
  animation: pz-pop-in .2s var(--pz-ease); }
.pz-lightbox-head { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px;
  min-height: 40px; }
.pz-lightbox-head strong { font-size: 16px; font-weight: 700; letter-spacing: -.01em; color: var(--pz-ink); }
.pz-lightbox-close { position: static; flex: 0 0 auto; }
.pz-lightbox-inner .pz-preview { position: relative; top: auto; display: block; flex: 0 0 auto;
  width: var(--pz-frame-width); max-width: 100%; border: 1px solid var(--pz-line); border-radius: 12px; box-shadow: none; }
.pz-lightbox-inner .pz-preview canvas, .pz-lightbox-inner .pz-preview.is-collapsed canvas {
  display: block; width: 100%; height: var(--pz-frame-height);
  max-height: none; max-width: 100%; margin: 0; transform: none !important; transition: none; }
.pz-lightbox-inner .pz-preview-toggle { display: none; }
.pz-lightbox-inner .pz-previews { display: flex; }
.pz-lightbox-hint { margin: 0; text-align: center; color: var(--pz-muted); font-size: 12.5px; line-height: 1.4; }
.pz-lightbox-hint b { font-weight: 700; color: var(--pz-ink-2); }
.pz-btn.pz-lightbox-done { width: 100%; min-height: 48px; padding: 12px 20px; font-size: 15px; }
@keyframes pz-pop-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (max-height: 520px) { .pz-lightbox-inner { gap: 6px; padding: 8px; } .pz-btn.pz-lightbox-done { min-height: 40px; } }

/* A finished sound: player first, cutter on request. */
.pz-playback { display: grid; gap: 8px; }
.pz-playback-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pz-playback-row .pz-player { flex: 1 1 210px; }
.pz-playback-row audio { flex: 1; min-width: 0; height: 40px; }
.pz-playback-row .pz-btn-ghost.small { padding: 8px 12px; font-size: 12.5px; white-space: nowrap; flex-shrink: 0; }

/* A YouTube song: results, YouTube's own player, and the od–do cutter. */
.pz-yt-results { display: grid; gap: 6px; margin-top: 8px; }
.pz-yt-results[hidden] { display: none; }
.pz-yt-result { display: grid; grid-template-columns: 88px minmax(0, 1fr) auto; align-items: center; gap: 10px;
  width: 100%; padding: 6px; text-align: left; cursor: pointer; font-family: inherit; color: var(--pz-ink);
  background: var(--pz-surface); border: 1px solid var(--pz-line); border-radius: 10px; }
.pz-yt-result:hover { border-color: var(--pz-accent-line); background: var(--pz-accent-soft); }
.pz-yt-result img { width: 88px; height: 50px; object-fit: cover; border-radius: 6px; background: var(--pz-surface-3); }
.pz-yt-result-text { display: grid; gap: 2px; min-width: 0; }
.pz-yt-result-text strong { font-size: 13.5px; font-weight: 600; line-height: 1.3; overflow: hidden;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.pz-yt-result-text span { font-size: 12px; color: var(--pz-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pz-yt-result-pick { font-size: 12.5px; font-weight: 700; color: var(--pz-accent-ink); padding: 0 6px; }
/* The pick: a card with the video, its name and "Promeni pesmu", over the cutter. */
.pz-yt-pick { display: grid; gap: 10px; min-width: 0; margin-top: 4px; }
.pz-yt-pick[hidden] { display: none; }
.pz-yt-song { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 12px; min-width: 0;
              padding: 8px; border: 1px solid var(--pz-line); border-radius: 14px; background: var(--pz-surface-2); }
/* YouTube's plain player (its script did not load) needs the width for its own controls. */
.pz-yt-song.is-plain { grid-template-columns: minmax(0, 1fr); }
.pz-yt-song-media { min-width: 0; }
.pz-yt-song-text { display: grid; gap: 3px; min-width: 0; justify-items: start; }
.pz-yt-song-title { font-size: 13.5px; font-weight: 650; line-height: 1.3; color: var(--pz-ink); overflow: hidden;
                    overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.pz-yt-song-title:empty, .pz-yt-song-meta:empty { display: none; }
.pz-yt-song-meta { font-size: 12px; color: var(--pz-muted); font-variant-numeric: tabular-nums; }
.pz-yt-song .pz-yt-change { margin-top: 3px; padding: 6px 11px; font-size: 12.5px; }
.pz-yt-frame { position: relative; width: 100%; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden; background: #000; }
.pz-yt-frame iframe, .pz-yt-frame > div { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.pz-yt-frame.is-compact { width: 128px; border-radius: 8px; background: #000 center / cover no-repeat; }
/* The player on its way: a quiet spinner on the still. */
.pz-yt-frame.is-loading::after { content: ""; position: absolute; inset: 0; margin: auto; width: 22px; height: 22px;
  border-radius: 50%; border: 2.5px solid rgba(255,255,255,.4); border-top-color: #fff; animation: pz-spin .8s linear infinite; }
.pz-yt-badge { position: absolute; left: 4px; right: 4px; bottom: 4px; padding: 2px 5px; border-radius: 6px;
  background: rgba(12,12,20,.74); color: #fff; font-size: 10px; font-weight: 650; line-height: 1.3; text-align: center; }
.pz-yt-badge[hidden] { display: none; }
.pz-yt-song.is-heard .pz-yt-badge { display: none; }
@media (max-width: 360px) { .pz-yt-frame.is-compact { width: 104px; } }
.pz-yt-tap { margin: 0; }
.pz-yt-tap[hidden] { display: none; }
.pz-hint.pz-yt-blocked { color: var(--pz-ink-2); font-weight: 600; }
.pz-hint.pz-yt-blocked[hidden] { display: none; }
/* "Tvoja pesma" → Da, before its video: the Spotify sleeve stands in for it. */
.pz-yt-song-cover { display: grid; place-items: center; width: 72px; height: 72px; border-radius: 8px; object-fit: cover;
                    background: var(--pz-surface-3); color: var(--pz-accent-ink); }
.pz-yt-song-cover svg { width: 26px; height: 26px; }
.pz-hint.pz-yt-looking { margin: 0; }
/* The answer is in: said once, calmly, under the card ("možeš odmah dalje"). */
.pz-snd-done-line { margin: 0; font-size: 13px; line-height: 1.45; font-weight: 600; color: #27613d; }
.pz-snd-done-line[hidden] { display: none; }
/* …unless everything together runs too long: then it says what to cut, calmly, not in green. */
.pz-snd-done-line.is-cut { color: var(--pz-warn); }
/* "Cela pesma ✓ · Iseci deo": the whole is the answer; the cutter opens on request. */
.pz-part-choice { display: flex; gap: 8px; min-width: 0; }
.pz-part-choice[hidden] { display: none; }
.pz-part-btn { display: inline-flex; flex: 1 1 0; align-items: center; justify-content: center; gap: 7px; min-width: 0;
               min-height: 42px; padding: 8px 12px; cursor: pointer; font: inherit; font-size: 13.5px; font-weight: 650;
               color: var(--pz-ink-2); background: var(--pz-surface); border: 1.5px solid var(--pz-line-2); border-radius: 12px;
               -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
.pz-part-btn svg { width: 16px; height: 16px; flex-shrink: 0; }
.pz-part-btn:hover { border-color: var(--pz-accent-line); }
.pz-part-btn.is-active { color: var(--pz-accent-ink); background: var(--pz-accent-soft); border-color: var(--pz-accent); }
.pz-part-btn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-part-whole:not(.is-active) svg { opacity: .35; }
.pz-yt-cutter[hidden], .pz-tt-cutter[hidden] { display: none; }

/* ── On-canvas photo editing ─────────────────────────────────────────────── */
.pz-edit { position: absolute; touch-action: none; cursor: grab; overflow: hidden;
           border: 1.5px dashed var(--pz-accent-line); border-radius: 4px;
           transition: border-color .15s, background .15s; }
/* The gesture-time stand-in: the photo itself, moved with CSS at 60fps while
   the real renderer waits for the fingers to lift. */
.pz-edit-live { position: absolute; max-width: none; pointer-events: none; z-index: 0; }
/* The slot-sized box the stand-in is drawn in, masked like the slot itself. */
.pz-edit-clip { position: absolute; top: 0; right: 0; bottom: 0; left: 0; overflow: hidden; pointer-events: none; z-index: 0;
                -webkit-mask-size: 100% 100%; mask-size: 100% 100%; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; }
/* A photo slot draws its dashes above the picture instead of as a border: a
   border takes room INSIDE the box (border-box), so the stand-in's clip sat
   1.5px in from the slot on every side and the photo moved off where it prints. */
.pz-edit-photo { border: 0; overflow: visible; }
.pz-edit-photo::after { content: ""; position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: 1; pointer-events: none;
                        border: 1.5px dashed var(--pz-accent-line); border-radius: 4px; transition: border-color .15s; }
.pz-edit-photo:hover::after, .pz-edit-photo.is-active::after { border-color: var(--pz-accent); }
/* The layers drawn over a photo, laid over it while it is being moved. It is a
   canvas inside .pz-preview, so every ".pz-preview canvas" rule reaches it: the
   popup's white card background turned the photo into a white box for as long
   as a finger was on it, and a phone's max-height squashed it off the layers.
   It keeps only the size and place canvas-edit gives it. */
.pz-edit-above { position: absolute; pointer-events: none; z-index: 2;
                 background: transparent !important; outline: 0 !important; border: 0 !important;
                 border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important;
                 max-width: none !important; max-height: none !important; min-width: 0 !important; min-height: 0 !important;
                 transform: none !important; transition: none !important; filter: none !important; }
.pz-edit-above[hidden] { display: none; }
/* ── Several clipart from a collection ──────────────────────────────────── */
.pz-multi { display: grid; gap: 8px; }
.pz-multi-chosen { display: flex; flex-wrap: wrap; gap: 6px; min-height: 0; }
.pz-multi-item { position: relative; width: 56px; height: 56px; border-radius: 9px; border: 1.5px solid var(--pz-accent);
                 background: #fff; display: grid; place-items: center; overflow: visible; }
.pz-multi-item img { width: 48px; height: 48px; object-fit: contain; }
.pz-multi-item .x { position: absolute; top: -7px; right: -7px; width: 20px; height: 20px; border-radius: 50%;
                    border: 1px solid #d8d8e0; background: #fff; font-size: 11px; line-height: 1; cursor: pointer; }
.pz-multi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(calc(var(--pz-tile-size) * var(--pz-tile-scale, .75)), 1fr)); gap: 6px;
                 max-height: 260px; overflow: auto; padding: 2px; }
.pz-multi-cell { padding: 4px; border: 1.5px solid #e2e2ea; border-radius: 9px; background: #fff; cursor: pointer; }
.pz-multi-cell img { width: 100%; aspect-ratio: 1; object-fit: contain; display: block; }
.pz-multi-cell:hover { border-color: var(--pz-accent); }
.pz-multi-grid.is-full .pz-multi-cell { opacity: .45; cursor: not-allowed; }
.pz-imagine { display: grid; gap: 8px; }


/* Accordions: each question folds under its label; the label is the toggle. */
.pz-accordion > .pz-label { cursor: pointer; display: flex; align-items: center; }
.pz-accordion > .pz-label::after { content: "\\25BE"; margin-left: auto; font-size: 12px; opacity: .6; transition: transform .15s; }
.pz-accordion.is-open > .pz-label::after { transform: rotate(180deg); }
.pz-accordion > :not(.pz-label) { display: none; }
.pz-accordion.is-open > :not(.pz-label) { display: revert; }

/* A movable layer: a dashed frame the shopper drags; nothing inside it. */
.pz-edit-move { cursor: default; border-style: dashed; overflow: visible; }
.pz-edit-move.can-move, .pz-edit-move.can-move.is-active { cursor: move; }
.pz-edit-grip { display: none; position: absolute; right: -7px; bottom: -7px; width: 14px; height: 14px;
                border-radius: 50%; background: #fff; border: 2px solid var(--pz-accent); cursor: nwse-resize; z-index: 2; }
.pz-edit-move.can-scale .pz-edit-grip { display: block; }
.pz-edit-live[hidden] { display: none; }
.pz-edit-tip { z-index: 1; }
.pz-edit:hover, .pz-edit.is-active { border-color: var(--pz-accent); background: var(--pz-accent-wash); }
.pz-edit.is-active { cursor: grabbing; }
/* The photo tools: a floating toolbar over the photo being edited
   (canvas-edit's pz-edit-pop). Tap a photo and it is selected: a solid outline
   and its tools just above it; tap anywhere else and they go. Nothing sits on
   the preview while the shopper does something else. */
.pz-edit-photo.is-selected::after, .pz-edit-map.is-selected { border-style: solid; border-color: var(--pz-accent);
  box-shadow: 0 0 0 1px rgba(255,255,255,.7); }
.pz-edit-pop { position: absolute; z-index: 5; display: flex; max-width: calc(100% - 12px);
  padding: 4px; border-radius: 14px; background: var(--pz-surface);
  border: 1px solid var(--pz-line); box-shadow: 0 6px 22px -6px rgba(16,16,32,.32), 0 1px 3px rgba(16,16,32,.1);
  transition: opacity .15s; animation: pz-pop-rise .16s var(--pz-ease); }
.pz-edit-pop[hidden] { display: none; }
/* Out of the way while a finger moves the photo, back when it lifts. */
.pz-edit-pop.is-busy { opacity: 0; pointer-events: none; }
@keyframes pz-pop-rise { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.pz-surface-active .pz-edit-pop { display: none !important; }
/* A folded preview is a strip, and so is the picture while a phone keyboard is
   up: no room for tools, and nothing to aim them at. */
.pz-preview.is-collapsed .pz-edit-pop, .pz-layer.has-keyboard .pz-edit-pop { display: none; }
.pz-edit-tools { display: flex; flex: none; align-items: center; gap: 2px; min-width: 0; }
.pz-edit-btn { display: inline-flex; align-items: center; justify-content: center; gap: 5px;
               min-width: 34px; height: 34px; padding: 0 7px; cursor: pointer; flex: none;
               color: var(--pz-ink-2); background: transparent; border: 0; border-radius: 10px;
               font: 600 12.5px/1 inherit; white-space: nowrap;
               -webkit-tap-highlight-color: transparent; transition: background-color .12s, color .12s; }
.pz-edit-btn:hover { background: var(--pz-surface-2); color: var(--pz-accent-ink); }
.pz-edit-btn:active { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-edit-btn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 1px; }
.pz-edit-btn:disabled { opacity: .35; cursor: default; background: transparent; color: var(--pz-ink-2); }
.pz-edit-btn.pz-edit-expand { color: var(--pz-accent-ink); background: var(--pz-accent-soft); margin-left: 2px; }
.pz-edit-sep { width: 1px; height: 20px; margin: 0 3px; background: var(--pz-line); flex: none; }
/* Too narrow for the words: icons only, a little closer together. */
.pz-edit-pop.is-compact .pz-edit-btn span { display: none; }
.pz-edit-pop.is-compact .pz-edit-btn { min-width: 34px; padding: 0 4px; }
.pz-edit-pop.is-compact .pz-edit-sep { margin: 0 1px; }
.pz-edit-turn { display: none; }
.pz-edit-pop.can-rotate .pz-edit-turn { display: inline-flex; }
.pz-edit-pop.can-rotate span.pz-edit-turn { display: block; }
.pz-edit-tip { position: absolute; left: 50%; bottom: 6px; transform: translateX(-50%);
               padding: 3px 9px; border-radius: 99px; white-space: nowrap;
               background: rgba(17,17,24,.72); color: #fff; font-size: 10.5px;
               opacity: 0; transition: opacity .15s; pointer-events: none; }
.pz-edit:hover .pz-edit-tip { opacity: 1; }
.pz-edit.is-active .pz-edit-tip { opacity: 0; }

/* Library swap: two graphics from the same collection trade places when one is
   dragged onto the other. The drop target lights up so the gesture is legible. */
.pz-edit-swap { cursor: grab; border-style: dashed; background: transparent; }
.pz-edit-swap.is-active { cursor: grabbing; opacity: .75; z-index: 3; }
.pz-edit-swap.is-drop { border-color: var(--pz-accent); border-style: solid;
                        background: var(--pz-accent-ring); box-shadow: 0 0 0 3px var(--pz-accent-ring); }

/* ── "Uredi sliku": the photo's framing on a screen of its own ──────────── */
/* A crop screen as photo apps have it: dark, the printed part bright, the rest
   of the picture dimmed around it, the controls under the thumb. */
/* Two classes: dialog.pz-layer resets padding, background and size for every layer. */
.pz-framer.pz-layer { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483300;
  display: grid; place-items: center; padding: 16px; }
.pz-framer[hidden] { display: none; }
.pz-framer.pz-layer--fixed { background: rgba(8,8,14,.78); }
.pz-framer-card { display: flex; flex-direction: column; gap: 12px; width: 520px; max-width: 100%;
  height: 100%; max-height: 760px; padding: 14px 16px 16px; box-sizing: border-box;
  border-radius: 20px; background: #15161b; color: #f4f4f7; box-shadow: var(--pz-shadow-lg);
  animation: pz-pop-in .2s var(--pz-ease); }
.pz-framer-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 36px; }
.pz-framer-head strong { font-size: 16px; font-weight: 700; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-framer-stage { position: relative; flex: 1 1 auto; min-height: 180px; overflow: hidden; border-radius: 14px;
  display: grid; place-items: center; touch-action: none; cursor: grab; outline: 0;
  background: repeating-conic-gradient(#202128 0% 25%, #1a1b21 0% 50%) 50% / 20px 20px; }
.pz-framer-stage.is-active { cursor: grabbing; }
.pz-framer-stage:focus-visible { box-shadow: inset 0 0 0 2px var(--pz-accent); }
.pz-framer-box { position: relative; flex: none; }
.pz-framer-ghost, .pz-framer-pic { position: absolute; max-width: none; pointer-events: none; user-select: none; }
.pz-framer-ghost { opacity: .32; }
.pz-framer-ghost[hidden] { display: none; }
.pz-framer-window { position: absolute; top: 0; right: 0; bottom: 0; left: 0; overflow: hidden; pointer-events: none;
  -webkit-mask-size: 100% 100%; mask-size: 100% 100%; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; }
.pz-framer-grid { position: absolute; top: 0; right: 0; bottom: 0; left: 0; pointer-events: none;
  outline: 2px solid rgba(255,255,255,.92); box-shadow: 0 0 0 1px rgba(0,0,0,.35); }
/* Thirds, shown while a finger is down: where eyes and horizons sit well. */
.pz-framer-grid i { position: absolute; background: rgba(255,255,255,.55); opacity: 0; transition: opacity .15s; }
.pz-framer-stage.is-active .pz-framer-grid i { opacity: 1; }
.pz-framer-grid i:nth-child(1), .pz-framer-grid i:nth-child(2) { top: 0; bottom: 0; width: 1px; }
.pz-framer-grid i:nth-child(1) { left: 33.333%; }
.pz-framer-grid i:nth-child(2) { left: 66.666%; }
.pz-framer-grid i:nth-child(3), .pz-framer-grid i:nth-child(4) { left: 0; right: 0; height: 1px; }
.pz-framer-grid i:nth-child(3) { top: 33.333%; }
.pz-framer-grid i:nth-child(4) { top: 66.666%; }
.pz-framer-hint { margin: 0; text-align: center; font-size: 12.5px; color: #a9aab5; }
.pz-framer-zoom { display: flex; align-items: center; gap: 10px; }
.pz-framer-slider { flex: 1 1 auto; min-width: 0; height: 32px; margin: 0; accent-color: var(--pz-accent); cursor: pointer; }
.pz-framer-tools { display: flex; justify-content: center; gap: 8px; }
.pz-framer-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-width: 44px; height: 44px; padding: 0 12px; border: 0; border-radius: 12px; cursor: pointer;
  background: #25262e; color: #f4f4f7; font: 600 13.5px/1 inherit; -webkit-tap-highlight-color: transparent; }
.pz-framer-btn:hover { background: #30313b; }
.pz-framer-btn:active { background: #3a3b47; }
.pz-framer-btn:disabled { opacity: .4; cursor: default; }
.pz-framer-btn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-framer-x { background: transparent; padding: 0; }
.pz-framer-actions { display: grid; grid-template-columns: 1fr 1.6fr; gap: 10px; }
.pz-framer-actions .pz-btn, .pz-framer-actions .pz-btn-ghost { min-height: 48px; width: 100%; justify-content: center; }
.pz-framer-actions .pz-btn-ghost { background: #25262e; color: #f4f4f7; border-color: #34353f; }
@media (max-width: 699px) {
  .pz-framer.pz-layer { padding: 0; place-items: stretch; }
  .pz-framer-card { width: 100%; height: 100%; max-height: none; border-radius: 0;
    padding: max(12px, env(safe-area-inset-top)) 14px max(14px, env(safe-area-inset-bottom)); }
}

/* ── Form ────────────────────────────────────────────────────────────────── */
.pz-form { display: grid; grid-template-columns: minmax(0,1fr); gap: 20px; min-width: 0; max-width: 100%; }
.pz-row { display: grid; grid-template-columns: minmax(0,1fr); gap: 8px; min-width: 0; max-width: 100%; }
.pz-row[hidden] { display: none; }
.pz-label { display: block; font-weight: 600; font-size: 14px; line-height: 1.35; letter-spacing: .005em; color: var(--pz-ink); }
.pz-required { color: var(--pz-danger); font-weight: 700; }
/* The surcharge beside a label, as a quiet tag rather than more label text. */
.pz-price { display: inline-block; margin-inline-start: 6px; padding: 2px 8px; border-radius: 99px;
            vertical-align: 1px; background: var(--pz-surface-3); color: var(--pz-ink-2);
            font-size: 11.5px; font-weight: 600; line-height: 1.4; white-space: nowrap; }
.pz-hint { margin: 0; font-size: 12.5px; color: var(--pz-muted); line-height: 1.45; }

.pz-input { display: block; width: 100%; max-width: 100%; min-height: var(--pz-control-h, 46px); padding: 11px 14px;
            font-family: inherit; font-size: 15px; line-height: 1.35; color: var(--pz-ink);
            background: var(--pz-surface); border: 1.5px solid var(--pz-line-2); border-radius: var(--pz-radius);
            box-shadow: var(--pz-shadow-sm); transition: border-color .15s, box-shadow .15s, background-color .15s; }
/* iOS paints its own inner shadow and rounding on text fields. Dates keep
   theirs: without it the native picker's value collapses to nothing. */
input.pz-input:not([type="date"]):not([type="time"]):not([type="color"]), textarea.pz-input {
  -webkit-appearance: none; appearance: none; }
.pz-input::placeholder { color: var(--pz-subtle); opacity: 1; }
.pz-input:hover { border-color: var(--pz-line-hover); }
.pz-input:focus, .pz-input:focus-visible { outline: none; border-color: var(--pz-accent);
  box-shadow: 0 0 0 4px var(--pz-accent-ring); }
/* A shopper can drag a <textarea>'s native resize handle; without a hard cap
   that drag can widen it past its own row and reopen the whole bug sideways. */
textarea.pz-input { max-width: 100%; min-height: 104px; resize: vertical; line-height: 1.5; }
/* One arrow on every platform, in the widget's own grey. */
select.pz-input { -webkit-appearance: none; appearance: none; cursor: pointer; padding-right: 42px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23676774' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 14px center; background-size: 18px 18px; }
select.pz-input::-ms-expand { display: none; }
.pz-input-small { width: 86px; text-align: center; }
.pz-color { width: 56px; height: var(--pz-control-h, 46px); padding: 4px; border: 1.5px solid var(--pz-line-2);
            border-radius: var(--pz-radius); background: var(--pz-surface); cursor: pointer; }

/* Swatches */
.pz-swatches { display: flex; flex-wrap: wrap; gap: 10px; }
.pz-swatch { position: relative; display: grid; place-items: center; gap: 4px; min-width: 52px; min-height: 44px;
             padding: 6px 10px; cursor: pointer; font-family: inherit; color: var(--pz-ink); background: var(--pz-surface);
             border: 1.5px solid var(--pz-line-2); border-radius: var(--pz-radius);
             -webkit-tap-highlight-color: transparent;
             transition: border-color .15s, box-shadow .15s, background-color .15s, transform .1s; }
.pz-swatch:hover { border-color: var(--pz-line-hover); }
.pz-swatch:active { transform: scale(.97); }
.pz-swatch:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-swatch img { display: block; width: 44px; height: 44px; object-fit: cover; border-radius: 6px; }
.pz-swatch.is-active { border-color: var(--pz-selection, var(--pz-accent));
                       box-shadow: 0 0 0 3px var(--pz-accent-ring); }
/* A picture or colour choice says "picked" with a tick, not only a border —
   a border alone is invisible to anyone who cannot tell the colours apart. */
.pz-swatch--img.is-active::after, .pz-swatch--color.is-active::after, .pz-swatch--font.is-active::after {
  content: ''; position: absolute; top: 4px; right: 4px; z-index: 1; width: 18px; height: 18px; border-radius: 99px;
  background: var(--pz-selection, var(--pz-accent)) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='3.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E") center / 11px 11px no-repeat;
  box-shadow: 0 0 0 2px #fff, 0 1px 3px rgba(16,16,32,.25); }
/* "Moji detalji" on the design card. */
.pz-moment { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin: 2px 0 10px; }
.pz-moment[hidden], .pz-moment button[hidden] { display: none; }
.pz-moment .pz-hint { flex: 1 1 100%; }

/* The page's own choices inside the designer's Options step. */
.pz-page-options { display: grid; gap: 14px; }
.pz-page-opt { display: grid; gap: 8px; }
.pz-page-opt-title { font: 600 13px/1.3 inherit; color: var(--pz-ink); }
.pz-page-opt-row, .pz-page-qty { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.pz-page-opt-btn { min-width: 44px; min-height: 44px; padding: 6px 12px; border: 1.5px solid var(--pz-line-2);
                   border-radius: var(--pz-radius); background: var(--pz-surface); color: var(--pz-ink);
                   font: 500 13px/1.2 inherit; cursor: pointer; }
.pz-page-opt-btn.is-active { border-color: var(--pz-selection, var(--pz-accent)); box-shadow: 0 0 0 3px var(--pz-accent-ring); }
.pz-page-opt-btn:disabled { opacity: .4; cursor: not-allowed; text-decoration: line-through; }
.pz-page-qty-n { min-width: 36px; text-align: center; font: 600 15px/1 inherit; }
.pz-page-price { padding: 10px 12px; border-radius: var(--pz-radius); background: #f4f4f8; font: 600 14px/1.3 inherit; }
.pz-swatches--scroll { flex-wrap: nowrap; overflow-x: auto; padding: 2px 2px 8px; scroll-snap-type: x proximity;
                       scrollbar-width: thin; -webkit-overflow-scrolling: touch; }
.pz-swatches--scroll .pz-swatch { flex: 0 0 auto; scroll-snap-align: start; }
.pz-dropdown-search { display: grid; gap: 8px; }
.pz-zoom-hover canvas { transition: transform .08s ease-out; cursor: zoom-in; }
.pz-zoom-click { cursor: zoom-in; }
/* WAPF-style picture choices: big image, name and price stacked underneath. */
.pz-swatch--color { display: flex; flex-direction: column; align-items: center; gap: 6px;
                    min-width: 64px; padding: 8px 6px 7px; }
.pz-swatch-dot { display: block; width: var(--pz-swatch-size); height: var(--pz-swatch-size); border-radius: var(--pz-swatch-radius);
                 border: 1px solid rgba(0,0,0,.16); box-shadow: inset 0 0 0 2px #fff; }
.pz-swatch-name { font-size: 11.5px; line-height: 1.2; text-align: center; max-width: 76px; color: var(--pz-ink-2); }
.pz-swatch--img { display: flex; flex-direction: column; align-items: center; gap: 6px;
                  padding: 8px 8px 9px; }
.pz-swatch--img img { width: calc(var(--pz-tile-size) * var(--pz-tile-scale, 1));
                      height: calc(var(--pz-tile-size) * var(--pz-tile-scale, 1)); border-radius: 8px; }
.pz-swatch-label { font-size: 13px; font-weight: 650; line-height: 1.25; max-width: 130px; text-align: center; }
.pz-swatch-price { font-size: 12px; color: var(--pz-muted); line-height: 1.15; }
.pz-swatch--img.is-active .pz-swatch-label { color: var(--pz-accent-ink); }
/* A dropdown swatch / compact swatch: small pictures in a row. */
.pz-swatches--small { gap: 8px; }
.pz-swatches--small .pz-swatch--img { gap: 3px; padding: 6px; }
.pz-swatches--small .pz-swatch-label { font-size: 11px; max-width: 64px; }
/* The picture's frame: a backdrop of its own, so transparent art is seen.
   An opaque picture covers it (and it is the placeholder while loading); a
   transparent one is shown whole on it, its empty margins trimmed; mostly
   white art (a white pin, a white outline) gets the dark backdrop, or it
   vanishes into the white tile. The choice's own colour is never used here.
   See tile-art.ts and choice-display.ts in @pz/scene. */
.pz-swatch-pic { display: block; position: relative; overflow: hidden; border-radius: 8px;
                 background: var(--pz-tile-light); }
.pz-swatch-pic img { border-radius: 0; }
.pz-swatch-pic.has-alpha img, .pz-swatch-pic.is-whole img { object-fit: contain; padding: 8%; }
.pz-swatch-pic.is-dark-ground, .pz-multi-cell.is-dark-ground, .pz-multi-item.is-dark-ground {
  background: var(--pz-tile-dark); }
.pz-multi-cell.has-alpha:not(.is-dark-ground) { background: var(--pz-tile-light); }
/* Picture choices in an even grid: every tile the same size whatever its
   caption, as many to a row as fit, rows the same height. */
.pz-swatches--tiles { display: grid;
                      grid-template-columns: repeat(auto-fill, minmax(calc(var(--pz-tile-size) * var(--pz-tile-scale, 1)), 1fr));
                      grid-auto-rows: 1fr; gap: 8px; }
.pz-swatches--tiles .pz-swatch--img { min-width: 0; justify-content: flex-start; gap: 5px; padding: 5px 5px 7px; }
.pz-swatches--tiles .pz-swatch-pic { width: 100%; aspect-ratio: 1 / 1; flex: none; }
.pz-swatches--tiles .pz-swatch--img img { width: 100%; height: 100%; }
.pz-swatches--tiles .pz-swatch-label { max-width: 100%; font-size: 12px; line-height: 1.2; overflow-wrap: anywhere;
                                       display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pz-swatches--small.pz-swatches--tiles { gap: 6px; }
.pz-swatches--small.pz-swatches--tiles .pz-swatch-label { font-size: 11px; }
/* Captions left off: the tile is the picture, at the question's size. */
.pz-swatches--tiles.pz-swatches--bare .pz-swatch--img { padding: 4px; justify-content: center; }
.pz-swatches--bare .pz-swatch--img.is-active::after { top: 2px; inset-inline-end: 2px; width: 16px; height: 16px; background-size: 10px 10px; }
/* Customily's one scrolling row keeps its row, with tiles of the grid's size. */
.pz-swatches--scroll.pz-swatches--tiles { display: flex; }
.pz-swatches--scroll.pz-swatches--tiles .pz-swatch--img { width: calc(var(--pz-tile-size) * var(--pz-tile-scale, 1) + 10px); }
/* Customily's Text Swatch: the plain word as a pill, no picture and no colour. */
.pz-swatch--text { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 9px 16px; }
.pz-swatch--text .pz-swatch-label { font-weight: 600; }
.pz-swatch--text.is-active { background: var(--pz-accent-soft); }
.pz-swatch--text.is-active .pz-swatch-label { color: var(--pz-accent-ink); }
/* Font choices: each one drawn in its own font with the shopper's words, so
   the typeface is seen while picking (@pz/scene choiceFontId). An even grid,
   one line per sample, cut with an ellipsis rather than wrapped. */
.pz-swatches--fonts { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 8px; }
.pz-swatch--font { display: flex; flex-direction: column; align-items: stretch; justify-content: center; gap: 1px;
                   min-width: 0; min-height: 60px; padding: 8px 6px; }
.pz-swatch-sample { display: block; min-width: 0; padding: 2px 12px; font-size: 21px; font-weight: 400; line-height: 1.4;
                    text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pz-swatch-sample.is-long { font-size: 16px; }
.pz-swatch--font .pz-swatch-label { max-width: 100%; font-size: 11.5px; font-weight: 600; color: var(--pz-muted); }
.pz-swatch--font.is-active { background: var(--pz-accent-soft); }
.pz-swatch--font.is-active::after { top: 3px; right: 3px; width: 16px; height: 16px; background-size: 10px 10px; }
.pz-swatches--small.pz-swatches--fonts { grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); gap: 6px; }
.pz-swatches--small .pz-swatch--font { min-height: 50px; }
.pz-swatches--small .pz-swatch-sample { font-size: 17px; }
.pz-swatches--small .pz-swatch-sample.is-long { font-size: 14px; }
.pz-swatches--scroll.pz-swatches--fonts { display: flex; }
.pz-swatches--scroll .pz-swatch--font { width: 156px; }
/* A value the merchant disabled: still listed, so the choice is known to exist. */
.pz-swatch.is-disabled { opacity: .42; cursor: not-allowed; filter: grayscale(.7); }
.pz-swatch.is-disabled:hover { border-color: var(--pz-line-2); }
.pz-swatch.is-disabled:active { transform: none; }

/* Sections (a shop setting): a long form as folding or paged groups. */
.pz-section { border: 1px solid var(--pz-line); border-radius: var(--pz-radius); padding: 2px 14px 14px; margin: 0;
              background: var(--pz-surface); }
.pz-section-body { display: grid; grid-template-columns: minmax(0,1fr); gap: 18px; }
.pz-section-head { cursor: pointer; -webkit-user-select: none; user-select: none; }
.pz-section > .pz-row.pz-divider.pz-section-head { margin: 0; padding: 12px 0; border-top: 0; }
.pz-section-head .pz-divider-label::after { content: '\\25BE'; float: right; opacity: .55; transition: transform .2s var(--pz-ease); }
.pz-section.is-open > .pz-section-head .pz-divider-label::after { transform: rotate(180deg); }
.pz-section:not(.is-open) > .pz-section-body { display: none; }
.pz-section-nav { display: flex; justify-content: space-between; gap: 8px; margin: 4px 0 0; }
.pz-section-error { color: var(--pz-danger); font-size: 13px; margin: 0; }

/* Colour picker: the native swatch with its hex beside it. */
.pz-colorpick { display: flex; align-items: center; gap: 10px; }
.pz-colorpick-hex { width: 110px; flex: 0 0 auto; font: 13px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace;
                    text-transform: uppercase; }

/* Multiple Upload Image: one button over a card per bound placeholder. */
.pz-multiupload { display: grid; gap: 10px; }
.pz-multiupload-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 8px; }
.pz-multiupload-cell { position: relative; display: grid; place-items: center; gap: 3px; padding: 6px 4px;
                       min-height: 80px; border: 1.5px dashed var(--pz-line-2); border-radius: var(--pz-radius);
                       background: var(--pz-surface-2); }
.pz-multiupload-cell.is-filled { border-style: solid; border-color: var(--pz-accent); background: var(--pz-surface); }
.pz-multiupload-cell.is-uploading { border-color: var(--pz-accent); background: var(--pz-surface); }
.pz-multiupload-progress { font-size: 10px; line-height: 1.35; text-align: center; color: var(--pz-muted); overflow-wrap: anywhere; }
.pz-multiupload-progress[hidden] { display: none; }
.pz-multiupload-cell.is-upload-failed { border-color: #b45309; }
.pz-multiupload-cell img { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 8px; }
.pz-multiupload-cell img[hidden] { display: none; }
.pz-multiupload-cell.is-filled .pz-multiupload-slot { display: none; }
.pz-multiupload-slot { font-size: 11px; line-height: 1.25; text-align: center; color: var(--pz-muted); }
.pz-multiupload-cell .x { position: absolute; top: -7px; right: -7px; width: 22px; height: 22px; border-radius: 50%;
                          display: grid; place-items: center; font-size: 11px; cursor: pointer;
                          border: 1px solid var(--pz-line-2); background: var(--pz-surface); color: var(--pz-ink-2);
                          box-shadow: var(--pz-shadow-sm); }
.pz-multiupload-cell .x[hidden] { display: none; }
/* ‹ › under a photo: swap it with its neighbour. Big enough to tap. */
.pz-multiupload-move { position: absolute; bottom: 4px; width: 26px; height: 26px; padding: 0; border: 0;
                       border-radius: 50%; display: grid; place-items: center; cursor: pointer;
                       font-size: 17px; line-height: 1; color: #fff; background: rgba(20,20,30,.62); }
.pz-multiupload-move.is-back { left: 4px; }
.pz-multiupload-move.is-forward { right: 4px; }
.pz-multiupload-move:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 1px; }
.pz-multiupload-cell.is-filled[draggable="true"] { cursor: grab; }
.pz-multiupload-cell.is-dragging { opacity: .45; }
.pz-multiupload-count { margin: 0; font-weight: 600; }
/* A divider: a heading with a rule over it, sectioning a long form. */
.pz-row.pz-divider { margin-top: 4px; padding-top: 16px; gap: 2px; border-top: 1px solid var(--pz-line); }
.pz-form > .pz-row.pz-divider:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
.pz-divider-label { font-weight: 700; font-size: 15.5px; letter-spacing: -.01em; line-height: 1.3; color: var(--pz-ink); }

.pz-check { display: flex; align-items: center; gap: 10px; min-height: 40px; cursor: pointer; color: var(--pz-ink); }
.pz-check input { width: 20px; height: 20px; margin: 0; flex-shrink: 0; cursor: pointer; accent-color: var(--pz-accent); }


/* ── Map place picker ─────────────────────────────────────────────────────── */
/* The field opens the dialog and never takes typing, like the song field; only
   the cursor says so, because it still has to read as the answer. */
.pz-map { display: grid; gap: 10px; min-width: 0; }
.pz-map-field { cursor: pointer; caret-color: transparent; }

/* The dialog. Its z-index clears the wizard popup's (2147483000): the picker is
   opened FROM a form that may already be inside that popup. */
.pz-map-modal { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483100;
                display: grid; place-items: center; padding: 16px;
                background: rgba(16,16,24,.62); }
.pz-map-modal[hidden] { display: none; }
.pz-map-card { position: relative; display: grid; gap: 10px; width: 460px;
               max-width: 100%; padding: 16px; background: #fff;
               border-radius: 16px; box-shadow: 0 18px 60px rgba(10,10,30,.35); }
@media (max-width: 492px) { .pz-map-card { width: 100%; } }
.pz-map-status:empty { display: none; }

/* Suggestions sit between the box and the map: five hits, the same depth
   Mapbox's own geocoder shows. */
.pz-map-suggestions { display: grid; gap: 4px; max-height: 220px; overflow-y: auto; }
.pz-map-suggestions[hidden] { display: none; }
.pz-map-suggestion { display: grid; gap: 1px; width: 100%; min-width: 0; text-align: left;
                     padding: 8px 11px; font: inherit; cursor: pointer; background: #fff;
                     border: 1.5px solid #dcdce4; border-radius: 10px;
                     transition: border-color .12s, background .12s; }
.pz-map-suggestion:hover { border-color: var(--pz-accent); background: var(--pz-accent-soft); }
.pz-map-place { font-size: 13.5px; font-weight: 700; min-width: 0;
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-map-detail { color: #71717d; font-size: 12px; min-width: 0;
                 overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* The map itself. touch-action:none is what lets a finger drag it instead of
   scrolling the page under it. */
.pz-map-canvas { position: relative; width: 100%; height: 320px; overflow: hidden;
                 border-radius: 12px; background: #e9e9ef; touch-action: none; cursor: grab; }
.pz-map-canvas canvas { display: block; width: 100%; height: 100%; }
.pz-map-zooms { position: absolute; top: 10px; right: 10px; z-index: 2;
                display: grid; gap: 1px; border-radius: 6px; overflow: hidden;
                box-shadow: 0 1px 4px rgba(10,10,30,.35); }
.pz-map-zoom { width: 29px; height: 29px; padding: 0; cursor: pointer;
               font: 600 17px/1 inherit; color: #33333d; background: #fff; border: 0; }
.pz-map-zoom:hover { background: #f0f0f4; }

.pz-map-actions { display: flex; justify-content: flex-end; gap: 10px; }
/* Doubled selectors: the generic .pz-btn rule is declared later in this sheet
   and would otherwise paint both buttons in the theme's dark button colour. */
.pz-btn.pz-map-cancel { color: #45454f; background: #fff; border: 1.5px solid #d8d8e0; }
.pz-btn.pz-map-cancel:hover { background: #f4f4f8; }
/* Customily's primary button on this dialog is red; keeping it red is the
   difference between "the same product" and "a lookalike". */
.pz-btn.pz-map-select { color: #fff; background: #e4322b; }
.pz-btn.pz-map-select:hover { background: #c8271f; }

/* ── Spotify song picker ──────────────────────────────────────────────────── */
/* The field is read-only: it opens the dialog, it never takes typing. It has to
   look like the answer once one exists, so it keeps the ordinary input look and
   only the cursor says "this opens something". */
.pz-song { display: grid; gap: 10px; min-width: 0; }
/* The song block measures itself: it sits in the page's column or in the
   popup's step pane (outside that column), and must be compact in both. */
@supports (container-type: inline-size) { .pz-song { container: pz-song / inline-size; } }
.pz-spotify-field { cursor: pointer; caret-color: transparent; }
.pz-spotify-field:focus { border-color: #1db954; box-shadow: 0 0 0 3px rgba(29,185,84,.15); }

/* What the product will say, under the picker: the pick fills these in and the
   shopper edits them in place, so the panel only exists once a song does. */
.pz-song-text { border: 1.5px solid #e0e0e8; border-radius: 12px; padding: 12px 13px;
                background: #fafafc; display: grid; gap: 9px; }
.pz-song-text[hidden] { display: none; }
.pz-song-text-title { font-size: 13px; font-weight: 650; color: #37374a; }
/* Positioned, so a line's own character counter sits on the line, not on the picker row. */
.pz-song-line { position: relative; display: grid; gap: 5px; }
.pz-song-line-label { font-size: 12.5px; font-weight: 600; color: #4a4a55; }
.pz-song-text-foot { display: flex; justify-content: space-between; align-items: center;
                     gap: 10px; flex-wrap: wrap; }
.pz-song-reset { font-size: 13px; padding: 6px 10px; flex-shrink: 0; }
.pz-song-reset[hidden] { display: none; }

/* The dialog. Its z-index clears the wizard popup's (2147483000): the picker is
   opened FROM a form that may already be inside that popup. */
.pz-spotify-modal { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483100;
                    display: grid; place-items: center; padding: 16px;
                    background: rgba(16,16,24,.62); }
.pz-spotify-modal[hidden] { display: none; }

/* The variation step (variant-step.ts): picture tiles, three to a row (two
   on the narrowest phones); the picked one ringed and ticked. */
.pz-variant { display: grid; gap: 16px; }
.pz-variant[hidden] { display: none; }
.pz-variant-section { display: grid; gap: 10px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--pz-line, #e6e6ec); }
.pz-variant-group { display: grid; gap: 8px; }
.pz-variant-label { margin: 0; font-size: 15px; font-weight: 700; }
.pz-variant-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
@media (max-width: 359px) { .pz-variant-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
/* A phone: four small tiles a row (a Mini Box asks colour, letters and sound —
   three rows of big tiles were a long scroll before the first question). */
@media (max-width: 600px) {
  .pz-variant { gap: 12px; }
  .pz-variant-group { gap: 6px; }
  .pz-variant-label { font-size: 14px; }
  .pz-variant-grid { grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 6px; }
  .pz-variant-tile { gap: 2px; min-height: 44px; padding: 4px 4px 6px; border-radius: 10px; }
  .pz-variant-img { border-radius: 6px; }
  .pz-variant-name { font-size: 12.5px; line-height: 1.2; }
  .pz-variant-price { font-size: 11px; line-height: 1.25; }
  .pz-variant-price del { display: block; margin-right: 0; }
  .pz-variant-tick { top: 3px; right: 3px; width: 18px; height: 18px; }
  .pz-variant-tick svg { width: 11px; height: 11px; }
}
.pz-variant-tile { position: relative; display: grid; justify-items: center; align-content: start; gap: 4px; min-width: 0;
                   min-height: 56px; padding: 8px 8px 10px; font: inherit; text-align: center; cursor: pointer;
                   color: var(--pz-ink, #16161d); background: var(--pz-surface, #fff);
                   border: 1.5px solid var(--pz-line-2, #d8d8e0); border-radius: var(--pz-radius, 12px);
                   -webkit-tap-highlight-color: transparent; transition: border-color .15s, box-shadow .15s; }
.pz-variant-tile:hover:not(:disabled) { border-color: var(--pz-line-hover, #b9b9c6); }
.pz-variant-tile.is-on { border-color: var(--pz-accent); box-shadow: inset 0 0 0 1.5px var(--pz-accent); }
.pz-variant-tile:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-variant-tile:disabled { cursor: default; opacity: .5; }
/* Buyable, but not with the other picks: muted, still a tile one can take. */
.pz-variant-tile.is-blocked:not(.is-on) .pz-variant-img { opacity: .55; }
.pz-variant-tile.is-blocked .pz-variant-price { color: #b4232f; }
.pz-variant-img { display: block; width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 8px;
                  background: var(--pz-surface-2, #f4f4f8); }
.pz-variant-name { font-size: 14.5px; font-weight: 650; line-height: 1.25; overflow-wrap: anywhere; }
.pz-variant-price { font-size: 13px; color: var(--pz-muted, #6b6b78); }
.pz-variant-price del { margin-right: 2px; }
.pz-variant-tick { position: absolute; top: 6px; right: 6px; display: none; place-items: center; width: 24px; height: 24px;
                   border-radius: 99px; color: var(--pz-btn-text, #fff); background: var(--pz-accent);
                   box-shadow: 0 0 0 2px #fff; }
.pz-variant-tile.is-on .pz-variant-tick { display: grid; }
.pz-variant-error { margin: 0; font-size: 13.5px; font-weight: 600; color: var(--pz-danger, #c0392b); }
.pz-variant-reset { justify-self: start; min-height: 44px; padding: 0 2px; border: 0; background: none; font: inherit;
                     font-size: 14px; font-weight: 600; color: var(--pz-accent); text-decoration: underline; cursor: pointer; }

/* "Završi" (finish.ts): the date, the delivery promise, how many. Phone first:
   two big choices, 44 px calendar days, a 44 px stepper. */
.pz-finish { display: grid; gap: 18px; }
.pz-finish[hidden] { display: none; }
.pz-finish-section { display: grid; gap: 10px; margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--pz-line, #e6e6ec); }
.pz-finish-title { margin: 0; font-size: 17px; font-weight: 750; }
.pz-finish-when { display: grid; gap: 10px; }
.pz-finish-label { margin: 0; font-size: 15px; font-weight: 700; color: var(--pz-ink, #16161d); }
.pz-finish-label .pz-req { color: var(--pz-danger, #c0392b); }
.pz-finish-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pz-finish-opt { display: grid; align-content: center; gap: 2px; min-height: 64px; padding: 10px 12px; text-align: left;
                 font: inherit; color: var(--pz-ink, #16161d); background: var(--pz-surface, #fff); cursor: pointer;
                 border: 1.5px solid var(--pz-line-2, #d8d8e0); border-radius: var(--pz-radius, 12px);
                 -webkit-tap-highlight-color: transparent; transition: border-color .15s, box-shadow .15s, background-color .15s; }
.pz-finish-opt:hover { border-color: var(--pz-line-hover, #b9b9c6); }
.pz-finish-opt[aria-pressed="true"] { border-color: var(--pz-accent); background: var(--pz-accent-wash, #f4f3ff);
                                      box-shadow: inset 0 0 0 1px var(--pz-accent); }
.pz-finish-opt:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-finish-opt-title { font-size: 15.5px; font-weight: 700; line-height: 1.25; }
.pz-finish-opt-sub { font-size: 12.5px; color: var(--pz-muted, #6b6b78); line-height: 1.3; }
.pz-finish-opt-sub[hidden] { display: none; }
.pz-finish-help { margin: 0; font-size: 13px; line-height: 1.45; color: var(--pz-muted, #6b6b78); }
.pz-finish-error { margin: 0; font-size: 13.5px; font-weight: 600; color: var(--pz-danger, #c0392b); }
.pz-dcal { display: grid; gap: 6px; padding: 10px; border: 1px solid var(--pz-line, #e6e6ec); border-radius: var(--pz-radius, 12px);
          background: var(--pz-surface, #fff); }
.pz-dcal[hidden] { display: none; }
.pz-dcal-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.pz-dcal-title { font-size: 15px; }
.pz-dcal-nav { display: grid; place-items: center; width: 44px; height: 44px; padding: 0; cursor: pointer; color: inherit;
              background: var(--pz-surface-2, #f4f4f8); border: 0; border-radius: 99px; }
.pz-dcal-nav:disabled { opacity: .35; cursor: default; }
.pz-dcal-grid { display: grid; gap: 2px; }
.pz-dcal-row { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; }
.pz-dcal-wd { padding: 4px 0; font-size: 12px; font-weight: 600; text-align: center; color: var(--pz-muted, #6b6b78); }
.pz-dcal-day { min-width: 0; height: 44px; padding: 0; font: inherit; font-size: 15px; font-weight: 600; cursor: pointer;
              color: var(--pz-ink, #16161d); background: transparent; border: 0; border-radius: 10px;
              -webkit-tap-highlight-color: transparent; }
.pz-dcal-day:hover:not(:disabled) { background: var(--pz-accent-wash, #f4f3ff); }
.pz-dcal-day:disabled { cursor: default; color: #b7b7c2; text-decoration: line-through; font-weight: 400; }
.pz-dcal-day.is-out { visibility: hidden; }
.pz-dcal-day.is-today { box-shadow: inset 0 0 0 1.5px var(--pz-line-hover, #b9b9c6); }
.pz-dcal-day.is-picked { color: var(--pz-btn-text, #fff); background: var(--pz-accent); text-decoration: none; }
.pz-dcal-day:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 1px; }
/* The delivery promise, drawn as the ETA plugin draws its timeline
   (wc-delivery-eta-rs eta.css): a blue card, the headline on a white band,
   Poručeno (orange) → Poslato (blue) → Stiže (green) joined by a line; a
   picked date turns it green, amber or red like the plugin's wapf-* states. */
.pz-eta { display: grid; gap: 14px; padding: 16px 14px; border-radius: 12px; color: #0f172a;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #0ea5e9; border-left-width: 4px; box-shadow: 0 2px 8px rgba(14,165,233,.1); }
.pz-eta[hidden] { display: none; }
.pz-eta.is-ok { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border-color: #22c55e; }
.pz-eta.is-tight { background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border-color: #f59e0b; }
.pz-eta.is-no { background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%); border-color: #ef4444; }
.pz-eta-head { margin: 0; padding: 9px 12px; font-size: 15.5px; font-weight: 700; line-height: 1.3; text-align: center;
               background: rgba(255,255,255,.72); border-radius: 10px; }
.pz-eta.is-ok .pz-eta-head { color: #16a34a; }
.pz-eta.is-tight .pz-eta-head { color: #d97706; }
.pz-eta.is-no .pz-eta-head { color: #dc2626; }
.pz-eta-steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin: 0; padding: 0; list-style: none; }
.pz-eta-step { position: relative; display: grid; justify-items: center; align-content: start; gap: 4px; min-width: 0; padding: 0 2px; text-align: center; }
.pz-eta-step:not(:last-child)::after { content: ''; position: absolute; top: 21px; left: calc(50% + 26px); right: calc(-50% + 26px);
                                       height: 2px; background: #cbd5e1; }
.pz-eta-step.is-completed:not(:last-child)::after { background: #10b981; }
.pz-eta-step.is-active:not(:last-child)::after { background: #0ea5e9; }
.pz-eta-icon { display: grid; place-items: center; width: 44px; height: 44px; margin-bottom: 4px; border-radius: 50%;
               font-size: 19px; line-height: 1; color: #fff; background: #10b981; }
.pz-eta-step.is-completed .pz-eta-icon { background: #f59e0b; }
.pz-eta-step.is-active .pz-eta-icon { background: #0ea5e9; }
.pz-eta.is-tight .pz-eta-step:last-child .pz-eta-icon { background: #eab308; }
.pz-eta.is-no .pz-eta-step:last-child .pz-eta-icon { background: #ef4444; }
.pz-eta-day { font-size: 11.5px; font-weight: 600; line-height: 1.25; color: #64748b; text-transform: uppercase;
              letter-spacing: .4px; overflow-wrap: anywhere; }
.pz-eta-day del { color: #dc2626; }
.pz-eta-status { font-size: 13px; font-weight: 600; line-height: 1.2; color: #1e293b; }
.pz-eta-foot { margin: 0; font-size: 12px; font-style: italic; line-height: 1.35; text-align: center; color: #64748b; }
@media (min-width: 481px) {
  .pz-eta { padding: 18px; }
  .pz-eta-head { font-size: 16px; }
  .pz-eta-icon { width: 50px; height: 50px; font-size: 20px; }
  .pz-eta-step:not(:last-child)::after { top: 24px; left: calc(50% + 30px); right: calc(-50% + 30px); }
  .pz-eta-day { font-size: 12px; }
}
.pz-eta-take { justify-self: center; min-height: 44px; }
.pz-finish-qty { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.pz-finish-qty[hidden] { display: none; }
.pz-qty { display: inline-flex; align-items: center; border: 1.5px solid var(--pz-line-2, #d8d8e0); border-radius: 99px; overflow: hidden; }
.pz-qty-btn { width: 44px; height: 44px; padding: 0; font: inherit; font-size: 22px; line-height: 1; cursor: pointer;
              color: var(--pz-ink, #16161d); background: var(--pz-surface, #fff); border: 0; -webkit-tap-highlight-color: transparent; }
.pz-qty-btn:disabled { color: #c3c3cc; cursor: default; }
.pz-qty-val { min-width: 40px; font-size: 16px; font-weight: 700; text-align: center; }
/* The pieces deal (connector 0.12): its steps as one-tap picks over the stepper. */
.pz-finish-qtybox { display: grid; gap: 10px; }
.pz-deal-picks { display: grid; grid-template-columns: repeat(auto-fit, minmax(68px, 1fr)); gap: 8px; }
.pz-deal-pick { display: grid; justify-items: center; gap: 1px; min-height: 52px; padding: 6px 4px; font: inherit; cursor: pointer;
                color: var(--pz-ink, #16161d); background: var(--pz-surface, #fff); border: 1.5px solid var(--pz-line-2, #d8d8e0);
                border-radius: var(--pz-radius, 12px); -webkit-tap-highlight-color: transparent; }
.pz-deal-pick[aria-pressed="true"] { border-color: var(--pz-accent); background: var(--pz-accent-wash, #f4f3ff);
                                     box-shadow: inset 0 0 0 1px var(--pz-accent); }
.pz-deal-pick:disabled { opacity: .45; cursor: default; }
.pz-deal-n { font-size: 14.5px; font-weight: 700; }
.pz-deal-off { font-size: 12.5px; font-weight: 700; color: #16a34a; }
.pz-deal-line { margin: 0; font-size: 13px; line-height: 1.45; color: var(--pz-muted, #6b6b78); }
.pz-deal-line.is-on { color: #15803d; }
.pz-added-deal { margin: 0; padding: 8px 10px; font-size: 13.5px; font-weight: 600; line-height: 1.4; color: #15803d;
                 background: #f0fdf4; border-radius: 10px; }
.pz-pricebox-row.is-deal .pz-pricebox-value { color: #15803d; font-weight: 700; }
/* Several pieces: the same design or each its own, and the run's counter. */
.pz-piece-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pz-piece-choice[hidden], .pz-piece-note[hidden], .pz-piece-run[hidden], .pz-piece-banner[hidden] { display: none; }
.pz-piece-opt { min-height: 48px; padding: 8px 10px; font: inherit; font-size: 14px; font-weight: 650; line-height: 1.25; cursor: pointer;
                color: var(--pz-ink, #16161d); background: var(--pz-surface, #fff); border: 1.5px solid var(--pz-line-2, #d8d8e0);
                border-radius: var(--pz-radius, 12px); -webkit-tap-highlight-color: transparent; }
.pz-piece-opt[aria-pressed="true"] { border-color: var(--pz-accent); background: var(--pz-accent-wash, #f4f3ff);
                                     box-shadow: inset 0 0 0 1px var(--pz-accent); }
.pz-piece-note { margin: 0; font-size: 13px; line-height: 1.45; color: var(--pz-muted, #6b6b78); }
.pz-piece-run { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 2px 10px; padding: 10px 12px;
                border-radius: var(--pz-radius, 12px); background: var(--pz-accent-wash, #f4f3ff); border: 1.5px solid var(--pz-accent); }
.pz-piece-run-text { font-size: 15.5px; }
.pz-piece-run-note { grid-column: 1; font-size: 12.5px; color: var(--pz-muted, #6b6b78); }
.pz-piece-stop { grid-column: 2; grid-row: 1 / span 2; min-height: 44px; padding: 6px 12px; font: inherit; font-size: 14px; font-weight: 600;
                 color: var(--pz-muted, #6b6b78); background: none; border: 0; text-decoration: underline; cursor: pointer; }
.pz-piece-banner { margin: 0 0 10px; padding: 8px 12px; font-size: 14px; font-weight: 700; line-height: 1.35; text-align: center;
                   color: var(--pz-accent-ink, #3730a3); background: var(--pz-accent-wash, #f4f3ff); border-radius: 10px; }
.pz-finish-add, .pz-face-add { width: 100%; min-height: 52px; font-size: 16.5px; margin-top: 10px; }
.pz-finish-add.is-busy, .pz-face-add.is-busy, .pz-modal-next.is-busy { opacity: .7; cursor: progress; }
.pz-face-eta { margin: 10px 0 0; font-size: 14px; font-weight: 600; line-height: 1.4; color: var(--pz-ink, #16161d); }
.pz-modal-body.is-finish-step .pz-share { display: none; }

/* "Dodato u korpu" (cart-add.ts): a card on a wide screen, a bottom sheet on a
   phone (.pz-sheet below), above the popup it may replace. */
.pz-added.pz-layer { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483200;
                     display: grid; place-items: center; padding: 16px; }
.pz-added-card { position: relative; display: grid; gap: 14px; width: 420px; max-width: 100%;
                 padding: 18px 18px 16px; background: #fff; color: var(--pz-ink, #16161d);
                 border-radius: 18px; box-shadow: 0 18px 60px rgba(10,10,30,.35); }
.pz-added-head { display: flex; align-items: center; gap: 10px; padding-right: 36px; }
.pz-added-tick { display: grid; place-items: center; flex: none; width: 30px; height: 30px; border-radius: 99px;
                 color: #fff; background: var(--pz-success, #1f8a4c); }
.pz-added-title { font-size: 18px; font-weight: 750; line-height: 1.25; }
.pz-added-close { position: absolute; top: 12px; right: 12px; display: grid; place-items: center; width: 36px; height: 36px;
                  padding: 0; cursor: pointer; font-size: 14px; color: #45454f; background: #f0f0f4; border: 0; border-radius: 99px; }
.pz-added-close:hover { background: #e4e4ea; }
.pz-added-lines { display: grid; gap: 10px; }
.pz-added-line { display: flex; align-items: center; gap: 12px; min-width: 0; }
.pz-added-pic { flex: none; width: 64px; height: 64px; object-fit: contain; border-radius: 10px;
                background: var(--pz-surface-2, #f4f4f8); border: 1px solid var(--pz-line, #e6e6ec); }
.pz-added-text { display: grid; gap: 2px; min-width: 0; }
.pz-added-name { font-size: 15px; font-weight: 650; line-height: 1.3; overflow-wrap: anywhere; }
.pz-added-meta, .pz-added-summary { font-size: 13.5px; color: var(--pz-muted, #6b6b78); }
.pz-added-summary { margin: 0; padding-top: 10px; border-top: 1px solid var(--pz-line, #e6e6ec); }
.pz-added-actions { display: grid; gap: 8px; }
.pz-added-actions .pz-btn, .pz-added-actions .pz-btn-ghost { width: 100%; min-height: 50px; font-size: 16px; }
.pz-added-keep { justify-self: center; min-height: 44px; padding: 8px 16px; font: inherit; font-size: 14.5px; font-weight: 600;
                 color: var(--pz-muted, #6b6b78); background: none; border: 0; cursor: pointer; text-decoration: underline;
                 text-underline-offset: 3px; -webkit-tap-highlight-color: transparent; }
.pz-added-keep:hover { color: var(--pz-ink, #16161d); }
.pz-spotify-card { position: relative; display: grid; gap: 10px; width: 420px;
                   max-width: 100%; padding: 18px 16px 16px; background: #fff;
                   border-radius: 16px; box-shadow: 0 18px 60px rgba(10,10,30,.35); }
@media (max-width: 460px) { .pz-spotify-card { width: 100%; } }
.pz-spotify-close { position: absolute; top: 8px; right: 8px; display: grid; place-items: center;
                    width: 30px; height: 30px; padding: 0; cursor: pointer; font-size: 13px;
                    color: #45454f; background: #f0f0f4; border: 0; border-radius: 99px; }
.pz-spotify-close:hover { background: #e4e4ea; }
.pz-spotify-search { margin-top: 6px; }
.pz-spotify-status:empty { display: none; }

/* Five rows visible, the rest one scroll away — the same list depth Customily
   shows, so the dialog never grows past a phone screen. */
.pz-spotify-results { display: grid; gap: 6px; max-height: 320px; overflow-y: auto; }
.pz-spotify-results:empty { display: none; }
.pz-spotify-row, .pz-track { display: flex; align-items: center; gap: 10px; width: 100%;
            padding: 7px 9px; cursor: pointer; text-align: left; background: #fff;
            border: 1.5px solid #dcdce4; border-radius: 10px;
            transition: border-color .12s, background .12s; }
.pz-spotify-row:hover { border-color: #a9a9ba; background: #f7f7fa; }
.pz-spotify-row.is-active { border-color: #1db954; box-shadow: 0 0 0 3px rgba(29,185,84,.15); }
.pz-spotify-thumb { width: 50px; height: 50px; border-radius: 6px; object-fit: cover; flex-shrink: 0; }
.pz-spotify-meta { display: grid; line-height: 1.3; min-width: 0; flex: 1; }
.pz-spotify-name { font-size: 13.5px; font-weight: 700; min-width: 0;
                   overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-spotify-artist { color: #71717d; font-size: 12px; min-width: 0;
                     overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* "I checked the design": one calm line above the shop's cart button. */
.pz-proof { display: flex; align-items: flex-start; gap: 10px; margin: 12px 0 4px; padding: 10px 12px;
            border: 1.5px solid #e0e0e8; border-radius: 10px; background: #fafafc; cursor: pointer;
            font-size: 13.5px; line-height: 1.4; color: #2e2e38; }
.pz-proof input { width: 18px; height: 18px; margin: 1px 0 0; flex: none; accent-color: var(--pz-accent, #1db954); }
/* Letters the font cannot print: a warning, never a block. */
.pz-glyph-note { color: #9a5b00; }
.pz-spotify-album { color: #9a9aa6; font-size: 11.5px; min-width: 0;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-spotify-tags { display: flex; gap: 4px; flex-shrink: 0; align-self: center; }
.pz-spotify-tag { font-size: 10.5px; font-weight: 650; color: #5b5b68; background: #f0f0f4;
                  border-radius: 5px; padding: 2px 6px; white-space: nowrap; }
.pz-spotify-explicit { background: #6b6b76; color: #fff; }
.pz-spotify-heading { font-size: 12px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase;
                      color: #7a7a86; margin: 4px 2px 0; }

/* The picked song, in one short row: sleeve, words, play — and two quiet links
   to change or remove it. Album and year only where there is room. */
.pz-song-card { display: grid; grid-template-columns: 56px minmax(0, 1fr) auto; gap: 8px 12px;
                align-items: center; border: 1.5px solid #1db954; border-radius: 12px;
                padding: 9px 10px 9px 9px; background: #fff; }
.pz-song-card[hidden], .pz-song-note[hidden], .pz-spotify-field[hidden] { display: none; }
/* Explicit cells, so a missing cover or code never shifts the words into its column. */
.pz-song-card-cover { grid-column: 1; grid-row: 1; width: 56px; height: 56px; border-radius: 8px;
                      object-fit: cover; background: #ececf2; }
.pz-song-card-body { grid-column: 2; grid-row: 1; display: grid; gap: 1px; min-width: 0; }
.pz-song-card-title { font-size: 14.5px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-song-card-by { display: flex; align-items: center; gap: 5px; min-width: 0; }
.pz-song-card-artist { font-size: 13px; color: #55555f; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-song-card-meta { font-size: 12px; color: #8a8a96; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Spotify's logo IS the link back to Spotify; its words stay for a screen reader. */
.pz-song-card-spotify { position: relative; flex: none; width: 15px; height: 15px; font-size: 0; color: transparent;
  overflow: visible; background: no-repeat center/contain url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='12' fill='%231DB954'/%3E%3Cpath d='M6.5 9.3c3.8-1.1 8-.8 11.2 1M7.2 12.4c3.1-.9 6.5-.6 9.1.9M7.8 15.3c2.5-.7 5.1-.5 7.2.7' fill='none' stroke='%23fff' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E"); }
.pz-song-card-spotify::after { content: ''; position: absolute; left: 50%; top: 50%;
  width: 32px; height: 32px; transform: translate(-50%, -50%); }
.pz-song-card-spotify:focus-visible { outline: 2px solid #1db954; outline-offset: 2px; border-radius: 99px; }
/* Change / remove: small words, not two more buttons. */
.pz-song-card-actions { display: flex; gap: 16px; margin-top: 3px; }
.pz-song-card-actions [hidden] { display: none; }
.pz-song-act { position: relative; padding: 0; margin: 0; border: 0; background: none; cursor: pointer;
               font: inherit; font-size: 12.5px; font-weight: 650; line-height: 1.4;
               color: var(--pz-accent-ink, #3f3a9e); }
.pz-song-act.pz-song-remove { color: #7a7a86; font-weight: 600; }
.pz-song-act:hover { text-decoration: underline; }
.pz-song-act::after { content: ''; position: absolute; left: -6px; right: -6px; top: 50%;
                      height: 36px; transform: translateY(-50%); }
/* Listen: one round play button, Spotify green; a stop square while the player is open. */
.pz-song-listen { grid-column: 3; grid-row: 1; position: relative; display: grid; place-items: center;
                  width: 38px; height: 38px; padding: 0; border: 0; border-radius: 99px; cursor: pointer;
                  background: #1db954; color: transparent; font-size: 0; box-shadow: 0 2px 6px rgba(29,185,84,.3);
                  transition: background .12s, transform .12s; }
.pz-song-listen:hover { background: #1aa34a; }
.pz-song-listen:active { transform: scale(.94); }
.pz-song-listen:focus-visible { outline: 2px solid #1db954; outline-offset: 2px; }
/* The glyph is pinned to the middle: the button's words (kept for a screen
   reader) would otherwise take a row of their own and push it up. The triangle
   sits 1.5px right of true centre, where the eye puts a play sign's middle. */
.pz-song-listen::before { content: ''; position: absolute; left: 50%; top: 50%;
                          transform: translate(calc(-50% + 1.5px), -50%);
                          border-style: solid; border-width: 7px 0 7px 12px;
                          border-color: transparent transparent transparent #fff; }
.pz-song-listen[aria-expanded="true"] { background: #24242c; box-shadow: none; }
.pz-song-listen[aria-expanded="true"]::before { width: 11px; height: 11px; border: 0; border-radius: 2px;
                                                background: #fff; transform: translate(-50%, -50%); }
.pz-song-listen[hidden] { display: none; }
.pz-song-embed { grid-column: 1 / -1; }
.pz-song-embed[hidden] { display: none; }
.pz-song-embed-frame { display: block; width: 100%; height: 80px; border: 0; border-radius: 12px; }
/* A narrow song block: the row keeps what identifies the song and the lines
   below move up. The block's own width decides, not the screen's. */
@supports (container-type: inline-size) {
  @container pz-song (max-width: 460px) {
${SONG_CARD_NARROW}  }
}
@supports not (container-type: inline-size) {
  @media (max-width: 460px) {
${SONG_CARD_NARROW}  }
}


/* Ghost rows while Spotify answers: the list keeps its height instead of
   collapsing and shoving the rest of the form up and down on every keystroke. */
.pz-track--ghost { pointer-events: none; gap: 10px; }
.pz-ghost-art { width: 50px; height: 50px; border-radius: 6px; flex-shrink: 0; }
.pz-ghost-lines { display: grid; gap: 6px; flex: 1; min-width: 0; }
.pz-ghost-lines i { display: block; height: 9px; border-radius: 5px; }
.pz-ghost-lines i:first-child { width: 62%; }
.pz-ghost-lines i:last-child { width: 38%; height: 8px; }
.pz-ghost-art, .pz-ghost-lines i {
  background: linear-gradient(100deg, #eeeef3 30%, #f8f8fb 48%, #eeeef3 66%);
  background-size: 400% 100%; animation: pz-shimmer 1.15s ease-in-out infinite; }
@keyframes pz-shimmer { from { background-position: 130% 0; } to { background-position: -30% 0; } }

/* The photo card while its background is being removed, for the steps where
   the shopper is looking at the card rather than at the product. */
.pz-file-card.is-cutting img { filter: saturate(.45) brightness(1.04); }
.pz-file-card.is-cutting { position: relative; overflow: hidden; }
.pz-file-card.is-cutting::after {
  content: ''; position: absolute; top: 0; right: 0; bottom: 0; left: 0; pointer-events: none;
  background: linear-gradient(100deg, transparent 20%, var(--pz-accent-ring) 50%, transparent 80%);
  background-size: 220% 100%;
  animation: pz-card-shimmer 1.25s linear infinite;
}
@keyframes pz-card-shimmer { from { background-position: 120% 0; } to { background-position: -120% 0; } }
/* The background disappearing is what the shopper waited for, so it fades in
   rather than being swapped under them. */
.pz-file-card.is-revealing img { animation: pz-card-reveal .4s ease-out; }
@keyframes pz-card-reveal { from { opacity: .25; transform: scale(.97); } to { opacity: 1; transform: none; } }

/* The owner's rule: a photo is never flagged as bad on the card itself — same
   border whether or not it warned. Only the calm status line differs, and an
   all-good upload gets a small green check for its wording, nothing more. */
.pz-file-card.is-ok .pz-upload-status { color: #2f855a; }


@media (prefers-reduced-motion: reduce) {
  .pz-ghost-art, .pz-ghost-lines i { animation: none; }
}

/* Upload: a generous drop zone that reads as "tap to add a photo". */
.pz-drop { display: flex; align-items: center; justify-content: center; gap: 10px;
           width: 100%; min-height: 64px; padding: 16px; cursor: pointer; text-align: center;
           font-family: inherit; font-size: 14px; font-weight: 600; line-height: 1.3;
           color: var(--pz-ink-2); background: var(--pz-surface-2);
           border: 1.5px dashed var(--pz-line-hover); border-radius: var(--pz-radius);
           -webkit-tap-highlight-color: transparent;
           transition: border-color .15s, background-color .15s, color .15s; }
.pz-drop:hover, .pz-drop.is-over { border-color: var(--pz-accent); color: var(--pz-accent-ink); background: var(--pz-accent-soft); }
.pz-drop:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-drop svg { width: 22px; height: 22px; flex-shrink: 0; }
.pz-drop.is-busy { pointer-events: none; opacity: .6; }
/* The picker and "Slikaj" side by side on phones; the picker keeps the room. */
.pz-pick-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; }
.pz-pick-row[hidden], .pz-drop-camera[hidden] { display: none; }
.pz-pick-row:has(> .pz-drop[hidden]) { display: none; }
.pz-drop-camera { padding-left: 16px; padding-right: 16px; }
.pz-upload-retry { justify-self: start; }
.pz-upload-retry[hidden] { display: none; }
.pz-upload-status.is-notice { color: var(--pz-warn, #9a5b00); font-weight: 600; }
/* The in-page camera: the whole screen, the photo in the middle, the shutter
   under the thumb. */
/* The camera's layer: the whole screen, above whatever layer opened it. */
.pz-camera-layer.pz-layer { position: fixed; top: 0; right: 0; bottom: 0; left: 0; width: 100%; height: 100%; }
.pz-camera-layer[hidden] { display: none; }
.pz-camera { position: fixed; inset: 0; z-index: 2147483000; display: grid; grid-template-rows: 1fr auto;
             background: #0c0c10; color: #fff; }
.pz-camera-stage { position: relative; min-height: 0; display: grid; place-items: center; overflow: hidden; }
.pz-camera-video, .pz-camera-still { max-width: 100%; max-height: 100%; width: 100%; height: 100%; object-fit: contain; }
.pz-camera-video.is-mirrored { transform: scaleX(-1); }
.pz-camera-video[hidden], .pz-camera-still[hidden] { display: none; }
.pz-camera-note { position: absolute; left: 16px; right: 16px; bottom: 16px; margin: 0; text-align: center;
                  font-size: 15px; line-height: 1.4; text-shadow: 0 1px 3px rgba(0,0,0,.6); }
.pz-camera-note:empty { display: none; }
.pz-camera-bar { display: flex; align-items: center; justify-content: center; gap: 18px; flex-wrap: wrap;
                 padding: 16px 16px max(16px, env(safe-area-inset-bottom)); }
.pz-camera-bar [hidden] { display: none !important; }
.pz-camera-close, .pz-camera-flip { width: 48px; height: 48px; border-radius: 50%; border: 0; cursor: pointer;
                                    background: rgba(255,255,255,.14); color: #fff; font-size: 20px; }
.pz-camera-shutter { width: 74px; height: 74px; border-radius: 50%; border: 4px solid #fff; background: transparent;
                     padding: 5px; cursor: pointer; }
.pz-camera-shutter span { display: block; width: 100%; height: 100%; border-radius: 50%; background: #fff; }
.pz-camera-shutter:active span { transform: scale(.92); }
.pz-camera-use, .pz-camera-retake, .pz-camera-gallery { min-height: 48px; }

.pz-file-card { display: flex; align-items: center; gap: 14px; padding: 10px 10px 10px 10px;
                min-width: 0; background: var(--pz-surface); border: 1.5px solid var(--pz-line-2);
                border-radius: var(--pz-radius); box-shadow: var(--pz-shadow-sm); }
/* The picture in a frame of its own: big enough to recognise the face. */
.pz-file-thumb { flex: none; display: block; width: 68px; height: 68px; overflow: hidden;
                 border-radius: 12px; background: var(--pz-surface-2); box-shadow: inset 0 0 0 1px var(--pz-line-2); }
.pz-file-thumb img { display: block; width: 100%; height: 100%; object-fit: cover; }
/* A cut-out head sits whole on a soft chequer, the way it will print. */
.pz-file-thumb.is-cutout { background-color: #fff;
  background-image: conic-gradient(#eef0f4 25%, transparent 0 50%, #eef0f4 0 75%, transparent 0);
  background-size: 12px 12px; }
.pz-file-thumb.is-cutout img { box-sizing: border-box; object-fit: contain; padding: 5px; }
/* The date question: a field that reads 14.10.2025. and our own calendar under it. */
.pz-datepick { display: grid; gap: 8px; min-width: 0; }
.pz-date-field { display: flex; align-items: center; justify-content: space-between; gap: 10px; cursor: pointer;
                 text-align: left; font-variant-numeric: tabular-nums; }
.pz-date-field.is-empty span:first-child { color: var(--pz-muted); }
.pz-date-icon { display: grid; color: var(--pz-accent-ink); }
.pz-date-icon svg { width: 20px; height: 20px; }
.pz-cal { max-width: 360px; padding: 12px; background: var(--pz-surface); border: 1.5px solid var(--pz-line-2);
          border-radius: var(--pz-radius); box-shadow: var(--pz-shadow-sm); }
.pz-cal[hidden] { display: none; }
.pz-cal-head { display: grid; grid-template-columns: 36px minmax(0, 1.4fr) minmax(0, 1fr) 36px; gap: 6px; align-items: center;
               margin-bottom: 8px; }
.pz-cal-nav { display: grid; place-items: center; width: 36px; height: 36px; padding: 0; border: 0; border-radius: 50%;
              background: var(--pz-surface-2); color: var(--pz-ink-2); cursor: pointer; }
.pz-cal-nav svg { width: 18px; height: 18px; }
.pz-cal-nav:hover { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-cal-select { min-width: 0; height: 36px; padding: 0 8px; font: inherit; font-size: 15px; font-weight: 600; color: var(--pz-ink);
                 background: transparent; border: 0; border-radius: 8px; cursor: pointer; text-align: center; text-align-last: center; }
.pz-cal-select:hover { background: var(--pz-surface-2); }
.pz-cal-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; }
.pz-cal-dow { padding: 4px 0; text-align: center; font-size: 12px; font-weight: 700; color: var(--pz-muted); }
.pz-cal-dow:nth-child(7) { color: #c53030; }
.pz-cal-day { aspect-ratio: 1; min-height: 36px; padding: 0; border: 0; border-radius: 50%; background: none; cursor: pointer;
              font: inherit; font-size: 14px; color: var(--pz-ink); font-variant-numeric: tabular-nums; }
.pz-cal-day:hover { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-cal-day.is-today { box-shadow: inset 0 0 0 1.5px var(--pz-accent-line, var(--pz-accent)); }
.pz-cal-day.is-chosen { background: var(--pz-accent); color: var(--pz-btn-text); font-weight: 700; }
.pz-cal-day:focus-visible, .pz-cal-nav:focus-visible, .pz-cal-select:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 1px; }
.pz-file-info { flex: 1; min-width: 0; display: grid; gap: 5px; justify-items: start; }
.pz-file-replace { display: inline-flex; align-items: center; gap: 5px; padding: 2px 0; border: 0; background: none;
                   font: inherit; font-size: 13px; font-weight: 600; color: var(--pz-accent-ink); cursor: pointer; }
.pz-file-replace svg { width: 15px; height: 15px; }
.pz-file-replace:hover span { text-decoration: underline; }
.pz-file-replace:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; border-radius: 4px; }
/* The bug: flex:1 alone never lets a flex item shrink below its own content —
   a long file name pushed the card, the row and the page wider than the
   viewport. min-width:0 is what actually lets the ellipsis do its job.
   Wraps rather than nowrap+ellipsis: a nowrap file name keeps its full width as
   the card's min-content size in every engine, which is what pushed the whole
   widget sideways under a theme's flex cart row. Two lines, then an ellipsis. */
.pz-file-card .name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; color: var(--pz-ink); line-height: 1.3; overflow: hidden;
                      overflow-wrap: anywhere; word-break: break-word; display: -webkit-box;
                      -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.pz-file-card .x { display: grid; place-items: center; width: 34px; height: 34px; flex-shrink: 0; cursor: pointer; padding: 0;
                   color: var(--pz-ink-2); background: var(--pz-surface-2); border: 0; border-radius: 50%; font-size: 15px;
                   transition: background-color .15s, color .15s; }
.pz-file-card .x svg { width: 16px; height: 16px; }
.pz-file-card .x:hover { background: #fdecec; color: #c53030; }
.pz-file-card .x:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }


/* ── Sound checklist: one card per way of providing sound ────────────────── */
.pz-audio { display: grid; grid-template-columns: minmax(0,1fr); gap: 10px; min-width: 0; max-width: 100%; }
.pz-src-list { display: grid; gap: 9px; }
.pz-src-card { border: 1.5px solid #e0e0e8; border-radius: 14px; background: #fff;
               overflow: hidden; transition: border-color .15s, box-shadow .15s; }
.pz-src-card:has(.pz-src-head.is-active) { border-color: var(--pz-accent); box-shadow: 0 3px 14px var(--pz-accent-ring); }
.pz-src-head { display: flex; align-items: center; gap: 11px; width: 100%; padding: 12px 13px;
               cursor: pointer; text-align: left; background: transparent; border: 0;
               transition: background .12s; }
.pz-src-head:hover { background: #f8f8fc; }
.pz-src-head.is-active { background: var(--pz-accent-soft); }
.pz-src-check { display: grid; place-items: center; width: 22px; height: 22px; flex-shrink: 0;
                border: 2px solid #c9c9d6; border-radius: 7px; background: #fff;
                font-size: 13px; color: transparent; transition: all .15s; }
.pz-src-check::after { content: "✓"; font-weight: 800; line-height: 1; }
.pz-src-head.is-active .pz-src-check { border-color: var(--pz-accent); background: var(--pz-accent); color: #fff; }
.pz-src-check.is-done { border-color: #35a264 !important; background: #35a264 !important; color: #fff; }
.pz-src-icon { display: grid; place-items: center; flex-shrink: 0; width: 34px; height: 34px; border-radius: 10px;
               background: #f1f1f6; color: var(--pz-accent-ink); }
.pz-src-icon svg { width: 18px; height: 18px; }
.pz-src-head.is-active .pz-src-icon { background: var(--pz-accent-soft); }
.pz-file-icon svg { width: 18px; height: 18px; vertical-align: -4px; }
.pz-src-copy { display: grid; gap: 1px; min-width: 0; }
.pz-src-copy strong { font-size: 14px; letter-spacing: .01em; }
.pz-src-copy span { font-size: 12px; color: #71717d; }
.pz-src-body { display: grid; grid-template-columns: minmax(0,1fr); gap: 10px;
               padding: 4px 13px 14px; border-top: 1px dashed #e8e8ef; padding-top: 12px; }
.pz-src-body[hidden] { display: none; }

/* A sound carried over from an earlier commit (edit-from-cart). */
.pz-restored { display: flex; align-items: center; gap: 10px; padding: 9px 11px;
               border: 1px solid #c8e8d4; border-radius: 10px; background: #f3fbf6;
               color: #27613d; font-size: 12.5px; }
.pz-restored span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis;
                    white-space: nowrap; }
.pz-restored .pz-btn-ghost.small { padding: 5px 10px; font-size: 12px; flex-shrink: 0; }

/* ── The sound step: "Tvoj zvuk" (the arrangement), tiles, one editor ───── */
.pz-mix[hidden], .pz-snd-add[hidden], .pz-snd-editor[hidden], .pz-snd-editor-body[hidden],
.pz-tile-badge[hidden], .pz-mix-move[hidden], .pz-kept[hidden], .pz-player[hidden],
.pz-playback-row[hidden], .pz-yt-alt[hidden], .pz-mix-note[hidden],
.pz-snd-offer[hidden], .pz-snd-offer-again[hidden] { display: none !important; }
/* "Tvoja pesma": the Spotify song, offered as the sound step's first screen. */
.pz-snd-offer { display: grid; gap: 11px; padding: 14px; border: 1.5px solid var(--pz-accent);
                border-radius: 16px; background: var(--pz-surface); box-shadow: 0 3px 14px var(--pz-accent-ring); min-width: 0; }
.pz-snd-offer-song { display: flex; align-items: center; gap: 12px; min-width: 0; }
.pz-snd-offer-cover { width: 56px; height: 56px; flex-shrink: 0; border-radius: 8px; object-fit: cover;
                      background: var(--pz-surface-3); }
.pz-snd-offer-words { display: grid; gap: 2px; min-width: 0; }
.pz-snd-offer-words strong { font-size: 15px; line-height: 1.3; color: var(--pz-ink); overflow-wrap: anywhere; }
.pz-snd-offer-words span { font-size: 12.5px; color: var(--pz-muted); font-variant-numeric: tabular-nums; }
.pz-snd-offer-question { margin: 0; font-size: 14px; font-weight: 650; color: var(--pz-ink); }
.pz-snd-offer-note { margin: 0; color: #8a5a0b; font-weight: 600; }
.pz-snd-offer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.pz-snd-offer-yes { flex: 1 1 200px; }
.pz-snd-offer-no { flex: 1 1 160px; }
.pz-snd-offer .pz-yt-results { margin-top: 0; }
.pz-snd-offer-again { justify-self: start; }
.pz-yt-result-text .pz-snd-offer-same { white-space: normal; color: #2f7a4d; font-weight: 600; }
.pz-mix { display: grid; gap: 10px; padding: 13px 14px; border: 1.5px solid var(--pz-accent-line);
          border-radius: 16px; background: var(--pz-surface); box-shadow: 0 2px 12px rgba(16,16,40,.05); }
.pz-mix.is-over { border-color: #f0b4b4; background: #fffafa; }
.pz-mix-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.pz-mix-head strong { font-size: 15px; color: var(--pz-ink); }
.pz-mix-sum { font-size: 12.5px; color: var(--pz-muted); font-variant-numeric: tabular-nums; text-align: right; }
.pz-mix-meter { display: grid; gap: 5px; }
.pz-mix-bar { position: relative; height: 10px; border-radius: 99px; background: var(--pz-surface-3); overflow: hidden; }
.pz-mix-fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 99px; background: var(--pz-accent);
               transition: width .25s ease; z-index: 1; }
.pz-mix.is-premium .pz-mix-fill { background: linear-gradient(90deg, var(--pz-accent), #d98a00); }
.pz-mix.is-over .pz-mix-fill { background: #c53030; }
.pz-mix-zone { position: absolute; top: 0; bottom: 0; right: 0;
               background: repeating-linear-gradient(135deg, #fbe7c2 0 5px, #fff4de 5px 10px); }
.pz-mix-mark { position: absolute; top: -2px; bottom: -2px; width: 2px; margin-left: -1px; background: #b7791f; z-index: 2; }
.pz-mix-scale { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap;
                font-size: 11.5px; color: var(--pz-muted); }
.pz-mix-scale-premium { color: #975a16; font-weight: 600; }
.pz-mix-note { margin: 0; font-size: 12.5px; line-height: 1.45; color: var(--pz-muted); }
.pz-mix-note.is-premium { color: #8a5a0b; font-weight: 600; }
.pz-mix-note.is-over { padding: 8px 10px; border-radius: 10px; background: #fdecec; color: #b3261e; font-weight: 600; }
.pz-mix-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 7px; }
.pz-mix-row { display: flex; align-items: center; gap: 8px; min-width: 0; padding: 8px 8px 8px 6px;
              background: var(--pz-surface-2); border: 1px solid var(--pz-line); border-radius: 12px;
              transition: border-color .15s, background .15s; }
.pz-mix-row.is-playing { border-color: var(--pz-accent); background: var(--pz-accent-soft); box-shadow: 0 0 0 3px var(--pz-accent-ring); }
.pz-mix-kind { display: inline-grid; place-items: center; color: var(--pz-accent-ink); }
.pz-mix-kind svg { width: 14px; height: 14px; }
.pz-mix-row .pz-icon-btn { width: 34px; height: 34px; }
.pz-mix-move { display: grid; gap: 2px; flex-shrink: 0; }
.pz-mix-move .pz-icon-btn { width: 28px; height: 22px; border-radius: 7px; }
.pz-mix-move .pz-icon-btn svg { width: 15px; height: 15px; }
.pz-mix-text { display: grid; gap: 2px; flex: 1; min-width: 0; }
.pz-mix-text strong { font-size: 13.5px; font-weight: 650; line-height: 1.3; color: var(--pz-ink); min-width: 0;
                      overflow: hidden; overflow-wrap: anywhere; display: -webkit-box;
                      -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.pz-mix-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 12px; color: var(--pz-muted);
               font-variant-numeric: tabular-nums; }
.pz-mix-gap { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; margin-left: 4px; }
.pz-mix-gap-select { font: inherit; font-size: 12px; color: var(--pz-ink-2); padding: 2px 4px; min-height: 26px;
                     border: 1px solid var(--pz-line-2); border-radius: 7px; background: var(--pz-surface); }
.pz-icon-btn { display: grid; place-items: center; width: 36px; height: 36px; flex-shrink: 0; padding: 0; cursor: pointer;
               color: var(--pz-ink-2); background: var(--pz-surface); border: 1px solid var(--pz-line-2); border-radius: 10px;
               -webkit-tap-highlight-color: transparent; transition: background .12s, color .12s, border-color .12s; }
.pz-icon-btn svg { width: 17px; height: 17px; }
.pz-icon-btn:hover { background: var(--pz-surface-2); color: var(--pz-ink); }
.pz-icon-btn:disabled { opacity: .35; cursor: default; }
.pz-icon-btn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-mix-remove:hover { background: #fdecec; color: #c53030; border-color: #f0b4b4; }
.pz-mix-actions { display: flex; gap: 8px; }
.pz-mix-play { flex: 1; }
.pz-mix-play svg { width: 18px; height: 18px; }
/* While an editor is open the arrangement shrinks to its meter. */
.pz-audio.is-editing .pz-mix-list, .pz-audio.is-editing .pz-mix-actions,
.pz-audio.is-editing .pz-snd-add { display: none; }
.pz-audio.is-editing .pz-mix { padding: 10px 12px; gap: 7px; box-shadow: none; }

.pz-snd-add { display: grid; gap: 8px; }
.pz-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 230px), 1fr)); gap: 8px; }
.pz-tile { position: relative; display: flex; align-items: center; gap: 11px; min-width: 0; min-height: 56px;
           padding: 9px 12px; text-align: left; cursor: pointer; font-family: inherit;
           color: var(--pz-ink); background: var(--pz-surface); border: 1.5px solid var(--pz-line-2); border-radius: 14px;
           -webkit-tap-highlight-color: transparent; transition: border-color .15s, background .15s, box-shadow .15s; }
.pz-tile:hover { border-color: var(--pz-accent-line); background: var(--pz-accent-soft); }
.pz-tile:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-tile.is-added { border-color: #9fd8b5; background: #f4fbf7; }
.pz-tile-copy { display: grid; gap: 1px; min-width: 0; }
.pz-tile-copy strong { font-size: 13.5px; line-height: 1.25; font-weight: 650; }
.pz-tile-copy span { font-size: 11.5px; line-height: 1.3; color: var(--pz-muted); }
.pz-tile-badge { position: absolute; top: -8px; right: 8px; display: inline-flex; align-items: center; gap: 3px;
                 padding: 2px 7px 2px 5px; font-size: 11px; font-weight: 700; color: #fff; background: #2f9e5b; border-radius: 99px; }
.pz-tile-badge svg { width: 11px; height: 11px; }

.pz-snd-editor { display: grid; gap: 12px; padding: 12px 13px 14px; border: 1.5px solid var(--pz-accent);
                 border-radius: 16px; background: var(--pz-surface); box-shadow: 0 3px 14px var(--pz-accent-ring); min-width: 0; }
.pz-snd-editor-head { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pz-snd-editor-head strong { font-size: 15px; min-width: 0; }
.pz-snd-back { display: inline-flex; align-items: center; gap: 2px; padding: 6px 10px 6px 4px; min-height: 36px;
               font: inherit; font-size: 13px; font-weight: 600; color: var(--pz-accent-ink); cursor: pointer;
               background: var(--pz-accent-soft); border: 0; border-radius: 99px; flex-shrink: 0; }
.pz-snd-back svg { width: 18px; height: 18px; }
.pz-snd-back[hidden] { display: none; }
.pz-snd-editor-bodies, .pz-snd-editor-body { display: grid; grid-template-columns: minmax(0,1fr); gap: 10px; min-width: 0; }
.pz-snd-editor-foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
                      padding-top: 10px; border-top: 1px dashed var(--pz-line); }
.pz-snd-editor-foot[hidden] { display: none; }
.pz-snd-done svg { width: 18px; height: 18px; }
.pz-snd-editor-foot .pz-hint { flex: 1 1 180px; margin: 0; }
.pz-snd-editor-foot .pz-hint[hidden] { display: none; }
.pz-kept { display: grid; gap: 6px; padding: 10px; border-radius: 12px; background: #f3fbf6; border: 1px solid #c8e8d4; }
.pz-yt-alt { display: grid; gap: 6px; }

/* A sound's own player: play button, seek bar, time. */
.pz-player { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; padding: 6px 12px 6px 6px;
             border-radius: 99px; background: var(--pz-surface-2); border: 1px solid var(--pz-line); }
.pz-player-btn { display: grid; place-items: center; width: 40px; height: 40px; flex-shrink: 0; padding: 0; cursor: pointer;
                 color: #fff; background: var(--pz-accent); border: 0; border-radius: 50%;
                 -webkit-tap-highlight-color: transparent; }
.pz-player-btn svg { width: 19px; height: 19px; }
.pz-player-mid { display: grid; gap: 1px; flex: 1; min-width: 0; }
.pz-player-seek { width: 100%; margin: 0; accent-color: var(--pz-accent); height: 18px; }
.pz-player-time { font-size: 11.5px; color: var(--pz-muted); font-variant-numeric: tabular-nums; }
.pz-player.is-broken .pz-player-btn { background: var(--pz-line-2); cursor: default; }
.pz-cut-btn svg { width: 15px; height: 15px; }

/* Choice chips (song style, voice gender, song part) */
.pz-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.pz-chips[hidden] { display: none; }
.pz-chip { padding: 8px 13px; font-size: 13px; font-weight: 550; cursor: pointer;
           color: #45454f; background: #fff; border: 1.5px solid #d8d8e0; border-radius: 99px;
           transition: all .12s; }
.pz-chip:hover { border-color: var(--pz-accent-line); color: var(--pz-accent-ink); }
.pz-chip.is-active { border-color: var(--pz-accent); background: var(--pz-accent-soft); color: var(--pz-accent-ink);
                     box-shadow: inset 0 0 0 1px var(--pz-accent); }
.pz-sub-label { margin: 2px 0 0; font-size: 13px; font-weight: 650; color: #3c3c46; }
.pz-row-inline { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.pz-row-inline[hidden] { display: none; }

/* AI song */
.pz-song-form { display: grid; gap: 9px; }
.pz-song-lyrics-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.pz-song-generate { justify-self: start; background: var(--pz-accent); }
.pz-song-generate:hover { background-color: var(--pz-accent); }
.pz-song-results { display: grid; gap: 9px; }
.pz-song-results:empty { display: none; }
.pz-song-version { display: grid; gap: 8px; padding: 12px 13px; border: 1.5px solid #e0e0e8;
                   border-radius: 12px; background: #fafafc; }
.pz-song-version.is-selected { border-color: var(--pz-accent); background: var(--pz-accent-soft);
                               box-shadow: 0 0 0 3px var(--pz-accent-ring); }
.pz-song-version strong { font-size: 13.5px; min-width: 0; overflow-wrap: anywhere; }
.pz-song-select { justify-self: start; padding: 9px 15px; font-size: 13.5px; }
.pz-song-version.is-selected .pz-song-select { background: #35a264; }
.pz-song-status:empty { display: none; }

/* Voice recording */
.pz-rec-stage { display: flex; align-items: center; gap: 14px; padding: 6px 2px; }
.pz-rec-btn { display: grid; place-items: center; width: 62px; height: 62px; flex-shrink: 0;
              cursor: pointer; color: #fff; background: var(--pz-accent); border: 0; border-radius: 99px;
              box-shadow: 0 4px 14px var(--pz-accent-line); transition: background .15s, transform .1s; }
.pz-rec-btn:hover { background-image: linear-gradient(rgba(0,0,0,.09), rgba(0,0,0,.09)); transform: scale(1.04); }
.pz-rec-btn svg { width: 26px; height: 26px; }
.pz-rec-stage.is-recording .pz-rec-btn { background: #c0392b; animation: pz-pulse 1.4s ease-in-out infinite; }
.pz-rec-stage.is-opening .pz-rec-btn { opacity: .6; cursor: progress; transform: none; }
.pz-rec-delete svg { width: 15px; height: 15px; }
.pz-snd-add-another { justify-self: start; }
.pz-rec-readout { display: grid; gap: 6px; min-width: 0; flex: 1; }
.pz-rec-timer { font-size: 21px; font-weight: 700; font-variant-numeric: tabular-nums; color: #2b2b33; }
.pz-rec-level { display: flex; align-items: center; gap: 3px; height: 26px; max-width: 180px; }
.pz-rec-level span { flex: 1; height: 100%; border-radius: 3px; background: var(--pz-line-2);
                     transform: scaleY(.12); transform-origin: center; transition: transform .06s linear; }
.pz-rec-stage.is-recording .pz-rec-level span { background: #c0392b; }
.pz-rec-status.is-error { padding: 8px 10px; border-radius: 10px; background: #fdecec; color: #b3261e; font-weight: 600; }
.pz-rec-status .pz-btn-ghost.small { margin-left: 8px; }
.pz-rec-mic { display: flex; align-items: flex-end; gap: 8px; flex-wrap: wrap; }
.pz-rec-mic[hidden], .pz-rec-mic-pick[hidden] { display: none; }
.pz-rec-mic-pick { display: grid; gap: 4px; flex: 1 1 200px; min-width: 0; font-size: 12.5px; font-weight: 600; color: var(--pz-ink-2); }
.pz-rec-mic-pick .pz-input { min-height: 40px; padding: 8px 10px; font-size: 13.5px; }
.pz-rec-mic .pz-btn-ghost.small { min-height: 40px; }
.pz-rec-stage.is-checking .pz-rec-level span { background: #2f9e5b; }
.pz-rec-result { display: grid; gap: 8px; }
.pz-rec-result:empty { display: none; }

/* Uploaded sound files */
.pz-upload-list { display: grid; gap: 8px; }
.pz-upload-list:empty { display: none; }
.pz-upload-item { display: grid; gap: 8px; }
.pz-audio-file .pz-file-icon { font-size: 19px; }
.pz-audio-file.is-busy { opacity: .55; pointer-events: none; }
.pz-audio-file.is-error { border-color: #e5b5b5; background: #fff7f7; }
.pz-audio-file .pz-btn-ghost.small { padding: 6px 11px; font-size: 12px; white-space: nowrap; }

/* Photo framing adjuster */
.pz-frame { position: relative; width: 100%; max-height: 260px; overflow: hidden;
            border: 1.5px solid #dcdce4; border-radius: 12px; cursor: grab; touch-action: none;
            background: repeating-conic-gradient(#f0f0f3 0 25%, #fafafb 0 50%) 50%/16px 16px; }
.pz-frame:active { cursor: grabbing; }
.pz-frame canvas { display: block; width: 100%; height: 100%; }

/* YouTube preparation: real worker progress, not an indeterminate spinner. */
.pz-prep-status { display: grid; gap: 8px; padding: 12px 13px; border: 1px solid var(--pz-accent-line);
                  border-radius: 12px; background: var(--pz-accent-soft); color: var(--pz-ink-2); }
.pz-prep-status[hidden] { display: none; }
.pz-prep-status-top { display: flex; justify-content: space-between; gap: 12px;
                      font-size: 13px; font-variant-numeric: tabular-nums; }
.pz-prep-status-top strong { font-weight: 650; min-width: 0; overflow-wrap: anywhere; }
.pz-prep-status-top span { flex-shrink: 0; }
.pz-prep-track { height: 8px; overflow: hidden; border-radius: 99px; background: var(--pz-accent-soft); }
.pz-prep-fill { width: 0; height: 100%; border-radius: inherit;
                background: var(--pz-accent); transition: width .35s ease; }
.pz-prep-note { font-size: 11.5px; line-height: 1.4; color: var(--pz-muted); }
.pz-prep-status.is-done { border-color: #c8e8d4; background: #f3fbf6; color: #27613d; }
.pz-prep-status.is-done .pz-prep-track { background: #dcefe3; }
.pz-prep-status.is-done .pz-prep-fill { background: #35a264; }
.pz-prep-status.is-error { border-color: #f0caca; background: #fff7f7; color: #9a3333; }
.pz-prep-status.is-error .pz-prep-fill { background: #d95b5b; }

/* Song cutter — mp3cut-style and made for a thumb: the part's length and the
   zoom over the wave; the wave with finger-wide handles; play and "Preslušaj
   deo"; the two ends with their steps and "Sada"; quick lengths; effects. */
.pz-cutter { display: grid; grid-template-columns: minmax(0,1fr); gap: 10px; min-width: 0; margin-top: 4px; padding: 10px;
             border: 1.5px solid #dedee8; border-radius: 13px; background: #fff;
             box-shadow: 0 4px 16px rgba(36,31,80,.05); container-type: inline-size; }
/* Inside a sound editor, which is a card already: no second box around it. */
.pz-snd-editor .pz-cutter { margin-top: 0; padding: 0; border: 0; border-radius: 0; background: none; box-shadow: none; }
.pz-cut-lane { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 28px; }
.pz-cut-picked { min-width: 0; font-size: 12.5px; font-weight: 650; color: var(--pz-accent-ink);
                 font-variant-numeric: tabular-nums; }
.pz-wave-zoom { display: flex; gap: 4px; flex-shrink: 0; }
.pz-wave-zoom-btn { display: grid; place-items: center; width: 28px; height: 28px; padding: 0;
                    cursor: pointer; font-size: 16px; line-height: 1; color: var(--pz-accent-ink);
                    background: var(--pz-surface); border: 1px solid var(--pz-accent-line);
                    border-radius: 8px; -webkit-tap-highlight-color: transparent; }
.pz-wave-zoom-btn:hover { border-color: var(--pz-accent); }
.pz-wave-zoom-btn:disabled { opacity: .4; cursor: default; }
.pz-wave-wrap { position: relative; }
.pz-wave-scroll { overflow-x: auto; overflow-y: hidden; border: 1px solid #d8d6e7;
                  border-radius: 10px; background: linear-gradient(#fafafe, #f4f3fa);
                  overscroll-behavior-x: contain; }
.pz-wave-scroll::-webkit-scrollbar { height: 8px; }
.pz-wave-scroll::-webkit-scrollbar-thumb { background: #cfcfda; border-radius: 6px; }
/* A vertical swipe over the wave still scrolls the page; the handles and the
   part take horizontal drags themselves. */
.pz-wave { position: relative; height: 84px; min-width: 100%; overflow: hidden; touch-action: pan-x pan-y; }
.pz-wave canvas { position: absolute; top: 0; right: 0; bottom: 0; left: 0; width: 100%; height: 100%; touch-action: pan-x pan-y; }
.pz-wave.is-arriving canvas { animation: pz-wave-in .5s ease-out; }
@keyframes pz-wave-in { from { opacity: .2; } to { opacity: 1; } }
.pz-wave .shade { position: absolute; top: 0; bottom: 0; background: rgba(235,235,242,.82); pointer-events: none; }
.pz-wave .region { position: absolute; top: 0; bottom: 0; cursor: grab; touch-action: pan-y;
                   background: var(--pz-accent-wash); border-top: 2px solid var(--pz-accent-line);
                   border-bottom: 2px solid var(--pz-accent-line); }
.pz-wave .region:active { cursor: grabbing; }
/* A handle's grip is a thumb wide and lies mostly OUTSIDE the part, so two
   handles side by side can both still be taken. */
.pz-wave .grip { position: absolute; top: 0; bottom: 0; width: 36px; display: flex; align-items: center;
                 cursor: ew-resize; touch-action: none; z-index: 2; -webkit-tap-highlight-color: transparent; }
.pz-wave .grip-start { justify-content: flex-end; }
.pz-wave .grip-end { justify-content: flex-start; }
.pz-wave .grip::after { content: ""; width: 10px; height: 62%; margin: 0 3px; border: 2px solid #fff;
                        border-radius: 6px; background: var(--pz-accent); box-shadow: 0 1px 5px rgba(30,25,80,.28);
                        transition: transform .12s; }
.pz-wave .grip.is-dragging::after { transform: scaleY(1.14); }
.pz-wave .bubble { position: absolute; top: 4px; z-index: 4; padding: 1px 6px; border-radius: 6px; pointer-events: none;
                   background: var(--pz-accent-ink); color: #fff; font-size: 10px; font-weight: 650;
                   font-variant-numeric: tabular-nums; white-space: nowrap; box-shadow: 0 2px 7px rgba(36,28,120,.2); }
.pz-wave .playhead { position: absolute; top: 0; bottom: 0; width: 2px; z-index: 3;
                     background: #e0506e; pointer-events: none; box-shadow: 0 0 0 1px rgba(255,255,255,.6); }
/* The real wave on its way: a soft shimmer on the stand-in wave, no words on it. */
.pz-wave-veil { position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: 1; pointer-events: none;
                background: linear-gradient(100deg, rgba(255,255,255,0) 30%, rgba(255,255,255,.6) 48%, rgba(255,255,255,0) 66%);
                background-size: 300% 100%; animation: pz-shimmer 2.2s ease-in-out infinite; }
.pz-wave-veil[hidden] { display: none; }
/* …and one quiet line under it: a calm sentence and a thin bar, never a counting percentage. */
.pz-cut-coming { display: grid; gap: 5px; margin: 0; font-size: 12px; line-height: 1.4; color: var(--pz-muted); }
.pz-cut-coming[hidden] { display: none; }
.pz-cut-coming-bar { display: block; height: 3px; overflow: hidden; border-radius: 99px; background: var(--pz-accent-soft); }
.pz-cut-coming-bar[hidden] { display: none; }
.pz-cut-coming-bar i { display: block; width: 0; height: 100%; border-radius: inherit; background: var(--pz-accent-line);
                       transition: width .6s ease; }
/* Play, where the song is, "Preslušaj deo". */
.pz-cut-transport { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pz-play-selection { position: relative; display: grid; place-items: center; width: 44px; height: 44px; flex-shrink: 0;
                     padding: 0; cursor: pointer; color: var(--pz-btn-text, #fff); background: var(--pz-accent); border: 0;
                     border-radius: 99px; box-shadow: 0 2px 8px var(--pz-accent-ring);
                     -webkit-tap-highlight-color: transparent; transition: background .12s, opacity .12s; }
.pz-play-selection svg { width: 20px; height: 20px; }
.pz-play-selection:hover { background-image: linear-gradient(rgba(0,0,0,.09), rgba(0,0,0,.09)); }
.pz-play-selection:disabled { opacity: .45; cursor: default; box-shadow: none; }
.pz-play-selection:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
/* Nothing to play yet, but it is coming: a spinner in the button. */
.pz-play-selection.is-waiting { opacity: 1; }
.pz-play-selection.is-waiting svg { opacity: 0; }
.pz-play-selection.is-waiting::after { content: ""; position: absolute; top: 0; right: 0; bottom: 0; left: 0; margin: auto;
  width: 20px; height: 20px; border-radius: 50%; border: 2.5px solid rgba(255,255,255,.45); border-top-color: #fff;
  animation: pz-spin .8s linear infinite; }
.pz-cut-clock { font-size: 13px; font-weight: 600; color: var(--pz-ink-2); font-variant-numeric: tabular-nums; white-space: nowrap; }
.pz-btn-ghost.pz-cut-preview { margin-left: auto; gap: 6px; padding: 8px 12px; font-size: 13px; white-space: nowrap; }
.pz-cut-preview svg { width: 16px; height: 16px; flex-shrink: 0; }
.pz-cut-preview[hidden] { display: none; }
/* The two ends: the time, its steps, "Sada". Side by side where they fit. */
.pz-cut-ends { display: grid; grid-template-columns: repeat(auto-fit, minmax(146px, 1fr)); gap: 8px; }
.pz-cut-end { display: grid; gap: 6px; min-width: 0; padding: 8px; border: 1px solid var(--pz-line);
              border-radius: 12px; background: var(--pz-surface-2); }
.pz-cut-end-label { font-size: 12px; font-weight: 650; color: var(--pz-muted); }
.pz-cut-stepper { display: grid; grid-template-columns: 38px minmax(0, 1fr) 38px; gap: 4px; }
.pz-cut-nudge { display: grid; place-items: center; min-height: 40px; padding: 0; cursor: pointer;
                font: inherit; font-size: 20px; font-weight: 600; line-height: 1; color: var(--pz-accent-ink);
                background: var(--pz-surface); border: 1px solid var(--pz-line-2); border-radius: 10px;
                -webkit-tap-highlight-color: transparent; touch-action: manipulation;
                -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; }
.pz-cut-nudge:active:not(:disabled) { background: var(--pz-accent-soft); border-color: var(--pz-accent-line); }
.pz-cut-nudge:disabled { opacity: .35; cursor: default; }
.pz-cut-time { width: 100%; min-width: 0; padding: 7px 2px; text-align: center; font: inherit;
               font-size: 14px; font-weight: 650; font-variant-numeric: tabular-nums;
               color: var(--pz-ink); background: var(--pz-surface); border: 1px solid var(--pz-line-2);
               border-radius: 10px; }
.pz-cut-time:focus-visible { outline: none; border-color: var(--pz-accent); box-shadow: 0 0 0 3px var(--pz-accent-ring); }
.pz-cut-time.is-invalid { color: #bb3333; border-color: #e5b5b5; }
.pz-cut-here { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px;
               padding: 6px 10px; cursor: pointer; font: inherit; font-size: 13px; font-weight: 650;
               font-variant-numeric: tabular-nums; color: var(--pz-accent-ink); background: var(--pz-accent-soft);
               border: 1px solid var(--pz-accent-line); border-radius: 10px;
               -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
.pz-cut-here svg { width: 16px; height: 16px; flex-shrink: 0; }
.pz-cut-here:disabled { opacity: .45; cursor: default; }
/* Too narrow for the ends side by side (phones up to ~375 px): one row per
   end instead — its steps and time, "Sada" beside them — and the quick
   lengths speak for themselves without their caption. */
@container (max-width: 339px) {
  .pz-cut-ends { grid-template-columns: minmax(0, 1fr); }
  .pz-cut-end { grid-template-columns: minmax(0, 1fr) auto; align-items: center; column-gap: 8px; }
  .pz-cut-end-label { grid-column: 1 / -1; }
  .pz-cut-here { min-height: 40px; }
  .pz-cut-lengths > span { display: none; }
}
/* Wide (a desktop, a phone on its side): "Sada" beside the steps too — height is the scarce thing there. */
@container (min-width: 560px) {
  .pz-cut-end { grid-template-columns: minmax(0, 1fr) auto; align-items: center; column-gap: 8px; }
  .pz-cut-end-label { grid-column: 1 / -1; }
  .pz-cut-here { min-height: 40px; }
}
/* Quick lengths: the whole, or so long from the part's start. */
.pz-cut-lengths { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.pz-cut-lengths > span { margin-right: 2px; font-size: 12px; font-weight: 600; color: var(--pz-muted); }
.pz-cut-lengths .pz-chip { padding: 6px 11px; font-size: 12.5px; }
.pz-cut-hint[hidden], .pz-cut-warn[hidden], .pz-cut-note[hidden] { display: none; }
.pz-cut-warn, .pz-cut-note { margin: 0; font-size: 12.5px; line-height: 1.45; }
.pz-cut-warn { color: var(--pz-warn); font-weight: 600; }
.pz-cut-note { color: var(--pz-ink-2); }
.pz-cut-note.is-done { color: #27613d; font-weight: 600; }
.pz-cut-status { margin: 0; font-size: 12px; color: #a02318; }
.pz-range { display: flex; align-items: center; gap: 8px; }
.pz-range[hidden] { display: none; }
.pz-audio-search { display: grid; grid-template-columns: minmax(0,1fr) auto; }
.pz-audio-search .pz-input { min-width: 0; }
.pz-audio-search .pz-btn { white-space: nowrap; padding-inline: 14px !important; }
.pz-time { font-variant-numeric: tabular-nums; font-size: 12px; color: #656571; min-width: 130px; }
.pz-audio-effects[hidden] { display: none; }
.pz-audio-effects { display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
                    padding-top: 8px; border-top: 1px solid #ececf1; }
.pz-effect-buttons { display: flex; gap: 7px; flex: 0 0 auto; }
.pz-effect-btn { display: inline-flex; align-items: center; gap: 7px; padding: 7px 9px;
                 border: 1px solid #dddde6; border-radius: 9px; background: #fff;
                 color: #555560; cursor: pointer; font-size: 11.5px; }
.pz-effect-btn { justify-content: center; }
.pz-effect-btn:hover { border-color: var(--pz-accent-line); color: var(--pz-accent-ink); }
.pz-effect-btn.is-active { border-color: var(--pz-accent); background: var(--pz-accent-soft); color: var(--pz-accent-ink);
                           box-shadow: inset 0 0 0 1px var(--pz-accent); }
.pz-fade-icon { width: 21px; height: 13px; border-bottom: 1.5px solid currentColor; opacity: .85; }
.pz-fade-icon.is-in { border-radius: 0 0 100% 0; transform: skewY(-22deg); }
.pz-fade-icon.is-out { border-radius: 0 0 0 100%; transform: skewY(22deg); }
/* A fade's length picker appears once that fade is on, mp3cut-style. */
.pz-fade-group { display: flex; gap: 5px; align-items: stretch; }
.pz-fade-group .pz-effect-btn { flex: 1; }
.pz-fade-length { display: none; padding: 4px 5px; font: inherit; font-size: 12px;
                  color: var(--pz-accent-ink); background: var(--pz-accent-soft); border: 1px solid var(--pz-accent-line);
                  border-radius: 9px; cursor: pointer; }
.pz-fade-group.is-active .pz-fade-length { display: block; }
.pz-volume { display: grid; grid-template-columns: auto 1fr 44px; align-items: center; gap: 8px;
             flex: 1; min-width: 190px; color: #62626e; font-size: 11.5px; }
.pz-volume input { width: 100%; accent-color: var(--pz-accent); cursor: pointer; }
.pz-volume output { text-align: right; color: #35313f; font-weight: 650;
                    font-variant-numeric: tabular-nums; }

.pz-audio-preview { width: 100%; margin-top: 4px; }

/* ── Buttons ─────────────────────────────────────────────────────────────── */
/* Primary is the shop's accent. Hover darkens whatever colour the button has
   with an overlay, never with a second hard-coded colour — so a shop's own
   button colours (Storefront look) keep their hover too. */
.pz-btn { position: relative; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          min-height: 44px; padding: 10px 20px; font-family: inherit; font-size: 15px; font-weight: 650; line-height: 1.2;
          text-decoration: none; cursor: pointer; color: var(--pz-btn-text); background: var(--pz-accent);
          border: 0; border-radius: var(--pz-btn-radius); box-shadow: 0 1px 2px rgba(16,16,32,.14);
          -webkit-tap-highlight-color: transparent;
          transition: background-color .15s, box-shadow .15s, transform .1s, opacity .15s; }
.pz-btn:hover { background-image: linear-gradient(rgba(0,0,0,.09), rgba(0,0,0,.09)); }
.pz-btn:active { transform: translateY(1px); }
.pz-btn:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; background-image: none; }
.pz-btn .pz-ic { font-size: 18px; }
.pz-btn.is-recording { background: #c0392b; animation: pz-pulse 1.4s ease-in-out infinite; }
@keyframes pz-pulse { 50% { box-shadow: 0 0 0 7px rgba(192,57,43,.18); } }
.pz-btn-ghost { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 42px;
                padding: 9px 16px; font-family: inherit; font-size: 14px; font-weight: 600; line-height: 1.2;
                cursor: pointer; color: var(--pz-ink-2); background: var(--pz-surface);
                border: 1.5px solid var(--pz-line-2); border-radius: var(--pz-btn-radius);
                -webkit-tap-highlight-color: transparent;
                transition: background-color .15s, border-color .15s, color .15s; }
.pz-btn-ghost:hover { background: var(--pz-surface-2); border-color: var(--pz-line-hover); color: var(--pz-ink); }
.pz-btn-ghost:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-btn-ghost:disabled { opacity: .45; cursor: default; }
.pz-btn-ghost .pz-ic { font-size: 17px; }
.pz-btn-ghost.small { min-height: 0; }

/* ── Running total ───────────────────────────────────────────────────────── */
.pz-total { display: flex; justify-content: space-between; align-items: center; gap: 12px;
            padding: 12px 16px; border-radius: var(--pz-radius); background: var(--pz-surface-2);
            border: 1px solid var(--pz-line); color: var(--pz-ink-2); font-size: 14px; font-weight: 550; }
.pz-total b { font-size: 16px; font-weight: 700; color: var(--pz-ink); font-variant-numeric: tabular-nums; white-space: nowrap; }

/* ── The price box: product, extras, total, the saving ───────────────────── */
.pz-pricebox { display: grid; gap: 7px; padding: 14px 16px; border-radius: var(--pz-radius);
            background: var(--pz-surface); border: 1px solid var(--pz-line); color: var(--pz-ink-2); font-size: 14px; }
.pz-pricebox[hidden] { display: none; }
.pz-pricebox-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; min-width: 0; }
.pz-pricebox-label { min-width: 0; overflow-wrap: anywhere; }
.pz-pricebox-value { flex-shrink: 0; text-align: end; font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--pz-ink); }
.pz-pricebox-value del { color: var(--pz-subtle); font-size: .88em; font-weight: 500; margin-inline-end: 4px; }
.pz-pricebox-value b { font-weight: 650; }
.pz-pricebox-head { margin-top: 3px; font-size: 11.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--pz-muted); }
.pz-pricebox-row.is-extra { padding-inline-start: 10px; border-inline-start: 2px solid var(--pz-accent-line); font-size: 13.5px; }
.pz-pricebox-row.is-extra .pz-pricebox-value { color: var(--pz-ink-2); font-weight: 600; }
.pz-pricebox-row.is-qty { font-size: 13.5px; color: var(--pz-muted); }
.pz-pricebox-rule { height: 1px; margin: 3px 0 1px; background: var(--pz-line); }
.pz-pricebox-row.is-total { align-items: center; font-size: 15px; font-weight: 650; color: var(--pz-ink); }
.pz-pricebox-row.is-total .pz-pricebox-value b { font-size: 20px; font-weight: 800; letter-spacing: -.01em; }
.pz-pricebox-row.is-total .pz-pricebox-value del { font-size: 14px; }
.pz-pricebox-saved { justify-self: end; padding: 3px 10px; border-radius: 99px; font-size: 12.5px; font-weight: 700;
                  color: #1f7a45; background: #e6f6ec; }
.pz-foot-total del { font-weight: 500; color: var(--pz-subtle); }
/* The saving beside the total in the popup's footer: a sale, a pieces deal, or both. */
.pz-foot-save { display: inline-block; margin-inline-end: 4px; padding: 1px 6px; border-radius: 99px; font-size: .78em; font-style: normal;
                font-weight: 700; color: #1f7a45; background: #e6f6ec; vertical-align: 1px; }
.pz-step-pricebox { margin-top: 14px; }
.pz-step-pricebox.is-off { display: none; }
.pz-pricebox + .pz-finish-add, .pz-pricebox + .pz-face-add { margin-top: 12px; }

/* ── Share link ──────────────────────────────────────────────────────────── */
.pz-share { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.pz-share .pz-input { flex: 1 1 180px; min-width: 0; min-height: 40px; font-size: 13px; padding: 8px 12px; }
.pz-share .pz-hint { flex-basis: 100%; margin: 0; }
.pz-share-btn { display: inline-flex; align-items: center; gap: 7px; min-height: 40px; padding: 8px 14px;
                background: var(--pz-share-bg, var(--pz-surface)); color: var(--pz-share-text, var(--pz-ink-2));
                border: var(--pz-share-border-width, 1.5px) solid var(--pz-share-border, var(--pz-line-2));
                border-radius: var(--pz-share-radius, var(--pz-btn-radius)); font-size: var(--pz-share-font-size, 13.5px); }
.pz-share-btn .pz-ic { font-size: 16px; }
.pz-modal-preview .pz-share { justify-content: center; }

/* ── Popup mode: the calm face on the product page ───────────────────────── */
.pz-launch { display: grid; gap: 12px; }
/* The call to action while nothing is personalized yet: full width of the
   summary column, thumb-height, the shop's accent (or its own button look). */
.pz-launch-btn { width: 100%; min-height: 54px; padding: 14px 22px; gap: 10px;
                 font-size: var(--pz-personalize-font-size, 16px); font-weight: 700; letter-spacing: .005em;
                 background: var(--pz-personalize-bg, var(--pz-accent)); color: var(--pz-personalize-text, var(--pz-btn-text));
                 border: var(--pz-personalize-border-width, 0) solid var(--pz-personalize-border, transparent);
                 border-radius: var(--pz-personalize-radius, var(--pz-btn-radius));
                 box-shadow: 0 1px 2px rgba(16,16,32,.14), 0 12px 24px -14px rgba(16,16,32,.5); }
.pz-launch-btn .pz-ic { font-size: 20px; }
.pz-launch-btn .pz-launch-arrow { font-size: 18px; opacity: .9; transition: transform .2s var(--pz-ease); }
.pz-launch-btn:hover .pz-launch-arrow { transform: translateX(3px); }
/* Once the design is finished, the shop's add-to-cart is the next step:
   "Izmeni" steps back to a secondary button instead of competing with it. */
.pz-launch-btn.is-edit { min-height: 48px; font-size: 15px; font-weight: 650; color: var(--pz-ink);
                         background: var(--pz-surface); border: 1.5px solid var(--pz-line-2); box-shadow: var(--pz-shadow-sm); }
.pz-launch-btn.is-edit:hover { background: var(--pz-surface-2); background-image: none; border-color: var(--pz-line-hover); }
.pz-launch-btn.is-edit .pz-ic { font-size: 17px; color: var(--pz-muted); }
.pz-launch-meta { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px 18px;
                  font-size: 12.5px; color: var(--pz-muted); }
.pz-launch-meta span { display: inline-flex; align-items: center; gap: 6px; }
.pz-launch-meta .pz-ic { font-size: 15px; color: var(--pz-subtle); }

/* The summary after the popup closed: what was chosen, at a glance. */
.pz-summary { display: grid; overflow: hidden; background: var(--pz-surface); border: 1px solid var(--pz-line);
              border-radius: var(--pz-radius-lg); box-shadow: var(--pz-shadow-sm); }
.pz-summary-top { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap;
                  padding: 12px 14px; }
.pz-summary-top strong { font-size: 15px; font-weight: 700; letter-spacing: -.01em; color: var(--pz-ink); }
.pz-summary-state { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 99px;
                    font-size: 12px; font-weight: 650; line-height: 1.5; }
.pz-summary-state .pz-ic { font-size: 13px; }
.pz-summary-state.is-ok { color: var(--pz-success); background: #e7f4ec; }
.pz-summary-state.is-warn { color: var(--pz-warn); background: #fdf2e1; }
.pz-summary > .pz-peek { width: auto; margin: 0 14px 12px; box-shadow: none; }
.pz-summary-lines { display: grid; margin: 0; padding: 4px 14px; border-top: 1px solid var(--pz-line); }
.pz-summary-swatch { display: inline-block; width: 14px; height: 14px; border-radius: 50%; margin-right: 6px;
                      vertical-align: -2px; border: 1px solid var(--pz-line); }
.pz-summary-line { display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
                   padding: 8px 0; font-size: 13.5px; line-height: 1.4; }
.pz-summary-lines .pz-summary-line + .pz-summary-line { border-top: 1px dashed var(--pz-line); }
.pz-summary-line dt, .pz-summary-line span { margin: 0; color: var(--pz-muted); flex-shrink: 0; max-width: 46%; }
.pz-summary-line dd, .pz-summary-line b { margin: 0; min-width: 0; text-align: end; font-weight: 600; color: var(--pz-ink); }
.pz-summary-line.is-total { padding: 11px 14px; background: var(--pz-surface-2); border-top: 1px solid var(--pz-line); }
.pz-summary-line.is-total span { color: var(--pz-ink-2); font-weight: 550; }
.pz-summary-line.is-total b { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }

/* ── The popup (wizard) ──────────────────────────────────────────────────── */
/* No backdrop-filter here: blurring the whole page behind a fixed overlay makes
   some GPUs re-composite continuously, and the dimmed page reads just as well.
   The z-index sits near the ceiling on purpose: chat bubbles float at millions,
   and a chatbot hovering over the "Dalje" button mid-checkout loses sales. */
.pz-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483000; background: rgba(12,12,20,.6);
              display: grid; place-items: center; padding: 16px; }
.pz-overlay.pz-layer { padding: 16px;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right))
           max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left)); }
.pz-overlay[hidden] { display: none; }
/* A fixed height, so the dialog does not grow and shrink between steps; the
   visible viewport (--pz-vvh) caps it where the browser's own bars take room. */
.pz-modal { position: relative; display: flex; flex-direction: column; width: min(1040px, 100%);
            height: 92vh; height: min(760px, 92vh);
            height: min(760px, calc(var(--pz-vvh, 100vh) - 32px));
            background: var(--pz-surface); color: var(--pz-ink); border-radius: 20px; box-shadow: var(--pz-shadow-lg);
            overflow: hidden; animation: pz-pop-in .22s var(--pz-ease); }
.pz-modal-head { display: flex; align-items: center; gap: 12px; flex-shrink: 0; padding: 14px 14px 12px 22px; }
.pz-modal-heading { display: grid; gap: 1px; flex: 1; min-width: 0; }
.pz-modal-heading strong { font-size: 16px; font-weight: 700; letter-spacing: -.01em; line-height: 1.3; color: var(--pz-ink);
                           min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* "Korak 2 od 4" is what the stepper shows; the words stay for a screen reader. */
.pz-step-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.pz-modal-close { display: grid; place-items: center; width: 40px; height: 40px; flex-shrink: 0; padding: 0;
                  cursor: pointer; color: var(--pz-ink-2); background: var(--pz-surface-2); border: 0; border-radius: 99px;
                  -webkit-tap-highlight-color: transparent; transition: background-color .15s, color .15s; }
.pz-modal-close:hover { background: var(--pz-surface-3); color: var(--pz-ink); }
.pz-modal-close:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-modal-close svg, .pz-lightbox-close svg { width: 18px; height: 18px; }
/* The stepper: numbered dots joined by a line that fills as the shopper goes;
   the current step is a pill with its name. Three states besides the current
   one, each with its own colour: done (a check on the accent's wash), missing
   (passed with a required answer still empty — amber), to come (grey). */
/* One top row everywhere: the title, the stepper, the close button. */
.pz-modal-head .pz-modal-heading { flex: 0 1 auto; max-width: 34%; }
.pz-steps { flex: 1 1 auto; display: flex; align-items: center; min-width: 0;
            overflow-x: auto; scrollbar-width: none; padding: 2px; margin: 0 -2px; }
.pz-steps::-webkit-scrollbar { display: none; }
/* Centred while it fits; auto margins drop to zero once it scrolls, so the
   first step never ends up out of reach the way justify-content would leave it. */
.pz-steps > :first-child { margin-inline-start: auto; }
.pz-steps > :last-child { margin-inline-end: auto; }
.pz-step-line { flex: 1 1 14px; min-width: 8px; max-width: 72px; height: 2px; margin: 0 5px; border-radius: 2px;
                background: var(--pz-line-2); transition: background-color .25s; }
.pz-step-line.is-done { background: var(--pz-accent); }
.pz-step-chip { position: relative; display: inline-flex; align-items: center; gap: 7px; flex: none;
                min-height: 30px; padding: 0; margin: 0; border: 0; border-radius: 99px; background: none;
                cursor: pointer; font: inherit; color: var(--pz-muted); -webkit-tap-highlight-color: transparent;
                transition: background-color .2s, color .2s, padding .2s; }
/* A thumb-sized target around a small dot. */
.pz-step-chip::after { content: ''; position: absolute; top: -7px; bottom: -7px; left: -4px; right: -4px; }
.pz-step-chip:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-step-dot { display: grid; place-items: center; flex: none; width: 24px; height: 24px; border-radius: 99px;
               border: 1.5px solid var(--pz-line-2); background: var(--pz-surface); color: var(--pz-muted);
               font-size: 12px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums;
               transition: background-color .2s, border-color .2s, color .2s; }
.pz-step-dot .pz-ic { font-size: 13px; }
.pz-step-ok { display: none; }
.pz-step-name { font-size: 13px; font-weight: 600; line-height: 1.2; max-width: 160px;
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pz-step-chip:not(.is-current):hover .pz-step-dot { border-color: var(--pz-accent); color: var(--pz-accent-ink); }
.pz-step-chip.is-done { color: var(--pz-ink-2); }
.pz-step-chip.is-done .pz-step-dot { border-color: transparent; background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-step-chip.is-done .pz-step-num { display: none; }
.pz-step-chip.is-done .pz-step-ok { display: inline-grid; }
.pz-step-chip.is-missing { color: var(--pz-warn); }
.pz-step-chip.is-missing .pz-step-dot { border-color: #e3a52b; background: #fff6e2; color: var(--pz-warn); }
.pz-step-chip.is-current { padding: 3px 13px 3px 3px; background: var(--pz-accent); color: var(--pz-btn-text);
                           box-shadow: 0 2px 8px var(--pz-accent-ring); }
.pz-step-chip.is-current .pz-step-dot { border-color: transparent; background: var(--pz-btn-text); color: var(--pz-accent-ink); }
.pz-modal-body { display: grid; grid-template-columns: minmax(0,1fr); gap: 18px; flex: 1; min-height: 0;
                 padding: 18px; overflow-y: auto; overscroll-behavior: contain; border-top: 1px solid var(--pz-line); }
.pz-modal-step { display: grid; grid-template-columns: minmax(0,1fr); gap: 18px; align-content: start; min-width: 0; }
.pz-modal-step .pz-label { font-size: 15px; }
/* The lines inside a song card are sub-fields, not the step's question. */
.pz-modal-step .pz-song-line-label { font-size: 13px; }
.pz-step-title { margin: 0; font-size: 20px; font-weight: 700; letter-spacing: -.015em; line-height: 1.25; color: var(--pz-ink); }
.pz-step-title[hidden] { display: none; }
/* In the popup the artwork itself carries the frame: the box around it is
   transparent, so a portrait poster is not flanked by an empty card. */
.pz-modal-preview { display: flex; flex-direction: column; align-items: stretch; gap: 12px; min-width: 0; }
/* The box still clips: photo-framing frames and grips can reach past the
   artwork (bleed), and must never spill over the pane around it. So the
   artwork's edge is an outline drawn INSIDE the canvas, which no clip cuts. */
.pz-modal-preview .pz-preview { background: transparent; border: 0; box-shadow: none; border-radius: 10px; }
.pz-modal-preview .pz-preview canvas { width: auto; max-width: 100%; height: auto; margin: 0 auto;
                                       border-radius: 10px; background: var(--pz-surface);
                                       outline: 1px solid rgba(16,16,32,.1); outline-offset: -1px; }
.pz-modal-preview .pz-preview-cap { justify-content: center; min-height: 0; padding: 12px 0 0;
                                    background: transparent; border: 0; }
.pz-modal-preview .pz-previews { justify-content: center; background: transparent; border: 0; padding: 12px 0 0; }
.pz-modal-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; flex-shrink: 0;
                 padding: 14px 18px; padding-bottom: max(14px, env(safe-area-inset-bottom));
                 background: var(--pz-surface); border-top: 1px solid var(--pz-line); }
/* The reason the button did nothing, beside the button — a message at the top
   of a scrolled step is a message nobody sees. */
.pz-foot-error { flex: 1 1 100%; order: -1; margin: 0; padding: 9px 12px;
  border-radius: var(--pz-radius-sm); background: #fdeeee; color: #9b1c1c; font-size: 13px; font-weight: 500;
  overflow-wrap: anywhere; }
.pz-foot-error[hidden] { display: none; }
.pz-foot-total { display: grid; justify-items: end; min-width: 0; line-height: 1.2; text-align: end; margin-inline-start: auto; }
.pz-foot-total small { font-size: 11.5px; font-weight: 500; color: var(--pz-muted); }
.pz-foot-total b { font-size: 16px; font-weight: 700; color: var(--pz-ink); font-variant-numeric: tabular-nums; white-space: nowrap; }
.pz-modal-back { padding-left: 12px; padding-right: 16px; }
.pz-modal-back.is-first { visibility: hidden; }
.pz-modal-next { min-width: 148px; min-height: 48px; margin-inline-start: auto; padding-left: 22px; padding-right: 18px; font-size: 15.5px; }
.pz-foot-total:not([hidden]) + .pz-modal-next { margin-inline-start: 0; }
.pz-modal-next .pz-done-ic { display: none; }
.pz-modal-next.is-last .pz-next-ic { display: none; }
.pz-modal-next.is-last .pz-done-ic { display: inline-grid; }
.pz-modal-cart { min-height: 48px; }
/* Sound steps hide the artwork: nothing on it changes, and the cutter needs the room. */
.pz-modal-body.is-audio-step { grid-template-columns: minmax(0,1fr); }
.pz-modal-body.is-audio-step .pz-modal-preview { display: none; }

/* Tablets and desktops: two panes, each scrolling on its own — the picture
   stays put while a long step scrolls beside it. */
@media (min-width: 700px) {
  .pz-modal-body { grid-template-columns: minmax(0,1.08fr) minmax(0,1fr); gap: 0; padding: 0; overflow: hidden; }
  .pz-modal-preview { justify-content: center; min-height: 0; overflow-y: auto; padding: 24px;
                      background: var(--pz-surface-2); border-right: 1px solid var(--pz-line); }
  /* What the pane has left once the head (~86px), the footer (~78px), the
     pane's padding, the caption and the share row have taken theirs. */
  .pz-modal-preview .pz-preview canvas { max-height: 50vh;
    max-height: calc(min(760px, 92vh) - 306px);
    max-height: calc(min(760px, var(--pz-vvh, 100vh) - 32px) - 306px); }
  .pz-modal-step { min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 24px 26px 28px; }
  .pz-modal-foot { padding-left: 22px; padding-right: 22px; }
}
@media (min-width: 1000px) {
  .pz-modal-step { padding: 28px 32px 32px; }
}

/* ── Phones: the popup becomes a full-screen sheet, picture pinned on top ── */
@media (max-width: 699px) {
  .pz-wave { height: 76px; }
  .pz-cutter { gap: 9px; }
  .pz-audio-effects { grid-template-columns: 1fr; }
  .pz-effect-buttons { flex: 1 1 100%; }
  .pz-effect-buttons .pz-fade-group { flex: 1; }
  .pz-effect-btn { justify-content: center; flex: 1; }
  .pz-volume { padding-top: 3px; }

  .pz-overlay, .pz-overlay.pz-layer { padding: 0; place-items: stretch; grid-template-rows: minmax(0, 1fr); }
  /* The sheet is the VISIBLE viewport, not 100vh: the URL bar and the keyboard
     both take a bite out of the latter and push "Dalje" off the bottom. And it
     is never taller than the layer's own box, which the browser keeps on the
     screen: --pz-vvh is a measurement, and a stale one (the browser's bars
     coming back after the page was locked) hid the whole footer under the
     bottom edge while the head stayed put. The body gives up the height. */
  .pz-layer.pz-overlay { top: 0; top: var(--pz-vv-top, 0px); }
  .pz-modal { width: 100%; max-width: none; height: 100vh; height: 100dvh; height: var(--pz-vvh, 100dvh);
              max-height: 100%;
              border-radius: 0; box-shadow: none; animation-name: pz-sheet-in; }
  /* One top row: the stepper where the heading was, then the close button.
     The heading stays for a screen reader; the chips say the same, by name. */
  .pz-modal-head { gap: 10px; padding: 8px 8px 8px 14px;
                   padding-top: max(8px, env(safe-area-inset-top)); border-bottom: 1px solid var(--pz-line); }
  .pz-modal-heading { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  /* Off-screen steps show as dots; only the current one spends width on a name. */
  .pz-step-chip:not(.is-current) .pz-step-name { display: none; }
  .pz-step-name { max-width: 42vw; }
  .pz-step-line { margin: 0 4px; }
  .pz-modal-close { width: 38px; height: 38px; }
  .pz-modal-body { display: flex; flex-direction: column; gap: 0; padding: 0; overflow-y: auto; border-top: 0; }
  .pz-modal-preview { position: sticky; top: 0; z-index: 2; gap: 8px; padding: 10px 16px;
                      background: var(--pz-surface-2); border-bottom: 1px solid var(--pz-line); }
  .pz-modal-preview .pz-preview canvas { max-height: 34vh; max-height: 34svh; border-radius: 10px; }
  .pz-modal-preview .pz-preview-cap { display: none; }
  .pz-modal-preview .pz-previews { padding-top: 8px; }
  .pz-modal-step { gap: 16px; padding: 16px 16px 24px; }
  /* The current chip already names the step — and, on a step of one question,
     that question too; its label stays for a screen reader only. */
  .pz-step-title { display: none; }
  .pz-modal-body.is-single .pz-modal-step .pz-row:not(.pz-accordion) > .pz-label {
    position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  /* The picture is a thumbnail here: the "editing" chip must not outgrow it. */
  .pz-modal-preview .pz-focus-chip { font-size: 10.5px; padding: 2px 8px; }
  /* Sharing is a thing to do with a finished design: on a phone it waits for
     the last step instead of sitting under every question. */
  .pz-modal-body:not(.is-last-step) .pz-modal-step > .pz-share { display: none; }
  /* "Dalje" owns the row: a thumb-sized primary that no floating chat bubble
     can shadow (the overlay also sits above chat widgets while open). */
  .pz-modal-foot { gap: 10px; padding: 10px 12px;
                   padding-bottom: max(10px, env(safe-area-inset-bottom));
                   padding-bottom: max(10px, env(safe-area-inset-bottom), var(--pz-inapp-bottom, 0px)); }
  .pz-modal-back { width: 48px; min-height: 48px; padding: 0; flex-shrink: 0; }
  .pz-modal-back .pz-btn-text { display: none; }
  .pz-modal-back .pz-ic { font-size: 20px; }
  .pz-modal-back.is-first { display: none; }
  .pz-foot-total { flex: 0 1 auto; margin-inline-start: 0; }
  .pz-modal-next { flex: 1 1 0; min-width: 0; margin-inline-start: 0; padding-left: 18px; padding-right: 14px; }
  .pz-modal-cart { flex: 1 1 100%; }
  /* iOS zooms into any input under 16px — never let that happen mid-checkout. */
  .pz-input, .pz-modal-step input, .pz-modal-step textarea, .pz-modal-step select { font-size: 16px; }
  /* Keyboard up: the picture collapses to a strip so the field being typed into
     and the button that leaves the step are both on screen. */
  .pz-layer.has-keyboard .pz-modal-preview { padding: 6px 16px; }
  .pz-layer.has-keyboard .pz-modal-preview .pz-preview canvas { max-height: 56px; }
  .pz-layer.has-keyboard .pz-modal-head,
  .pz-layer.has-keyboard .pz-modal-preview .pz-previews { display: none; }
}
@keyframes pz-sheet-in { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
/* "Završi" on a phone (the popup): one screen holds the picture strip, the
   date, the promise, how many, the price and the button. */
.pz-finish-peek { display: none; }
@media (max-width: 699px) {
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview { flex-direction: row; align-items: center; gap: 12px; padding: 8px 14px; }
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-preview { flex: 0 0 auto; }
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview { padding: 6px 14px; }
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-preview canvas { max-height: 52px; max-width: 84px; border-radius: 6px; }
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-previews,
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-focus-chip,
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-edit,
  .pz-modal-body.is-finish-step:not(.is-peek-open) .pz-modal-preview .pz-edit-above { display: none !important; }
  .pz-modal-body.is-finish-step .pz-finish-peek { display: flex; flex: 1 1 auto; align-items: center; justify-content: space-between; gap: 10px;
    min-width: 0; min-height: 44px; padding: 0; font: inherit; text-align: left; color: var(--pz-ink); background: none; border: 0; cursor: pointer; }
  .pz-modal-body.is-finish-step.is-peek-open .pz-finish-peek { justify-content: center; flex: 0 0 auto; }
  .pz-modal-body.is-finish-step.is-peek-open .pz-finish-peek-copy { display: none; }
  .pz-finish-peek-copy { display: grid; gap: 1px; min-width: 0; }
  .pz-finish-peek-copy strong { font-size: 14px; font-weight: 700; }
  .pz-finish-peek-copy span { font-size: 12px; color: var(--pz-muted); }
  .pz-finish-peek-cta { flex-shrink: 0; padding: 7px 12px; border-radius: 99px; font-size: 13px; font-weight: 650;
    color: var(--pz-accent); background: var(--pz-surface); border: 1px solid var(--pz-accent-line, var(--pz-line)); }

  .pz-modal-body.is-finish-step .pz-modal-step { gap: 8px; padding: 10px 14px 12px; }
  .pz-modal-step .pz-finish { gap: 10px; }
  .pz-modal-step .pz-finish-when { gap: 6px; }
  .pz-modal-step .pz-finish-label { font-size: 14px; }
  .pz-modal-step .pz-finish-opt { min-height: 44px; padding: 6px 10px; border-radius: 10px; }
  .pz-modal-step .pz-finish-opt-title { font-size: 14px; }
  .pz-modal-step .pz-finish-opt-sub { font-size: 11.5px; }
  .pz-modal-step .pz-finish-opt-sub.is-note { display: none; }
  .pz-modal-step .pz-finish-help { font-size: 11.5px; line-height: 1.35; }
  .pz-modal-step .pz-finish:not(.is-date) .pz-finish-help { display: none; }
  /* The promise: one headline and a slim timeline instead of the plugin's big card. */
  .pz-modal-step .pz-eta { gap: 4px; padding: 7px 10px; border-radius: 10px; }
  .pz-modal-step .pz-eta-head { padding: 0; font-size: 13px; background: none; }
  .pz-modal-step .pz-eta-icon { width: 24px; height: 24px; margin-bottom: 0; font-size: 12px; }
  .pz-modal-step .pz-eta-step { gap: 1px; }
  .pz-modal-step .pz-eta-step:not(:last-child)::after { top: 11px; left: calc(50% + 16px); right: calc(-50% + 16px); }
  .pz-modal-step .pz-eta-day { font-size: 10.5px; text-transform: none; letter-spacing: 0; white-space: nowrap; }
  .pz-modal-step .pz-eta-status { font-size: 11px; }
  .pz-modal-step .pz-eta-foot { font-size: 11px; }
  .pz-modal-step .pz-eta-take { min-height: 38px; font-size: 13px; }
  .pz-modal-step .pz-finish-qtybox { gap: 8px; }
  .pz-modal-step .pz-qty-btn { width: 36px; height: 36px; font-size: 20px; }
  .pz-modal-step .pz-qty-val { min-width: 32px; font-size: 15px; }
  .pz-modal-step .pz-deal-picks { gap: 6px; }
  .pz-modal-step .pz-deal-pick { min-height: 38px; padding: 3px 4px; gap: 0; border-radius: 10px; }
  .pz-modal-step .pz-deal-n { font-size: 13px; }
  .pz-modal-step .pz-deal-off { font-size: 11px; }
  .pz-modal-step .pz-deal-line, .pz-modal-step .pz-piece-note { font-size: 12px; line-height: 1.35; }
  .pz-modal-step .pz-piece-opt { min-height: 38px; padding: 5px 8px; font-size: 13px; }
  .pz-modal-step .pz-step-pricebox { gap: 3px; margin-top: 0; padding: 6px 12px; font-size: 13px; }
  /* The footer shows the total: here only what it does not — each saving, each paid extra, the whole saving. */
  .pz-modal-step .pz-step-pricebox:not(.has-more) { display: none; }
  .pz-modal-step .pz-step-pricebox .is-base, .pz-modal-step .pz-step-pricebox .is-qty,
  .pz-modal-step .pz-step-pricebox .pz-pricebox-rule, .pz-modal-step .pz-step-pricebox .is-total { display: none; }
  .pz-modal-step .pz-step-pricebox .pz-pricebox-row.is-total { font-size: 14px; }
  .pz-modal-step .pz-step-pricebox .pz-pricebox-row.is-total .pz-pricebox-value b { font-size: 17px; }
  .pz-modal-step .pz-step-pricebox .pz-pricebox-saved { padding: 2px 8px; font-size: 11.5px; }
  /* Only the saving to say: the pill alone, not a box around it. */
  .pz-modal-step .pz-step-pricebox:not(:has(.pz-pricebox-row:not(.is-base):not(.is-qty):not(.is-total))) { padding: 0; border: 0; background: none; }
}
/* Phones up to 430 px: prices with ",00 RSD" leave "Dodaj u korpu" no room for
   two words and a tick — the footer drops the word "Ukupno" (the struck price
   stays) and the tick, never the button's words onto two lines. */
@media (max-width: 430px) {
  .pz-foot-total .pz-foot-label { display: none; }
  .pz-foot-total small:not(:has(del)) { display: none; }
  .pz-modal-next.is-last { white-space: nowrap; padding-left: 14px; padding-right: 14px; }
  .pz-modal-next.is-last .pz-done-ic { display: none; }
}

/* A phone on its side: head, a third of the height of picture and a footer
   left ~100px for the question itself. Two columns instead. */
@media (max-height: 520px) and (orientation: landscape) {
  .pz-modal-head { padding: 6px 10px 6px 16px; }
  /* The stepper shares the one top row with the close button, as on a phone upright. */
  .pz-modal-heading { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  .pz-modal-close { width: 36px; height: 36px; }
  .pz-modal-body { display: grid; grid-template-columns: 42% minmax(0,1fr); gap: 0; padding: 0; overflow: hidden; }
  .pz-modal-preview { position: static; justify-content: center; overflow-y: auto; min-height: 0; padding: 10px 12px;
                      border-bottom: 0; border-right: 1px solid var(--pz-line); }
  /* Head (~50px), footer (~56px), the pane's padding and the share row all
     take a bite; what is left is the picture. The caption has no room here. */
  .pz-modal-preview .pz-preview canvas { max-height: 40vh; max-height: calc(var(--pz-vvh, 100vh) - 196px); }
  .pz-modal-preview .pz-preview-cap { display: none; }
  .pz-modal-step { min-height: 0; overflow-y: auto; padding: 12px 16px 16px; gap: 14px; }
  .pz-step-title { font-size: 16px; }
  .pz-modal-foot { padding: 6px 12px; padding-bottom: max(6px, env(safe-area-inset-bottom)); }
  .pz-modal-foot .pz-btn, .pz-modal-foot .pz-btn-ghost { min-height: 42px; padding-top: 6px; padding-bottom: 6px; }
  .pz-modal-back { width: auto; padding-left: 10px; padding-right: 14px; }
  .pz-modal-back .pz-btn-text { display: inline; }
  .pz-modal-back.is-first { display: inline-flex; visibility: hidden; }
  .pz-modal-next { flex: 0 1 auto; min-width: 140px; margin-inline-start: auto; }
  .pz-lightbox-hint { display: none; }
}
/* Folded phones and the iPhone SE: the total drops its caption. */
@media (max-width: 380px) {
  .pz-foot-total .pz-foot-label { display: none; }
  .pz-foot-total small:not(:has(del)) { display: none; }
  .pz-foot-total b { font-size: 14.5px; }
  .pz-modal-heading strong { font-size: 14px; }
  .pz-launch-btn { padding-left: 16px; padding-right: 16px; }
}

/* ── Status line and loading ─────────────────────────────────────────────── */
.pz-status { margin: 0; padding: 11px 14px; border-radius: var(--pz-radius); border: 1px solid transparent;
             font-size: 14px; font-weight: 500; line-height: 1.45; }
.pz-status.is-error { background: #fdeeee; border-color: #f6caca; color: #9b1c1c; }
.pz-status.is-info { background: var(--pz-accent-soft); border-color: var(--pz-accent-line); color: var(--pz-ink-2); }
.pz-status[hidden] { display: none; }
/* "Počni ispočetka" beside "Nastavljeno od prethodnog puta". */
.pz-status-action { padding: 0; border: 0; background: none; color: var(--pz-accent); font: inherit; font-weight: 600;
  text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
/* Editing a cart line: what is going on, and the way back. */
.pz-edit-note { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 0 0 14px; padding: 11px 14px;
  border-radius: var(--pz-radius); background: var(--pz-accent-soft); border: 1px solid var(--pz-accent-line); color: var(--pz-ink-2); }
.pz-edit-note > .pz-ic { color: var(--pz-accent); flex: none; }
.pz-edit-note-text { display: grid; gap: 2px; flex: 1 1 220px; min-width: 0; font-size: 14px; line-height: 1.35; }
.pz-edit-note-text strong { color: var(--pz-ink); font-weight: 650; }
.pz-edit-note-back { color: var(--pz-accent); font-weight: 600; text-decoration: none; white-space: nowrap; }
.pz-edit-note-back:hover, .pz-edit-note-back:focus-visible { text-decoration: underline; }
/* Across both columns, above the design and the form — never in the preview's cell. */
.pz-wrap > .pz-edit-note { grid-column: 1 / -1; }

/* A skeleton of the form: the page keeps its shape while the questions load. */
.pz-loading { display: grid; gap: 10px; padding: 2px 0; }
.pz-bone { display: block; height: 14px; border-radius: 8px;
           background: linear-gradient(100deg, #ececf1 30%, #f7f7f9 48%, #ececf1 66%);
           background-size: 400% 100%; animation: pz-shimmer 1.2s ease-in-out infinite; }
.pz-bone--title { width: 58%; height: 20px; margin-bottom: 8px; }
.pz-bone--label { width: 34%; height: 12px; margin-top: 6px; }
.pz-bone--field { height: 46px; border-radius: 12px; }
.pz-bone--button { height: 54px; border-radius: 12px; margin-top: 10px; }
@keyframes pz-spin { to { transform: rotate(360deg); } }

/* Background removal, shown over the preview while it runs.
   A viewfinder rather than a spinner: corner brackets and a sweep say "this
   picture is being looked at", which is what is actually happening. A spinner
   says "wait", which tells the shopper nothing about their photo. */
.pz-cutout { position: absolute; top: 0; right: 0; bottom: 0; left: 0; display: flex; align-items: center;
             justify-content: center; pointer-events: none;
             overflow: hidden; border-radius: inherit; z-index: 4;
             background: rgba(10,10,18,.58); opacity: 1;
             transition: opacity .28s ease; }
.pz-cutout.is-done { opacity: 0; }

/* The frame the brackets sit on: a square-ish box centred on the preview. */
.pz-cutout-frame { position: absolute; top: 50%; left: 50%;
                   width: min(54%, 230px); aspect-ratio: 1;
                   transform: translate(-50%, -50%); }
.pz-cutout-frame i { position: absolute; width: 34px; height: 34px;
                     border: 3px solid #fff; border-radius: 4px;
                     filter: drop-shadow(0 1px 3px rgba(0,0,0,.45)); }
.pz-cutout-frame i:nth-child(1) { top: 0; left: 0; border-right: 0; border-bottom: 0; }
.pz-cutout-frame i:nth-child(2) { top: 0; right: 0; border-left: 0; border-bottom: 0; }
.pz-cutout-frame i:nth-child(3) { bottom: 0; right: 0; border-left: 0; border-top: 0; }
.pz-cutout-frame i:nth-child(4) { bottom: 0; left: 0; border-right: 0; border-top: 0; }
/* Brackets breathe inward once per sweep, so the frame feels like it is
   focusing rather than just sitting there. */
.pz-cutout-frame { animation: pz-cutout-focus 1.9s ease-in-out infinite; }
@keyframes pz-cutout-focus {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-50%, -50%) scale(.955); }
}

.pz-cutout-scan { position: absolute; left: 0; right: 0; height: 2px;
                  background: linear-gradient(90deg, transparent, #fff 18%, #fff 82%, transparent);
                  box-shadow: 0 0 14px 3px rgba(255,255,255,.55);
                  animation: pz-cutout-sweep 1.9s cubic-bezier(.55,0,.45,1) infinite; }
@keyframes pz-cutout-sweep {
  0%   { top: 0;    opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { top: 100%; opacity: 0; }
}

/* Sits just under the frame rather than at the foot of the box, so the eye
   goes frame -> words instead of hunting for a caption. */
.pz-cutout-label { position: absolute; top: 50%; left: 50%;
                   transform: translate(-50%, calc(min(27vw, 115px) + 22px));
                   font-size: 13.5px; font-weight: 500; color: #fff;
                   letter-spacing: .1px; text-align: center; max-width: 80%;
                   text-shadow: 0 1px 4px rgba(0,0,0,.6); }
.pz-cutout.is-error { background: rgba(12,12,20,.62); }
.pz-cutout.is-error .pz-cutout-frame, .pz-cutout.is-error .pz-cutout-scan { display: none; }

/* Mini Me head cut (head-reveal.ts): the shopper's own photo is analysed on
   screen, the head is bracketed and cut out with a glow, then flies onto the
   product. Everything is transform/opacity so it stays on the compositor. */
.pz-hc { position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: 5;
         overflow: hidden; border-radius: inherit; pointer-events: none;
         background: radial-gradient(120% 90% at 50% 40%, rgba(24,22,48,.62), rgba(8,8,16,.8));
         -webkit-backdrop-filter: blur(3px) saturate(.9); backdrop-filter: blur(3px) saturate(.9);
         opacity: 0; transition: opacity .32s ease, background .5s ease; }
.pz-hc.is-in { opacity: 1; }
.pz-hc.is-out { opacity: 0; transition: opacity .36s ease; }
.pz-hc-card { position: absolute; top: 44%; left: 50%; transform: translate(-50%, -50%) scale(.94);
              border-radius: 14px;
              box-shadow: 0 18px 50px rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.14);
              transition: transform .5s cubic-bezier(.2,.8,.2,1), opacity .45s ease; }
.pz-hc.is-in .pz-hc-card { transform: translate(-50%, -50%) scale(1); }
.pz-hc-photo, .pz-hc-cut { position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                           border-radius: 14px; user-select: none; -webkit-user-drag: none; }
.pz-hc-cut { border-radius: 0; }
.pz-hc-photo { object-fit: cover; transition: opacity .7s ease, filter .7s ease; }
/* The measuring mesh: a dot grid that drifts, masked to a soft ellipse. */
.pz-hc-mesh { position: absolute; top: 0; right: 0; bottom: 0; left: 0; border-radius: 14px;
              background-image: radial-gradient(rgba(255,255,255,.55) 1px, transparent 1.4px);
              background-size: 14px 14px; opacity: .55; mix-blend-mode: screen;
              -webkit-mask-image: radial-gradient(60% 55% at 50% 42%, #000 35%, transparent 75%);
              mask-image: radial-gradient(60% 55% at 50% 42%, #000 35%, transparent 75%);
              animation: pz-hc-mesh 3.2s linear infinite; transition: opacity .4s ease; }
@keyframes pz-hc-mesh { to { background-position: 14px 28px; } }
.pz-hc-beam { position: absolute; left: 0; right: 0; height: 26%; top: 0; border-radius: 14px;
              background: linear-gradient(180deg, transparent, color-mix(in srgb, var(--pz-accent) 38%, transparent) 70%,
                          rgba(255,255,255,.95) 97%, transparent);
              filter: drop-shadow(0 0 10px color-mix(in srgb, var(--pz-accent) 70%, #fff));
              animation: pz-hc-beam 1.7s cubic-bezier(.5,0,.5,1) infinite alternate; transition: opacity .3s ease; }
@keyframes pz-hc-beam { from { transform: translateY(-20%); } to { transform: translateY(300%); } }
.pz-hc-cut { opacity: 0; transform: scale(1);
             transition: opacity .55s ease, transform .6s cubic-bezier(.2,.8,.2,1), filter .6s ease; }
.pz-hc-brackets { position: absolute; top: 0; right: 0; bottom: 0; left: 0; }
.pz-hc-mark { position: absolute; opacity: .55;
              transition: left .5s cubic-bezier(.2,.8,.2,1), top .5s cubic-bezier(.2,.8,.2,1),
                          width .5s cubic-bezier(.2,.8,.2,1), height .5s cubic-bezier(.2,.8,.2,1), opacity .4s ease; }
.pz-hc-mark.is-chosen, .pz-hc-mark:only-child { opacity: 1; }
.pz-hc-mark i { position: absolute; width: 22%; height: 22%; max-width: 26px; max-height: 26px; min-width: 9px; min-height: 9px;
                border: 2.5px solid #fff; border-radius: 3px;
                filter: drop-shadow(0 0 6px color-mix(in srgb, var(--pz-accent) 80%, #fff)); }
.pz-hc-mark i:nth-child(1) { top: 0; left: 0; border-right: 0; border-bottom: 0; }
.pz-hc-mark i:nth-child(2) { top: 0; right: 0; border-left: 0; border-bottom: 0; }
.pz-hc-mark i:nth-child(3) { bottom: 0; right: 0; border-left: 0; border-top: 0; }
.pz-hc-mark i:nth-child(4) { bottom: 0; left: 0; border-right: 0; border-top: 0; }
.pz-hc.is-found .pz-hc-beam { opacity: 0; }
.pz-hc.is-found .pz-hc-mesh { opacity: .25; }
/* The cut: the photo falls away, the head stays lit with a white rim. */
.pz-hc.is-cut .pz-hc-photo { opacity: .14; filter: grayscale(1) blur(2px); }
.pz-hc.is-cut .pz-hc-mesh { opacity: 0; }
.pz-hc.is-cut .pz-hc-mark { opacity: 0; }
.pz-hc.is-cut .pz-hc-cut { opacity: 1; transform: scale(1.035);
  filter: drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 0 14px color-mix(in srgb, var(--pz-accent) 75%, #fff)); }
.pz-hc.is-flying { background: transparent; -webkit-backdrop-filter: none; backdrop-filter: none; transition: background .6s ease; }
.pz-hc.is-flying .pz-hc-card, .pz-hc.is-flying .pz-hc-status { opacity: 0; transition: opacity .3s ease; }
.pz-hc-flyer { position: absolute; will-change: transform; }
.pz-hc-flyer img { position: absolute; top: 0; left: 0; width: 100%; height: 100%; transform-origin: 0 0; will-change: transform; }
.pz-hc-flyer.is-landed { opacity: 0; transition: opacity .26s ease; }
/* Status: what the AI is doing, with a bar that creeps while the server works. */
.pz-hc-status { position: absolute; left: 50%; bottom: 7%; transform: translateX(-50%);
                display: flex; align-items: center; gap: 9px; justify-content: center;
                max-width: 86%; padding: 9px 15px 12px; border-radius: 999px;
                background: rgba(255,255,255,.12); box-shadow: inset 0 0 0 1px rgba(255,255,255,.16);
                color: #fff; font-size: 13.5px; font-weight: 600; letter-spacing: .1px; text-align: center;
                text-shadow: 0 1px 3px rgba(0,0,0,.45); white-space: nowrap; transition: opacity .3s ease; }
.pz-hc-dot { width: 8px; height: 8px; border-radius: 50%; background: #fff; flex: none;
             box-shadow: 0 0 0 0 rgba(255,255,255,.7); animation: pz-hc-dot 1.2s ease-out infinite; }
@keyframes pz-hc-dot { to { box-shadow: 0 0 0 9px rgba(255,255,255,0); } }
.pz-hc-bar { position: absolute; left: 16px; right: 16px; bottom: 5px; height: 2px; border-radius: 2px;
             background: rgba(255,255,255,.18); overflow: hidden; }
.pz-hc-bar i { position: absolute; top: 0; bottom: 0; left: 0; width: 0; border-radius: 2px;
               background: linear-gradient(90deg, #fff, color-mix(in srgb, var(--pz-accent) 60%, #fff));
               animation: pz-hc-bar 8s cubic-bezier(.1,.7,.3,1) forwards; }
@keyframes pz-hc-bar { to { width: 88%; } }
.pz-hc.is-found .pz-hc-bar i { width: 100%; animation: none; transition: width .35s ease; }
.pz-hc.is-found .pz-hc-dot { animation: none; background: #6ee7a8; }
.pz-hc.is-error .pz-hc-beam, .pz-hc.is-error .pz-hc-mesh, .pz-hc.is-error .pz-hc-bar { display: none; }
.pz-hc.is-error .pz-hc-dot { animation: none; background: #fca5a5; }
.pz-hc.is-error .pz-hc-status { white-space: normal; }
/* Landing: one ring breathes out around the slot, and a short hint. */
.pz-hc-pulse { position: absolute; z-index: 5; pointer-events: none; border-radius: 50%;
               box-shadow: 0 0 0 3px color-mix(in srgb, var(--pz-accent) 70%, #fff);
               animation: pz-hc-pulse 1.2s cubic-bezier(.2,.8,.2,1) forwards; }
@keyframes pz-hc-pulse { from { opacity: .95; transform: scale(.7); } to { opacity: 0; transform: scale(1.35); } }
.pz-hc-toast { position: absolute; z-index: 5; left: 50%; bottom: 12px; transform: translateX(-50%);
               pointer-events: none; padding: 8px 14px; border-radius: 999px; max-width: 90%;
               background: rgba(16,16,32,.86); color: #fff; font-size: 12.5px; font-weight: 600; text-align: center;
               box-shadow: 0 8px 24px rgba(0,0,0,.25); animation: pz-hc-toast .35s cubic-bezier(.2,.8,.2,1);
               transition: opacity .35s ease; }
.pz-hc-toast.is-out { opacity: 0; }
@keyframes pz-hc-toast { from { opacity: 0; transform: translate(-50%, 8px); } }
@media (prefers-reduced-motion: reduce) {
  .pz-hc-mesh, .pz-hc-beam, .pz-hc-dot, .pz-hc-bar i, .pz-hc-pulse { animation: none; }
  .pz-hc-card, .pz-hc-cut, .pz-hc-mark { transition: none; }
}

/* "Which face?" — asked only when a photo genuinely contains more than one. */
.pz-facepick { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483001;
               display: flex; align-items: center; justify-content: center;
               padding: 20px; background: rgba(12,12,20,.62);
               animation: pz-fp-in .18s ease-out; }
.pz-facepick.is-closing { opacity: 0; transition: opacity .18s ease; }
@keyframes pz-fp-in { from { opacity: 0; } to { opacity: 1; } }

.pz-facepick-panel { background: #fff; border-radius: 14px; padding: 18px;
                     width: min(460px, 100%); max-height: 88vh; overflow: auto;
                     box-shadow: 0 18px 48px rgba(12,12,20,.32);
                     animation: pz-fp-rise .22s cubic-bezier(.2,.8,.3,1); }
@keyframes pz-fp-rise { from { transform: translateY(12px); } to { transform: none; } }

.pz-facepick-title { margin: 0 0 4px; font-size: 17px; font-weight: 600; color: #1b1b22; }
.pz-facepick-hint { margin: 0 0 14px; font-size: 13px; color: #71717d; }

.pz-facepick-frame { position: relative; line-height: 0; border-radius: 10px;
                     overflow: hidden; background: #f4f4f6; }
.pz-facepick-img { width: 100%; height: auto; display: block; }

/* The boxes are the control. They sit ON the faces, so the shopper points at a
   person rather than reading a list of numbers. */
.pz-facepick-spot { position: absolute; padding: 0; cursor: pointer;
                    border: 3px solid rgba(255,255,255,.95); border-radius: 8px;
                    background: var(--pz-accent-ring);
                    box-shadow: 0 0 0 2px var(--pz-accent-line), 0 2px 10px rgba(0,0,0,.25);
                    transition: background .14s ease, transform .14s ease; }
.pz-facepick-spot:hover, .pz-facepick-spot:focus-visible {
  background: var(--pz-accent-veil); transform: scale(1.06); outline: none;
  box-shadow: 0 0 0 3px var(--pz-accent), 0 3px 14px rgba(0,0,0,.3); }
/* A face in a group photo can be 20px across, well under the 44px a thumb
   needs. The visual box stays on the face; the tap target grows around it. */
.pz-facepick-spot::after { content: ''; position: absolute;
                           left: 50%; top: 50%; transform: translate(-50%,-50%);
                           min-width: 44px; min-height: 44px;
                           width: 100%; height: 100%; }
/* The numbers are for screen readers, not for the eye: with faces side by side
   the badges collided with each other and pulled attention off the faces. */
.pz-facepick-num { position: absolute; width: 1px; height: 1px; padding: 0;
                   margin: -1px; overflow: hidden; clip: rect(0 0 0 0);
                   white-space: nowrap; border: 0; }

.pz-facepick-cancel { margin-top: 14px; width: 100%; padding: 10px;
                      border: 1px solid #d8d8e0; border-radius: 9px;
                      background: #fff; color: #45454f; font-size: 14px;
                      cursor: pointer; }
.pz-facepick-cancel:hover { border-color: #b9b9c4; }

/* A length limit is only useful if the shopper can see it coming. */
.pz-row { position: relative; }
.pz-counter { position: absolute; top: 0; right: 0; font-size: 11px; color: #8a8a96;
              font-variant-numeric: tabular-nums; }

@media (prefers-reduced-motion: reduce) { * { transition: none !important; animation: none !important; } }

/* ═══════════════════════════════════════════════════════════════════════════
   Every screen, every theme.

   A WooCommerce product page is not a blank canvas: the widget is dropped into
   a ~45% wide summary column whose ancestors may be transformed (page builders)
   or overflow-hidden (most themes), under a fixed header, above a sticky
   add-to-cart bar, on a phone whose keyboard eats half the viewport. Everything
   below exists for one of those.

   Order matters: the plain property is always declared BEFORE the modern one,
   so an engine that does not understand dvh/color-mix/min() keeps the fallback.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── The host itself ─────────────────────────────────────────────────────── */
/* Themes float, inline-block or fixed-width whatever sits inside form.cart, and
   page CSS beats a shadow :host rule. These few properties are the widget's
   right to a full-width block of its own. */
:host {
  color-scheme: light;
  display: block !important;
  float: none !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  clear: both !important;
  flex: 0 0 100% !important;
  grid-column: 1 / -1 !important;
  box-sizing: border-box !important;
  /* Measured by JS: a theme's fixed header, its sticky bottom bar, and an
     in-app browser's invisible toolbar. */
  --pz-sticky-top: 12px;
  --pz-bottom-bar: 0px;
  --pz-inapp-bottom: 0px;
}
/* ── Inline layout: container-driven, viewport as the fallback ───────────── */
/* The widget's own box is what the layout must react to: the viewport says
   nothing useful about a 340px summary column on a 1440px screen, which is
   exactly where a WooCommerce product page puts us.
   The container is .pz-wrap rather than the host, because container-type
   brings layout containment with it — on the host that would make every
   position: fixed descendant (toasts, the floating thumbnail, the no-dialog
   overlay fallback) relative to the widget instead of the screen.
   The columns are always two; a child spans both until the container is wide
   enough, because an element cannot be styled by its own container query. */
/* A portrait poster must never be taller than the screen it is pinned to.
   Declared BEFORE the container queries: a narrow column's tighter cap below
   has to be able to win, or a 500px-wide square pins itself over the form. */
.pz-wrap > .pz-preview canvas {
  width: auto; max-width: 100%; height: auto; margin-left: auto; margin-right: auto;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - var(--pz-sticky-top, 12px) - 24px);
}
@supports (container-type: inline-size) {
  .pz-wrap { container-type: inline-size; container-name: pz;
             grid-template-columns: minmax(0,1fr) minmax(0,1fr); align-items: start; }
  .pz-wrap > * { grid-column: 1 / -1; }
  @container pz (min-width: 640px) {
    .pz-wrap > * { grid-column: auto; }
    .pz-wrap:not(.pz-wrap--single) { column-gap: 28px; }
    .pz-wrap > .pz-preview { position: sticky; top: var(--pz-sticky-top, 12px); }
    .pz-preview-toggle { display: none; }
  }
  @container pz (max-width: 639px) {
    .pz-wrap > .pz-preview { position: sticky; top: var(--pz-sticky-top, 12px); z-index: 2; box-shadow: var(--pz-shadow-md); }
    .pz-wrap > .pz-preview canvas { max-height: 38vh; max-height: 38svh; }
    .pz-preview-toggle { display: inline-flex; }
  }
  /* A phone-width column: the caption keeps its words ("Pregled dizajna"),
     the actions keep only their icons (each has an aria-label), the meter
     keeps its numbers and drops its bar, and the design card's pill its word. */
  @container pz (max-width: 420px) {
    .pz-preview-open .pz-btn-label { display: none; }
    .pz-preview-open { padding-left: 10px; padding-right: 10px; }
    .pz-meter-bar { display: none; }
    .pz-peek-cta { width: 40px; height: 40px; padding: 0; justify-content: center; font-size: 0; gap: 0; }
    .pz-peek-cta .pz-ic { font-size: 17px; }
  }
  /* A very narrow column (a sidebar, a quick-view popup): the meter moves
     under the heading instead of squeezing it. */
  @container pz (max-width: 280px) {
    .pz-head { flex-direction: column; align-items: flex-start; }
    .pz-preview-toggle .pz-btn-label { display: none; }
  }
}
/* A page where sticky cannot work (an overflow-hidden summary column) must not
   pretend: the preview scrolls away and the floating thumbnail takes over. */
.pz-wrap.pz-no-sticky > .pz-preview { position: relative; top: auto; }
.pz-preview.is-sticky { top: var(--pz-sticky-top, 12px); }
@media (max-width: 859px) {
  .pz-wrap > .pz-preview { top: var(--pz-sticky-top, 8px); }
}
/* Portrait phones and tablets share the screen between picture and form; a
   phone on its side has two columns, and the picture may use the whole height. */
@media (max-width: 859px) and (orientation: portrait) {
  .pz-wrap > .pz-preview canvas { max-height: 38vh; max-height: 38svh; }
}

/* Fold the preview away: on a phone the pinned picture otherwise leaves room
   for two questions at a time. */
.pz-preview.is-collapsed canvas { max-height: 64px; }
.pz-preview.is-collapsed .pz-previews { display: none; }
@supports not (container-type: inline-size) {
  @media (max-width: 859px) { .pz-preview-toggle { display: inline-flex; } }
}

/* The floating design bar arrives quietly once the preview has scrolled away
   (see syncFab), clear of a notch. */
.pz-preview-fab {
  animation: pz-fab-in .18s ease-out;
  padding-top: max(8px, env(safe-area-inset-top, 0px)); }
@keyframes pz-fab-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .pz-preview-fab { animation: none; } }

/* ── Layers: the browser's top layer, or a plain fixed element ───────────── */
/* A <dialog> opened with showModal() escapes every ancestor transform,
   overflow and z-index on the page — which is the only reliable way to be on
   top of a page builder's markup and a chat bubble at the same time. */
dialog.pz-layer { border: 0; padding: 0; margin: 0; max-width: none; max-height: none;
  width: auto; height: auto; background: transparent; color: inherit; overflow: visible; }
dialog.pz-layer::backdrop { background: rgba(12,12,20,.66); animation: pz-fade-in .2s ease-out; }
dialog.pz-layer:not([open]) { display: none; }
@keyframes pz-fade-in { from { opacity: 0; } to { opacity: 1; } }
/* Older engines (Safari < 15.4, old Android WebViews) get the same element as a
   fixed div; when even that is broken by a transformed ancestor, layers.ts
   portals it to a body-level shadow host. */
.pz-layer--fixed { position: fixed; top: 0; right: 0; bottom: 0; left: 0; inset: 0; }
.pz-portal { position: static; }

/* ── The popup's share row: under the picture on a wide screen, at the foot
   of the step on a phone, where the pinned picture has no room for it. ── */
.pz-modal-step > .pz-share { justify-content: flex-start; padding-top: 4px; }
@media (min-width: 700px) { .pz-modal-step > .pz-share { display: none; } }
@media (max-width: 699px) and (orientation: portrait), (max-width: 699px) and (min-height: 521px) {
  /* Exactly the screens that keep the step's own copy (see below). */
  .pz-modal-preview .pz-share { display: none; }
}

/* ── A phone on its side gets the whole screen too ───────────────────────── */
@media (max-height: 520px) and (orientation: landscape) {
  .pz-overlay, .pz-overlay.pz-layer { padding: 0; place-items: stretch; grid-template-rows: minmax(0, 1fr); }
  .pz-layer.pz-overlay { top: 0; top: var(--pz-vv-top, 0px); }
  /* Capped at the layer's box, as upright: the footer always stays on screen. */
  .pz-modal { width: 100%; max-width: none; height: 100vh; height: 100dvh; height: var(--pz-vvh, 100dvh);
              max-height: 100%; border-radius: 0; box-shadow: none;
              padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); }
  .pz-modal-step > .pz-share { display: none; }
}

/* ── Dialogs that open from a field: map, song, faces ────────────────────── */
.pz-map-card, .pz-spotify-card {
  max-height: 92vh; max-height: min(92dvh, var(--pz-vvh, 92dvh)); overflow: auto; }
.pz-map-card { width: 460px; width: min(460px, 100% - 24px); }
.pz-spotify-card { width: 420px; width: min(420px, 100% - 24px); }
.pz-facepick-panel { max-height: 88vh; max-height: min(88dvh, var(--pz-vvh, 88dvh)); }
.pz-map-canvas { height: 320px; height: clamp(200px, 40dvh, 320px); }
.pz-map-suggestions { max-height: 220px; max-height: min(220px, 40dvh); }
.pz-spotify-results { max-height: 320px; max-height: min(320px, 45dvh); }
/* Typing into the search box on a phone leaves a sliver of map; shrink it
   rather than push the buttons off the bottom. */
.has-keyboard .pz-map-canvas { height: 160px; }
/* On a phone these become bottom sheets: full width, rounded top, thumb-reach. */
@media (max-width: 699px) {
  .pz-layer.pz-sheet, .pz-map-modal, .pz-spotify-modal, .pz-facepick { padding: 0; align-items: end; place-items: end stretch; }
  .pz-sheet > *, .pz-map-card, .pz-spotify-card, .pz-facepick-panel {
    width: 100%; max-width: none; border-radius: 18px 18px 0 0;
    max-height: 92vh; max-height: min(92dvh, var(--pz-vvh, 92dvh)); overflow: auto;
    padding-bottom: max(16px, env(safe-area-inset-bottom)); }
}

/* ── Notifications that are always on screen ─────────────────────────────── */
/* The status line stays where it is (it belongs beside the form); this is its
   twin, anchored to the visible viewport, above the keyboard, the safe area,
   an in-app browser's toolbar and the theme's own sticky cart bar. */
.pz-toasts { position: fixed; left: 50%; transform: translateX(-50%);
  bottom: 16px;
  bottom: calc(var(--pz-kb, 0px) + var(--pz-bottom-bar, 0px) + var(--pz-inapp-bottom, 0px) + env(safe-area-inset-bottom, 0px) + 16px);
  z-index: 2147483600; display: grid; gap: 8px; justify-items: center;
  width: 92vw; width: min(420px, calc(var(--pz-vvw, 100vw) - 24px));
  pointer-events: none; }
.pz-toast { pointer-events: auto; display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 11px 12px; border-radius: 12px; font-size: 13.5px;
  line-height: 1.4; color: #fff; background: #23232d;
  box-shadow: 0 8px 28px rgba(10,10,30,.32);
  animation: pz-toast-in .18s ease-out; }
.pz-toast.is-error { background: #a32a1e; }
.pz-toast.is-info { background: #2f2a63; }
.pz-toast.is-success { background: #1f6d46; }
.pz-toast.is-leaving { opacity: 0; transform: translateY(6px); transition: opacity .18s, transform .18s; }
.pz-toast-text { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.pz-toast-action { flex-shrink: 0; padding: 7px 11px; cursor: pointer; font: inherit;
  font-weight: 650; color: #fff; background: rgba(255,255,255,.16);
  border: 0; border-radius: 99px; }
.pz-toast-action:hover { background: rgba(255,255,255,.26); }
.pz-toast-close { flex-shrink: 0; display: grid; place-items: center; width: 26px; height: 26px;
  padding: 0; cursor: pointer; font-size: 12px; line-height: 1; color: #fff;
  background: transparent; border: 0; border-radius: 99px; opacity: .75; }
.pz-toast-close:hover { opacity: 1; background: rgba(255,255,255,.16); }
@keyframes pz-toast-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (max-height: 520px) and (orientation: landscape) {
  .pz-toasts { bottom: 8px; width: min(380px, calc(var(--pz-vvw, 100vw) - 24px)); }
}
/* Inside the popup a toast never lands on its buttons: when it would (a phone,
   a short window, the keyboard up), layers.ts marks the region is-lifted with
   how much of the screen's foot the footer takes (--pz-toast-clear), and the
   toast sits above that. A popup with room under it keeps its toasts there. */
.pz-overlay.pz-layer > .pz-toasts.is-lifted {
  bottom: max(calc(var(--pz-kb, 0px) + var(--pz-bottom-bar, 0px) + var(--pz-inapp-bottom, 0px) + env(safe-area-inset-bottom, 0px) + 16px),
              calc(var(--pz-toast-clear, 0px) + 12px)); }

/* ── The field that is blocking the sale ─────────────────────────────────── */
/* Rows are scrolled to with block:'center'; the margin keeps them clear of a
   theme's fixed header and of our own sticky preview. Nothing here moves the
   row sideways: a highlight that shifts the text is a highlight that jitters. */
.pz-row { position: relative;
          scroll-margin-top: calc(var(--pz-sticky-top, 12px) + 12px);
          scroll-margin-bottom: calc(var(--pz-bottom-bar, 0px) + 96px); }
.pz-row.is-missing > .pz-label { color: var(--pz-danger); }
.pz-row.is-missing .pz-input, .pz-row.is-missing .pz-drop, .pz-row.is-missing .pz-swatch:not(.is-disabled),
.pz-row.is-missing .pz-src-card, .pz-row.is-missing .pz-file-card {
  border-color: var(--pz-danger); }
.pz-row.is-missing .pz-input:focus { box-shadow: 0 0 0 4px rgba(198,40,40,.14); }

/* ── "What am I editing right now" ───────────────────────────────────────── */
.pz-row.is-active-question > .pz-label, .pz-song-line.is-active-question > .pz-label { color: var(--pz-accent-ink); }
.pz-row.is-active-question.is-missing > .pz-label { color: var(--pz-danger); }
.pz-focus-spot { position: absolute; z-index: 4; pointer-events: none; box-sizing: border-box;
  outline: 2px solid var(--pz-accent, #5145cd); outline-offset: -2px; border-radius: 3px;
  box-shadow: 0 0 0 4px var(--pz-accent-ring); background: transparent; }
.pz-focus-chip { position: absolute; z-index: 5; top: 8px; left: 8px; max-width: calc(100% - 16px);
  padding: 4px 10px; border-radius: 99px; font-size: 12px; font-weight: 600; line-height: 1.3;
  color: var(--pz-btn-text, #fff); background: var(--pz-accent, #5145cd); box-shadow: 0 2px 8px rgba(10,10,30,.18);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; pointer-events: none; }
.pz-focus-chip[hidden], .pz-focus-spot[hidden] { display: none; }
@media (prefers-reduced-motion: no-preference) { .pz-focus-spot { transition: left .12s, top .12s, width .12s, height .12s; } }
/* The lines inside a song card answer their own optional questions; the song
   being missing is not their fault and must not paint them red. */
.pz-row.is-missing .pz-song-line .pz-input { border-color: var(--pz-line-2); }
.pz-row.is-missing .pz-song-line .pz-label { color: var(--pz-ink-2); }

/* ── Touch: nothing smaller than a thumb ─────────────────────────────────── */
@media (pointer: coarse) {
  /* Grown outright, where a bigger control still looks right. */
  .pz-modal-close, .pz-lightbox-close, .pz-spotify-close { width: 44px; height: 44px; }
  .pz-map-zoom { width: 44px; height: 44px; }
  .pz-edit-btn { min-width: 40px; height: 40px; }
  .pz-wave-zoom-btn { width: 38px; height: 38px; }
  .pz-file-card .x { width: 40px; height: 40px; }
  .pz-timeline-move .pz-btn-ghost.small { padding: 7px 12px; }
  .pz-btn, .pz-btn-ghost { min-height: 44px; }
  .pz-preview-open, .pz-preview-toggle { min-height: 40px; }
  .pz-swatch { min-height: 48px; }
  .pz-check { min-height: 44px; }
  /* Kept small on purpose (a ✕ badge on a 56px tile), with the hit area grown
     invisibly around it — the alternative covers the picture it deletes. */
  .pz-multi-item .x, .pz-multiupload-cell .x { position: absolute; }
  .pz-multi-item .x::after, .pz-multiupload-cell .x::after {
    content: ''; position: absolute; left: 50%; top: 50%;
    transform: translate(-50%, -50%); width: 44px; height: 44px; }
  /* Hover-only affordances never appear on a touch device. */
  .pz-edit-tip { display: none; }
}
/* Hover effects only where a pointer can actually hover. */
@media (hover: none) {
  .pz-peek:hover { border-color: var(--pz-preview-border, var(--pz-line-2)); box-shadow: var(--pz-shadow-sm); }
  .pz-peek:hover .pz-peek-cta { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
  .pz-preview-fab:hover { transform: none; }
  .pz-btn:hover { background-image: none; }
  .pz-launch-btn:hover .pz-launch-arrow { transform: none; }
  .pz-input:hover { border-color: var(--pz-line-2); }
  .pz-swatch:hover { border-color: var(--pz-line-2); }
  .pz-swatch.is-active:hover { border-color: var(--pz-selection, var(--pz-accent)); }
}
.pz-zoom-hover canvas { cursor: zoom-in; }
@media (hover: none) { .pz-zoom-hover canvas { cursor: pointer; } }

/* ── iOS zooms into any input under 16px ─────────────────────────────────── */
/* It happens on focus, mid-checkout, and the page never zooms back out. Every
   input the shopper can reach on a touch device is 16px, including the small
   ones that were styled down for looks. */
@media (pointer: coarse), (max-width: 859px) {
  .pz-input, input, textarea, select,
  .pz-cut-time, .pz-colorpick-hex, .pz-timeline-gap-input.pz-input,
  .pz-share .pz-input, .pz-fade-length { font-size: 16px; }
  .pz-colorpick-hex { width: 120px; }
  .pz-timeline-gap-input.pz-input { width: 74px; }
}

/* ── Long words, big text settings, small screens ────────────────────────── */
.pz-label, .pz-hint, .pz-swatch-name, .pz-swatch-label, .pz-peek-copy strong,
.pz-peek-copy span, .pz-src-copy strong, .pz-src-copy span, .pz-step-title,
.pz-status, .pz-section-error, .pz-head-title, .pz-summary-line dt, .pz-summary-line dd,
.pz-summary-line b, .pz-summary-top strong { overflow-wrap: anywhere; }
.pz-btn, .pz-btn-ghost { white-space: normal; text-align: center; }
.pz-modal-heading strong { min-width: 0; }

/* ── RTL shops ───────────────────────────────────────────────────────────── */
.pz-share-btn::before { margin-right: 0; margin-inline-end: 6px; }
.pz-timeline-len { margin-left: 0; margin-inline-start: auto; }
.pz-map-suggestion, .pz-src-head, .pz-spotify-row, .pz-track, .pz-design-card, .pz-peek { text-align: start; }
.pz-peek-badge { right: auto; inset-inline-end: 3px; }
.pz-counter { right: auto; inset-inline-end: 0; }
.pz-preview-toggle { right: auto; inset-inline-end: 6px; }
.pz-meter-fill { left: auto; inset-inline-start: 0; }
.pz-swatch--img.is-active::after, .pz-swatch--color.is-active::after { right: auto; inset-inline-end: 4px; }
.pz-swatch--font.is-active::after { right: auto; inset-inline-end: 3px; }
/* Arrows point the way the page reads. */
.pz-modal-back .pz-ic:dir(rtl), .pz-modal-next .pz-next-ic:dir(rtl), .pz-launch-arrow:dir(rtl) { transform: scaleX(-1); }
/* Narrow phones (320–379 px, Android 360 and iPhone SE among them): "Dodaj u
   korpu" on one line beside the total, its tick dropped rather than the words wrapped. */
@media (max-width: 379px) {
  .pz-modal-next { white-space: nowrap; padding-left: 12px; padding-right: 12px; font-size: 15px; }
  .pz-modal-next.is-last .pz-done-ic { display: none; }
}
@media (min-width: 700px) {
  .pz-modal-preview { border-right: 0; border-inline-end: 1px solid var(--pz-line); }
}

/* ── :has() fallback ─────────────────────────────────────────────────────── */
/* Safari before 15.4 and Firefox before 121 ignore the :has rule above; the
   form toggles this class on the same card so the selection is still visible. */
.pz-src-card.is-active { border-color: var(--pz-accent); box-shadow: 0 3px 14px var(--pz-accent-ring); }

/* ── Windows High Contrast / forced colours ──────────────────────────────── */
@media (forced-colors: active) {
  .pz-swatch.is-active, .pz-swatch--img.is-active, .pz-swatch--text.is-active, .pz-swatch--font.is-active,
  .pz-chip.is-active, .pz-src-head.is-active, .pz-previews-item.is-active,
  .pz-song-version.is-selected, .pz-spotify-row.is-active {
    outline: 3px solid Highlight; outline-offset: 2px; }
  .pz-step-line.is-done, .pz-step-chip.is-current, .pz-meter-fill {
    forced-color-adjust: none; background: Highlight; }
  .pz-btn, .pz-btn-ghost, .pz-toast, .pz-peek, .pz-modal-close { border: 1px solid ButtonText; }
  .pz-src-check.is-done, .pz-src-head.is-active .pz-src-check { forced-color-adjust: none; }
  .pz-edit, .pz-edit-photo::after { border-color: CanvasText; }
}

/* ── Print: the widget's chrome never reaches paper ──────────────────────── */
@media print {
  .pz-preview-fab, .pz-toasts, .pz-launch-meta, .pz-share, .pz-preview-cap-actions { display: none !important; }
}

/* ── Compat fallbacks the coordinator added after the agents' pass ────────── */
/* aspect-ratio is Safari 15+: older engines get a fixed square height. */
@supports not (aspect-ratio: 1) {
  .pz-multi-cell img { height: 64px; }
  .pz-multiupload-cell img { height: 78px; }
  .pz-cutout-frame { height: min(54%, 230px); }
  /* The zone's own shape is unavailable; a fixed box keeps the card from
     collapsing to the height of its caption. */
  .pz-design-thumb { height: 96px; }
}

/* ── On-canvas editor: thumb-sized hit areas ─────────────────────────────── */
/* canvas-edit.ts puts is-coarse on the overlay root when the pointer is
   coarse; the drawing stays small, the hit area does not. */
.pz-edit.is-coarse .pz-edit-grip::after {
  content: ''; position: absolute; top: 50%; left: 50%;
  width: 44px; height: 44px; transform: translate(-50%, -50%);
}
.pz-edit.is-coarse .pz-edit-grip { position: absolute; touch-action: none; }
.pz-edit.is-coarse .pz-edit-tip { display: none; }
.pz-map-modal.has-keyboard .pz-map-suggestions { max-height: 30vh; }

/* ── The design area: the card on the page ────────────────────────────────── */
/* A summary, not a working canvas: the design as it will print (rendered by the
   print renderer at thumbnail size), what is in it in words, what it costs, and
   one button. The designer itself takes the whole screen — see below. */
.pz-design-card { display: grid; gap: 12px; align-items: start; padding: 12px;
  grid-template-columns: minmax(0, 136px) minmax(0, 1fr);
  border: 1.5px solid #e2e1ee; border-radius: 12px; background: #fff; }
@media (max-width: 460px) { .pz-design-card { grid-template-columns: minmax(0, 1fr); } }
.pz-design-thumb { position: relative; display: grid; place-items: center; overflow: hidden;
  aspect-ratio: var(--pz-design-aspect, 3 / 2); min-height: 76px; border-radius: 9px;
  /* The checker says "this is the print area, not the product" without needing
     a product colour the widget cannot know. */
  background: repeating-conic-gradient(#f1f0f6 0 25%, #fafafd 0 50%) 50%/16px 16px; }
.pz-design-preview { display: block; width: 100%; height: 100%; object-fit: contain; }
.pz-design-empty { padding: 6px; color: #8a8a96; font-size: 12px; text-align: center; }
.pz-design-body { display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  min-width: 0; }
.pz-design-summary { margin: 0; font-weight: 600; overflow-wrap: anywhere; }
.pz-design-card.is-empty .pz-design-summary { font-weight: 500; color: #6b6b78; }
.pz-design-size { margin: 0; color: #71717d; font-size: 12.5px; }
.pz-design-price { margin: 0; color: #45454f; font-size: 12.5px; font-weight: 600; }
.pz-design-open { margin-top: 2px; }
/* Forgotten required design: the same red the other rows get, on the card. */
.pz-row.is-missing .pz-design-card { border-color: var(--pz-required, #d7263d); }

/* ── The card's quick fields (Zazzle's EZ Templates) ──────────────────────── */
/* The zone's author marks a layer "editable on the product page"; it appears
   here, above the button, so a name or a photograph is changed without opening
   anything. Full width of the card body, because a name field squeezed beside a
   thumbnail is a name field nobody uses. */
.pz-design-fields { display: grid; gap: 10px; width: 100%; margin: 2px 0 4px; }
.pz-design-field { display: grid; gap: 4px; width: 100%; }
.pz-design-field-label { display: flex; align-items: center; gap: 6px; min-width: 0;
  font-size: 12.5px; font-weight: 600; color: #45454f; overflow-wrap: anywhere; }
/* The layer's own label (a template author's name for it, not the shopper's
   text) is the first child; the "must replace" pill beside it stays intact. */
.pz-design-field-label > span:first-child { min-width: 0; }
.pz-design-field .pz-input { width: 100%; }
/* Until the shopper changes it, an authored sample is not an answer — the same
   red the form uses for a missing required field, as a pill on the field. */
.pz-design-required { padding: 1px 7px; border-radius: 99px; font-size: 10.5px;
  font-weight: 700; letter-spacing: .02em; text-transform: uppercase;
  color: #fff; background: var(--pz-required, #d7263d); }
.pz-design-field.is-unreplaced .pz-input { border-color: var(--pz-required, #d7263d); }
.pz-design-field-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pz-design-field-thumb { width: 44px; height: 44px; border-radius: 8px; object-fit: cover;
  background: repeating-conic-gradient(#f1f0f6 0 25%, #fafafd 0 50%) 50%/12px 12px;
  border: 1px solid #e2e1ee; flex: none; }
.pz-design-field-row .pz-status { flex-basis: 100%; margin: 0; font-size: 12px; }

/* ── The design area: the full-screen designer ────────────────────────────── */
/* A dialog element in the browser's top layer (layers.ts), which is what lets
   it open from the wizard as well and still be above it — the same pattern as
   the wizard overlay and the lightbox: pinned to the viewport, a grid, and the
   sheet inside sized in PERCENT of that box rather than in viewport units.
   Percent matters: the keyboard-aware custom properties are stamped when the
   layer opens, and a widget opened in a pane or tab that has no size yet stamps
   a zero — which would collapse a designer sized from it to nothing. So the box
   is the layer's own, and the viewport variable only trims it when a keyboard is
   actually up (which is the one case dvh gets wrong). */
/* Two classes, because the dialog.pz-layer rule above is more specific than one
   class would be and resets padding, background and size for every layer. */
.pz-designer-layer.pz-layer { position: fixed; top: 0; right: 0; bottom: 0; left: 0;
  z-index: 2147483300; display: grid; place-items: stretch; padding: 0; }
.pz-designer-layer[hidden] { display: none; }
/* Engines without a dialog element get no ::backdrop to dim the page with. */
.pz-designer-layer.pz-layer--fixed { background: rgba(12,12,20,.72); }
.pz-designer { display: flex; flex-direction: column; min-width: 0; min-height: 0;
  width: 100%; height: 100%; max-height: 100%;
  overflow: hidden; background: #fff; color: #1b1b22;
  padding-bottom: env(safe-area-inset-bottom); }
/* Keyboard up: the designer gives back exactly what the keyboard took, and
   never less than a usable strip however odd the reported viewport is. */
.pz-designer-layer.has-keyboard .pz-designer { height: max(320px, var(--pz-vvh, 100%)); }
@media (min-width: 860px) {
  /* Room to breathe on a desktop, but still the whole screen minus a margin:
     a print zone at a usable scale plus a tool rail plus a panel needs it. */
  .pz-designer-layer.pz-layer { place-items: center; padding: 16px; }
  .pz-designer { width: min(1280px, 100%);
    border-radius: 14px; box-shadow: 0 18px 60px rgba(16,16,28,.28); }
}
.pz-designer-failed { display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 14px; height: 100%; padding: 24px; text-align: center; }

${COLLAGE_STEP_STYLES}
/* ── The shopper guide (guide-styles.ts) ──────────────────────────────────── */
.pz-upload-status, .pz-import-notice, .pz-edit-hint { overflow-wrap: anywhere; min-width: 0; }
${GUIDE_STYLES}
${BOARD_STYLES}

/* ── The designer's own rules (designer/styles.ts) ────────────────────────── */
/* Last, like the guide's: the designer is a self-contained block on its own
   .pzd- classes, and it has to be able to win against the widget's generic
   rules for the buttons and inputs it reuses inside its panels. */
${DESIGNER_STYLES}
`;
